import { useState, useEffect, useCallback, useRef } from "react";
import {
  LayoutDashboard, Users, FileText, ShieldCheck, BarChart2, Settings,
  Car, Truck, AlertTriangle, CheckCircle, XCircle, Clock,
  Mail, RefreshCw, Upload, Download, Plus, Trash2, Eye,
  LogOut, Menu, X, ChevronRight, Search,
  Bot, Info, RotateCcw, FileCheck, Lock, Bell
} from "lucide-react";

// ═══════════════════════════════════════════════════════════════════════════════
// SUPABASE CONFIG
// ═══════════════════════════════════════════════════════════════════════════════
const SUPABASE_URL    = process.env.REACT_APP_SUPABASE_URL || "https://pkermjufxfswjbhmwfpk.supabase.co";
const SUPABASE_KEY    = process.env.REACT_APP_SUPABASE_ANON_KEY || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InBrZXJtanVmeGZzd2piaG13ZnBrIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzM5Mjg4OTAsImV4cCI6MjA4OTUwNDg5MH0.hvKo0u4CDqqV-G-AcdSwmJVbd4MLUxo5aOKJHFCyEV8";
const APP_URL         = process.env.REACT_APP_APP_URL    || "https://app.fleetcustodian.com";
const PORTAL_URL      = process.env.REACT_APP_PORTAL_URL || "https://portal.fleetcustodian.com";

const db = {
  async select(table, token) {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/${table}?select=*&order=created_at.asc`, {
      headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${token || SUPABASE_KEY}` },
    });
    return res.json();
  },
  async insert(table, data, token) {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/${table}`, {
      method: "POST",
      headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${token || SUPABASE_KEY}`, "Content-Type": "application/json", Prefer: "return=representation" },
      body: JSON.stringify(data),
    });
    return res.json();
  },
  async update(table, id, data, token) {
    await fetch(`${SUPABASE_URL}/rest/v1/${table}?id=eq.${id}`, {
      method: "PATCH",
      headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${token || SUPABASE_KEY}`, "Content-Type": "application/json", Prefer: "return=minimal" },
      body: JSON.stringify(data),
    });
  },
  async upsert(table, data, onConflict = "driver_id", token) {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/${table}?on_conflict=${onConflict}`, {
      method: "POST",
      headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${token || SUPABASE_KEY}`, "Content-Type": "application/json", Prefer: "resolution=merge-duplicates,return=representation" },
      body: JSON.stringify(data),
    });
    return res.json();
  },
  async delete(table, id, token) {
    await fetch(`${SUPABASE_URL}/rest/v1/${table}?id=eq.${id}`, {
      method: "DELETE",
      headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${token || SUPABASE_KEY}` },
    });
  },
};


const vesStatus = (dateStr, motStatus) => {
  if (!dateStr) return { color: C.textMuted, icon: null, label: "—", raw: "—", statusLabel: motStatus || "—" };
  const date = new Date(dateStr);
  const now  = new Date();
  const diffDays = Math.floor((date - now) / (1000 * 60 * 60 * 24));
  const label = date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  if (diffDays < 0)   return { color: C.red,   iconName: "XCircle",       label, raw: dateStr, statusLabel: motStatus || "Expired" };
  if (diffDays <= 30) return { color: C.amber,  iconName: "AlertTriangle", label, raw: dateStr, statusLabel: "Valid" };
  return                     { color: C.green,  iconName: "CheckCircle",   label, raw: dateStr, statusLabel: "Valid" };
};


// ── DESIGN TOKENS ─────────────────────────────────────────────────────────────
const C = {
  bg: "#0B0F1A", surface: "#111827", surfaceAlt: "#1a2235", border: "#1f2d45",
  accent: "#3B82F6", accentDim: "#1D4ED8", accentGlow: "rgba(59,130,246,0.15)",
  text: "#F1F5F9", textMuted: "#64748B", textSub: "#94A3B8",
  green: "#10B981", greenBg: "rgba(16,185,129,0.12)",
  amber: "#F59E0B", amberBg: "rgba(245,158,11,0.12)",
  red: "#EF4444", redBg: "rgba(239,68,68,0.12)",
  blue: "#3B82F6", blueBg: "rgba(59,130,246,0.12)",
};

// ── SHARED COMPONENTS ─────────────────────────────────────────────────────────
const RiskBadge = ({ level }) => {
  const cfg = {
    Low:       { bg: C.greenBg,                color: C.green    },
    Medium:    { bg: C.amberBg,                color: C.amber    },
    High:      { bg: C.redBg,                  color: C.red      },
    Valid:     { bg: C.greenBg,                color: C.green    },
    Banned:    { bg: C.redBg,                  color: C.red      },
    Unchecked: { bg: "rgba(100,116,139,0.12)", color: C.textMuted },
    Completed: { bg: C.greenBg,                color: C.green    },
    Pending:   { bg: C.amberBg,                color: C.amber    },
    Expired:   { bg: C.redBg,                  color: C.red      },
  }[level] || { bg: C.blueBg, color: C.blue };
  return (
    <span style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}22`, borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 5, whiteSpace: "nowrap" }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: cfg.color, display: "inline-block" }} />{level}
    </span>
  );
};

const Card = ({ children, style = {} }) => (
  <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, ...style }}>{children}</div>
);

const KPICard = ({ label, value, icon: IconComp, color, sub, onClick, loading }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div onClick={onClick} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{ background: hovered ? C.surfaceAlt : C.surface, border: `1px solid ${hovered ? color + "66" : C.border}`, borderRadius: 12, padding: 18, cursor: "pointer", transition: "all 0.18s", transform: hovered ? "translateY(-3px)" : "none", boxShadow: hovered ? `0 6px 24px ${color}25` : "none" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ minWidth: 0, flex: 1, paddingRight: 6 }}>
          <div className="kpi-label" style={{ color: C.textMuted, fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8, lineHeight: 1.2 }}>{label}</div>
          <div className="kpi-value" style={{ color: C.text, fontSize: 32, fontWeight: 700, lineHeight: 1 }}>
            {loading ? <span style={{ color: C.textMuted, fontSize: 20 }}>…</span> : value}
          </div>
          {sub && <div style={{ color: C.textMuted, fontSize: 12, marginTop: 6 }}>{sub}</div>}
        </div>
        <div className="kpi-icon" style={{ background: `${color}22`, border: `1px solid ${color}33`, borderRadius: 10, padding: 10, flexShrink: 0 }}>
          {IconComp && <IconComp size={20} color={color} strokeWidth={1.75} />}
        </div>
      </div>
    </div>
  );
};

const Btn = ({ children, onClick, variant = "primary", small, disabled, style = {} }) => {
  const v = {
    primary:   { background: disabled ? C.surfaceAlt : C.accent, color: disabled ? C.textMuted : "#fff", border: "none" },
    secondary: { background: C.surfaceAlt, color: C.textSub,  border: `1px solid ${C.border}` },
    danger:    { background: C.redBg,      color: C.red,       border: `1px solid ${C.red}33`  },
    ghost:     { background: "transparent",color: C.textSub,  border: `1px solid ${C.border}` },
  };
  return (
    <button disabled={disabled} style={{ borderRadius: 8, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer", transition: "opacity 0.15s", fontSize: small ? 12 : 14, padding: small ? "6px 12px" : "9px 18px", display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "inherit", ...v[variant], ...style }}
      onClick={onClick}
      onMouseOver={e => !disabled && (e.currentTarget.style.opacity = "0.8")}
      onMouseOut={e => (e.currentTarget.style.opacity = "1")}>
      {children}
    </button>
  );
};

const Input = ({ label, type = "text", value, onChange, placeholder, autoComplete }) => (
  <div style={{ marginBottom: 16 }}>
    {label && <label style={{ display: "block", color: C.textSub, fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{label}</label>}
    <input type={type} value={value} onChange={onChange} placeholder={placeholder} autoComplete={autoComplete}
      style={{ width: "100%", background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", color: C.text, fontSize: 14, outline: "none", boxSizing: "border-box" }} />
  </div>
);

const Modal = ({ title, children, onClose }) => (
  <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 16, overscrollBehavior: "contain" }}>
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, padding: 28, width: "100%", maxWidth: 480, overflowY: "auto", WebkitOverflowScrolling: "touch", maxHeight: "90vh" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div style={{ color: C.text, fontWeight: 700, fontSize: 18 }}>{title}</div>
        <button onClick={onClose} style={{ background: "none", border: "none", color: C.textMuted, cursor: "pointer", display: "flex" }}><X size={20} color={C.textMuted} /></button>
      </div>
      {children}
    </div>
  </div>
);

const TableWrap = ({ children }) => (
  <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: 600, marginTop: 16 }}>
    <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 500 }}>{children}</table>
  </div>
);
const Th = ({ c }) => <th style={{ padding: "10px 14px", textAlign: "left", color: C.textMuted, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8, borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap", position: "sticky", top: 0, zIndex: 1, background: C.surface }}>{c}</th>;
const Td = ({ children }) => <td style={{ padding: "12px 14px", color: C.textSub, fontSize: 14, borderBottom: `1px solid ${C.border}22` }}>{children}</td>;

// ── SKELETON LOADER ───────────────────────────────────────────────────────────
const shimmer = `
  @keyframes shimmer {
    0%   { background-position: -400px 0; }
    100% { background-position:  400px 0; }
  }
  @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
  @keyframes pulse { 0%,80%,100% { opacity: 0.3; transform: scale(0.8); } 40% { opacity: 1; transform: scale(1); } }
  .kpi-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
    margin-bottom: 20px;
  }
  @media (min-width: 900px) {
    .kpi-grid { grid-template-columns: repeat(4, 1fr); }
  }
  .kpi-label { font-size: 11px !important; }
  .kpi-value { font-size: 28px !important; }
  .kpi-icon  { padding: 8px !important; }
  @media (min-width: 600px) {
    .kpi-label { font-size: 12px !important; }
    .kpi-value { font-size: 32px !important; }
    .kpi-icon  { padding: 10px !important; }
  }
`;

const SkeletonCell = ({ width = "70%" }) => (
  <div style={{
    height: 13, borderRadius: 4, width,
    background: `linear-gradient(90deg, ${C.surfaceAlt} 25%, #243047 50%, ${C.surfaceAlt} 75%)`,
    backgroundSize: "400px 100%",
    animation: "shimmer 1.4s ease-in-out infinite",
  }} />
);

const SkeletonRow = ({ cols }) => (
  <tr>{Array(cols).fill(0).map((_, i) => (
    <td key={i} style={{ padding: "14px", borderBottom: `1px solid ${C.border}22` }}>
      <SkeletonCell width={i === 0 ? "80%" : i % 2 === 0 ? "60%" : "50%"} />
    </td>
  ))}</tr>
);

const SkeletonKPI = () => (
  <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 18 }}>
    <div style={{ display: "flex", justifyContent: "space-between" }}>
      <div style={{ flex: 1 }}>
        <SkeletonCell width="55%" />
        <div style={{ marginTop: 12 }}><SkeletonCell width="35%" /></div>
        <div style={{ marginTop: 10 }}><SkeletonCell width="45%" /></div>
      </div>
      <div style={{ width: 44, height: 44, borderRadius: 10, background: C.surfaceAlt, animation: "shimmer 1.4s ease-in-out infinite", backgroundSize: "400px 100%", backgroundImage: `linear-gradient(90deg, ${C.surfaceAlt} 25%, #243047 50%, ${C.surfaceAlt} 75%)` }} />
    </div>
  </div>
);

// ── FILTER BANNER ─────────────────────────────────────────────────────────────
const FilterBanner = ({ label, color, count, onClear }) => (
  <div style={{ background: `${color}12`, border: `1px solid ${color}44`, borderRadius: 10, padding: "12px 14px", marginBottom: 18 }}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ background: `${color}22`, border: `1px solid ${color}44`, borderRadius: 6, padding: "3px 8px", color, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>Filtered</span>
        <span style={{ color, fontWeight: 600, fontSize: 14 }}>{label}</span>
        <span style={{ background: `${color}22`, color, borderRadius: 20, padding: "2px 10px", fontSize: 12, fontWeight: 700 }}>{count} driver{count !== 1 ? "s" : ""}</span>
      </div>
      <button onClick={onClear} style={{ background: "none", border: `1px solid ${color}44`, borderRadius: 6, color, fontSize: 12, fontWeight: 600, cursor: "pointer", padding: "5px 12px", whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 4, fontFamily: "inherit" }}>
        <X size={12} color={color} /> Clear
      </button>
    </div>
  </div>
);

// ── TOAST ─────────────────────────────────────────────────────────────────────
const Toast = ({ msg, color }) => (
  <div style={{ position: "fixed", bottom: 24, right: 24, background: C.surface, border: `1px solid ${color}55`, borderRadius: 10, padding: "12px 18px", color, fontWeight: 600, fontSize: 14, zIndex: 200, boxShadow: `0 4px 20px ${color}25`, display: "flex", alignItems: "center", gap: 8 }}>
    {color === C.green ? <CheckCircle size={16} color={color} /> : <XCircle size={16} color={color} />} {msg}
  </div>
);

// ── TREND CHART ───────────────────────────────────────────────────────────────
const TrendChart = () => {
  const data = [{ m: "Oct", v: 72 },{ m: "Nov", v: 75 },{ m: "Dec", v: 68 },{ m: "Jan", v: 80 },{ m: "Feb", v: 78 },{ m: "Mar", v: 84 }];
  const h = 100;
  const pts = data.map((d, i) => ({ x: (i / (data.length - 1)) * 100, y: h - (d.v / 100) * h }));
  const path = pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
  const area = `${path} L ${pts[pts.length - 1].x} ${h} L ${pts[0].x} ${h} Z`;
  return (
    <div>
      <div style={{ color: C.textMuted, fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>Compliance Trend</div>
      <svg viewBox={`0 0 100 ${h}`} preserveAspectRatio="none" style={{ width: "100%", height: 80, display: "block" }}>
        <defs><linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={C.green} stopOpacity="0.3" /><stop offset="100%" stopColor={C.green} stopOpacity="0" /></linearGradient></defs>
        <path d={area} fill="url(#g1)" /><path d={path} fill="none" stroke={C.green} strokeWidth="2" />
        {pts.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r="2.5" fill={C.green} stroke={C.surface} strokeWidth="1.5" />)}
      </svg>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
        {data.map(d => <span key={d.m} style={{ color: C.textMuted, fontSize: 11 }}>{d.m}</span>)}
      </div>
    </div>
  );
};

// ── MANDATE VIEWER ────────────────────────────────────────────────────────────
const MandateViewer = ({ driver, onClose, company, sessionToken, showToast }) => {
  const [licenceSummary, setLicenceSummary] = useState(null);
  const [greyFleetCheck, setGreyFleetCheck] = useState(null);
  const [loadingData, setLoadingData]       = useState(true);
  const [resending, setResending]           = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoadingData(true);
      const token = sessionToken?.current || SUPABASE_KEY;
      const headers = { apikey: SUPABASE_KEY, Authorization: `Bearer ${token}` };
      try {
        const lsRes = await fetch(
          `${SUPABASE_URL}/rest/v1/licence_summaries?driver_id=eq.${driver.id}&select=*&order=extracted_at.desc&limit=1`,
          { headers }
        );
        if (lsRes.ok) {
          const rows = await lsRes.json();
          setLicenceSummary(rows?.[0] || null);
        }
      } catch {}
      if (driver.is_grey_fleet) {
        try {
          const gfRes = await fetch(
            `${SUPABASE_URL}/rest/v1/grey_fleet_checks?driver_id=eq.${driver.id}&select=*&limit=1`,
            { headers }
          );
          if (gfRes.ok) {
            const rows = await gfRes.json();
            setGreyFleetCheck(rows?.[0] || null);
          }
        } catch {}
      }
      setLoadingData(false);
    };
    load();
  }, [driver.id, driver.is_grey_fleet, sessionToken]);

  const resendEmail = async () => {
    setResending(true);
    try {
      const endpoint = company?.product_tier === "lite" ? "send-lite-mandate-email" : "send-mandate-email";
      const res = await fetch(`${SUPABASE_URL}/functions/v1/${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${SUPABASE_KEY}`, "apikey": SUPABASE_KEY },
        body: JSON.stringify({ driver_id: driver.id, driver_name: driver.name, driver_email: driver.email, company_name: company?.name, product_tier: company?.product_tier, is_grey_fleet: driver.is_grey_fleet }),
      });
      if (!res.ok) throw new Error();
      showToast?.(`Check email resent to ${driver.name}`, C.green);
    } catch { showToast?.("Failed to resend email", C.red); }
    setResending(false);
  };

  const SectionLabel = ({ children }) => (
    <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>{children}</div>
  );
  const Field = ({ label, value, full }) => (
    <div style={full ? { gridColumn: "1 / -1" } : {}}>
      <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 3 }}>{label}</div>
      <div style={{ color: C.text, fontSize: 13, fontWeight: 500 }}>{value || "—"}</div>
    </div>
  );
  const Section = ({ children }) => (
    <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 10, padding: 18, marginBottom: 12 }}>{children}</div>
  );

  const isCompleted = driver.mandate_status === "Completed";
  const outcomeCol = greyFleetCheck?.ai_outcome === "Green" ? C.green : greyFleetCheck?.ai_outcome === "Red" ? C.red : C.amber;

  return (
    <Modal title="Compliance Record" onClose={onClose}>
      {/* Section 1 — Driver & Consent */}
      <Section>
        <SectionLabel>Driver &amp; Consent</SectionLabel>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 16px", marginBottom: 14 }}>
          <Field label="Driver Name" value={driver.name} />
          <Field label="Email" value={driver.email} />
          <Field label="Mandate Status" value={<RiskBadge level={driver.mandate_status} />} />
          <Field label="Mandate Expiry" value={fmtDate(driver.mandate_expiry)} />
          <Field label="Last Check" value={fmtDate(driver.last_check)} />
        </div>
        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 12, color: C.textMuted, fontSize: 12, lineHeight: 1.6 }}>
          {isCompleted
            ? <>This driver has provided consent for DVLA licence checks on behalf of <strong style={{ color: C.textSub }}>{company?.name || "your organisation"}</strong>.</>
            : <span style={{ color: C.amber }}>Consent mandate not yet completed by this driver.</span>
          }
        </div>
      </Section>

      {/* Section 2 — Licence Details */}
      {loadingData ? (
        <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 10, padding: 18, marginBottom: 12, color: C.textMuted, fontSize: 13 }}>Loading licence data…</div>
      ) : licenceSummary ? (
        <Section>
          <SectionLabel>Licence Details</SectionLabel>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 16px", marginBottom: licenceSummary.categories?.length ? 14 : 0 }}>
            <Field label="Driving Status" value={<RiskBadge level={licenceSummary.driving_status} />} />
            <Field label="Points" value={licenceSummary.points ?? "—"} />
            <Field label="Endorsements" value={licenceSummary.endorsements ?? "—"} />
            <Field label="Check Code" value={licenceSummary.check_code} />
            <Field label="Licence Valid From" value={fmtDate(licenceSummary.licence_valid_from)} />
            <Field label="Licence Valid To" value={fmtDate(licenceSummary.licence_valid_to)} />
            <Field label="Summary Generated" value={fmtDate(licenceSummary.generated_date)} />
          </div>
          {licenceSummary.categories?.length > 0 && (
            <>
              <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 12, marginBottom: 10, color: C.textMuted, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>Authorised Categories</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {licenceSummary.categories.map((cat, i) => (
                  <span key={i} style={{ background: `${C.accent}18`, border: `1px solid ${C.accent}33`, borderRadius: 6, padding: "3px 10px", color: C.accent, fontSize: 12, fontWeight: 600 }}>
                    {cat.category || cat}
                  </span>
                ))}
              </div>
            </>
          )}
        </Section>
      ) : (
        <Section>
          <div style={{ display: "flex", alignItems: "center", gap: 10, color: C.textMuted, fontSize: 13 }}>
            <Clock size={15} color={C.textMuted} />
            No licence data on file — mandate pending completion.
          </div>
        </Section>
      )}

      {/* Section 3 — Grey Fleet */}
      {driver.is_grey_fleet && greyFleetCheck && (
        <Section>
          <SectionLabel>Grey Fleet</SectionLabel>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 16px" }}>
            <div>
              <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 3 }}>Registration</div>
              {greyFleetCheck.reg
                ? <span style={{ background: `${C.accent}18`, border: `1px solid ${C.accent}33`, borderRadius: 6, padding: "3px 10px", color: C.accent, fontFamily: "'Courier New', monospace", fontWeight: 700, fontSize: 13, letterSpacing: 2 }}>{greyFleetCheck.reg}</span>
                : <span style={{ color: C.textMuted }}>—</span>
              }
            </div>
            <div>
              <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 3 }}>Insurance Outcome</div>
              {greyFleetCheck.ai_outcome
                ? <span style={{ background: `${outcomeCol}15`, border: `1px solid ${outcomeCol}44`, borderRadius: 6, padding: "3px 10px", color: outcomeCol, fontWeight: 700, fontSize: 12 }}>{greyFleetCheck.ai_outcome}</span>
                : <span style={{ color: C.textMuted }}>—</span>
              }
            </div>
            <Field label="Policy Expiry" value={fmtDate(greyFleetCheck.policy_expiry)} />
            {greyFleetCheck.fm_resolution && <Field label="FM Resolution" value={greyFleetCheck.fm_resolution} full />}
            {greyFleetCheck.fm_notes && <Field label="FM Notes" value={greyFleetCheck.fm_notes} full />}
          </div>
        </Section>
      )}

      {/* Section 4 — Actions */}
      <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
        <Btn variant="secondary" onClick={onClose} style={{ flex: 1 }}>Close</Btn>
        <Btn variant="ghost" onClick={resendEmail} disabled={resending} style={{ flex: 1 }}>
          <Mail size={14} /> {resending ? "Sending…" : "Resend Check Email"}
        </Btn>
      </div>
    </Modal>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// PAGES
// ═══════════════════════════════════════════════════════════════════════════════
const fmtDate = (str) => {
  if (!str) return "—";
  const d = new Date(str);
  if (isNaN(d)) return str;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const GreyFleetBadge = ({ outcome, fmResolution, policyExpiry }) => {
  if (!outcome) return (
    <span style={{ background: "rgba(100,116,139,0.12)", color: "#64748B", border: "1px solid rgba(100,116,139,0.2)", borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 5 }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#64748B", display: "inline-block" }} />
      Not Checked
    </span>
  );

  // FM resolution overrides AI outcome badge
  if (fmResolution) {
    const fmCfg =
      fmResolution.startsWith("Accepted")  ? { bg: "rgba(16,185,129,0.12)",  color: "#10B981", label: "Accepted" } :
      fmResolution.startsWith("Escalated") ? { bg: "rgba(245,158,11,0.12)",  color: "#F59E0B", label: "Escalated" } :
      fmResolution.startsWith("Exempted")  ? { bg: "rgba(245,158,11,0.12)",  color: "#F59E0B", label: "Exempted" } :
      fmResolution.startsWith("Rejected")  ? { bg: "rgba(239,68,68,0.12)",   color: "#EF4444", label: "Rejected" } :
      null;
    if (fmCfg) return (
      <span style={{ background: fmCfg.bg, color: fmCfg.color, border: `1px solid ${fmCfg.color}33`, borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 5 }}>
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: fmCfg.color, display: "inline-block" }} />
        {fmCfg.label}
      </span>
    );
  }

  // If policy has expired and no FM resolution, show red Insurance Expired
  const insuranceExpired = policyExpiry && new Date(policyExpiry) < new Date() && !fmResolution;

  const cfg = insuranceExpired
    ? { bg: "rgba(239,68,68,0.12)", color: "#EF4444", label: "Insurance Expired" }
    : {
        Green: { bg: "rgba(16,185,129,0.12)",  color: "#10B981", label: "Confirmed" },
        Amber: { bg: "rgba(245,158,11,0.12)",  color: "#F59E0B", label: "Declaration Needed" },
        Red:   { bg: "rgba(239,68,68,0.12)",   color: "#EF4444", label: "FM Review Required" },
      }[outcome] || { bg: "rgba(100,116,139,0.12)", color: "#64748B", label: outcome };
  return (
    <span style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}33`, borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 5 }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: cfg.color, display: "inline-block" }} />
      {cfg.label}
    </span>
  );
};

// ── INSURANCE UPLOAD MODAL ────────────────────────────────────────────────────
const InsuranceUploadModal = ({ driver, existingData, onClose, onSave, showToast, company, sessionToken }) => {
  const [step, setStep] = useState("form");
  const [reg, setReg] = useState(existingData?.reg || "");
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState(existingData?.fileName || null);
  const [dragOver, setDragOver] = useState(false);
  const [aiResult] = useState(existingData?.aiResult || null);
  const [policyExpiry, setPolicyExpiry] = useState(existingData?.policyExpiry || "");
  const [driverDeclaration, setDriverDeclaration] = useState(false);
  const [checking, setChecking] = useState(false);

  const handleFile = (f) => { if (!f) return; setFile(f); setFileName(f.name); };
  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f && f.type === "application/pdf") handleFile(f);
    else showToast("Please upload a PDF insurance certificate", C.red);
  };

  const runRealCheck = async () => {
    if (!file) {
      showToast("Please upload the insurance certificate first", C.amber);
      return;
    }
    setChecking(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(",")[1]);
        reader.onerror = () => reject(new Error("Failed to read file"));
        reader.readAsDataURL(file);
      });

      const token = sessionToken?.current || SUPABASE_KEY;
      const res = await fetch(`${SUPABASE_URL}/functions/v1/check-insurance-pdf`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          apikey: SUPABASE_KEY,
        },
        body: JSON.stringify({
          insurance_pdf_base64: base64,
          vehicle_registration: reg,
          driver_id: driver.id,
          company_id: company?.id || null,
        }),
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Check failed");

      onSave(driver.id, { reg, fileName, aiResult: { outcome: json.outcome, summary: json.notes } });
      showToast("Insurance check complete", C.green);
      onClose();
    } catch (err) {
      showToast(err.message || "Check failed — please try again", C.red);
    }
    setChecking(false);
  };

  const handleSave = () => {
    let recheckDue = null;
    if (policyExpiry) {
      const d = new Date(policyExpiry);
      d.setDate(d.getDate() + 1);
      recheckDue = d.toISOString().split("T")[0];
    } else {
      const d = new Date();
      d.setMonth(d.getMonth() + 11);
      recheckDue = d.toISOString().split("T")[0];
    }
    onSave(driver.id, {
      reg: reg.toUpperCase().trim(),
      fileName,
      aiResult,
      policyExpiry: policyExpiry || null,
      recheckDue,
      checkedAt: new Date().toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }),
      driverDeclaration: aiResult?.outcome === "Amber" ? driverDeclaration : null,
    });
    onClose();
    showToast(`Insurance check saved for ${driver.name}`, C.green);
  };

  const resultColor = aiResult ? ({ Green: C.green, Amber: C.amber, Red: C.red }[aiResult.outcome] || C.textMuted) : null;
  const resultBg    = aiResult ? ({ Green: C.greenBg, Amber: C.amberBg, Red: C.redBg }[aiResult.outcome] || "transparent") : null;
  const ResultIcon  = aiResult?.outcome === "Green" ? CheckCircle : aiResult?.outcome === "Amber" ? AlertTriangle : XCircle;

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }}>
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, padding: 28, width: "100%", maxWidth: 520, maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <div>
            <div style={{ color: C.text, fontWeight: 700, fontSize: 18 }}>Grey Fleet Check</div>
            <div style={{ color: C.textMuted, fontSize: 13, marginTop: 2 }}>{driver.name} · {driver.email}</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: C.textMuted, cursor: "pointer", display: "flex" }}><X size={20} color={C.textMuted} /></button>
        </div>

        {step === "form" && (
          <>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: "block", color: C.textSub, fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Vehicle Registration</label>
              <input value={reg} onChange={e => setReg(e.target.value.toUpperCase())} placeholder="e.g. AB12 CDE" maxLength={8}
                style={{ width: "100%", background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px", color: C.text, fontSize: 16, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase", outline: "none", boxSizing: "border-box", fontFamily: "'Courier New', monospace" }} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: "block", color: C.textSub, fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Insurance Certificate (PDF)</label>
              <div onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={handleDrop}
                onClick={() => document.getElementById(`file-input-${driver.id}`).click()}
                style={{ background: dragOver ? "rgba(59,130,246,0.08)" : C.surfaceAlt, border: `2px dashed ${dragOver ? C.accent : fileName ? C.green : C.border}`, borderRadius: 10, padding: "24px 16px", textAlign: "center", cursor: "pointer", transition: "all 0.15s" }}>
                <input id={`file-input-${driver.id}`} type="file" accept=".pdf" style={{ display: "none" }} onChange={e => handleFile(e.target.files[0])} />
                {fileName ? (
                  <><FileCheck size={28} color={C.green} style={{ margin: "0 auto 6px" }} /><div style={{ color: C.green, fontWeight: 600, fontSize: 14 }}>{fileName}</div><div style={{ color: C.textMuted, fontSize: 12, marginTop: 4 }}>Click to replace</div></>
                ) : (
                  <><Upload size={28} color={C.textMuted} style={{ margin: "0 auto 6px" }} /><div style={{ color: C.textSub, fontSize: 14, fontWeight: 500 }}>Drop PDF here or click to browse</div><div style={{ color: C.textMuted, fontSize: 12, marginTop: 4 }}>Insurance certificate · PDF only</div></>
                )}
              </div>
            </div>
            <div style={{ background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: 8, padding: "12px 14px", marginBottom: 20, display: "flex", gap: 10, alignItems: "flex-start" }}>
              <Bot size={16} color={C.accent} style={{ flexShrink: 0, marginTop: 1 }} />
              <div>
                <div style={{ color: C.text, fontSize: 13, fontWeight: 600, marginBottom: 2 }}>AI-powered check</div>
                <div style={{ color: C.textMuted, fontSize: 12, lineHeight: 1.5 }}>The certificate will be analysed automatically to confirm business use coverage. Most standard UK certificates are confirmed in seconds.</div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={onClose} style={{ flex: 1, background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 8, color: C.textSub, fontWeight: 600, padding: "10px 0", cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
              <button onClick={runRealCheck} disabled={!fileName || !reg.trim() || checking} style={{ flex: 2, background: (!fileName || !reg.trim() || checking) ? C.surfaceAlt : C.accent, border: "none", borderRadius: 8, color: (!fileName || !reg.trim() || checking) ? C.textMuted : "#fff", fontWeight: 600, padding: "10px 0", cursor: (!fileName || !reg.trim() || checking) ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
                {checking ? "Checking…" : "Run Insurance Check →"}
              </button>
            </div>
          </>
        )}

        {step === "checking" && (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
              <RefreshCw size={40} color={C.accent} style={{ animation: "spin 1.2s linear infinite" }} />
            </div>
            <div style={{ color: C.text, fontWeight: 700, fontSize: 18, marginBottom: 8 }}>Analysing Certificate</div>
            <div style={{ color: C.textMuted, fontSize: 14, lineHeight: 1.6, maxWidth: 320, margin: "0 auto" }}>Scanning for business use clauses and coverage class…</div>
            <div style={{ marginTop: 24, display: "flex", justifyContent: "center", gap: 6 }}>
              {[0, 1, 2].map(i => (
                <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: C.accent, animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite` }} />
              ))}
            </div>
          </div>
        )}

        {step === "result" && aiResult && (
          <>
            <div style={{ background: resultBg, border: `1px solid ${resultColor}33`, borderRadius: 10, padding: "16px 18px", marginBottom: 20 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <ResultIcon size={20} color={resultColor} />
                <div>
                  <div style={{ color: resultColor, fontWeight: 700, fontSize: 15 }}>
                    {aiResult.outcome === "Green" && "Business Use Confirmed"}
                    {aiResult.outcome === "Amber" && "Ambiguous — Driver Declaration Needed"}
                    {aiResult.outcome === "Red" && "No Business Use Coverage Found"}
                  </div>
                  <div style={{ color: C.textSub, fontSize: 12, marginTop: 1 }}>AI confidence: {aiResult.confidence}%</div>
                </div>
              </div>
              <div style={{ color: C.textSub, fontSize: 13, lineHeight: 1.6 }}>{aiResult.summary}</div>
            </div>
            {aiResult.clause && (
              <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: "12px 14px", marginBottom: 20 }}>
                <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Extracted Clause</div>
                <div style={{ color: C.textSub, fontSize: 13, fontStyle: "italic", lineHeight: 1.6 }}>"{aiResult.clause}"</div>
              </div>
            )}
            <div style={{ background: C.surfaceAlt, border: `1px solid ${policyExpiry ? C.green : C.amber}44`, borderRadius: 8, padding: "12px 14px", marginBottom: 20 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>Policy Expiry Date</div>
                {policyExpiry
                  ? <span style={{ background: C.greenBg, color: C.green, border: `1px solid ${C.green}33`, borderRadius: 20, padding: "2px 8px", fontSize: 11, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}><CheckCircle size={11} /> Extracted by AI</span>
                  : <span style={{ background: C.amberBg, color: C.amber, border: `1px solid ${C.amber}33`, borderRadius: 20, padding: "2px 8px", fontSize: 11, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}><AlertTriangle size={11} /> Enter manually</span>}
              </div>
              <input type="date" value={policyExpiry} onChange={e => setPolicyExpiry(e.target.value)}
                style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: "8px 10px", color: C.text, fontSize: 14, outline: "none", boxSizing: "border-box" }} />
            </div>
            {aiResult.outcome === "Amber" && (
              <div style={{ background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.2)", borderRadius: 8, padding: "12px 14px", marginBottom: 20 }}>
                <div style={{ color: C.textSub, fontSize: 13, marginBottom: 10, lineHeight: 1.5 }}>Ask the driver to confirm their policy covers business driving before saving.</div>
                <label style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer" }}>
                  <input type="checkbox" checked={driverDeclaration} onChange={e => setDriverDeclaration(e.target.checked)} style={{ marginTop: 2, accentColor: "#F59E0B" }} />
                  <span style={{ color: C.text, fontSize: 13, lineHeight: 1.5 }}>Driver has verbally confirmed their certificate covers business use. Log this declaration.</span>
                </label>
              </div>
            )}
            <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px", marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>Registration</div>
                <div style={{ color: C.text, fontSize: 16, fontWeight: 700, fontFamily: "'Courier New', monospace", letterSpacing: 2, marginTop: 2 }}>{reg.toUpperCase()}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>Certificate</div>
                <div style={{ color: C.textSub, fontSize: 12, marginTop: 2 }}>{fileName}</div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setStep("form")} style={{ flex: 1, background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 8, color: C.textSub, fontWeight: 600, padding: "10px 0", cursor: "pointer", fontFamily: "inherit" }}>← Re-upload</button>
              <button onClick={handleSave} disabled={aiResult.outcome === "Amber" && !driverDeclaration} style={{ flex: 2, background: (aiResult.outcome === "Amber" && !driverDeclaration) ? C.surfaceAlt : C.accent, border: "none", borderRadius: 8, color: (aiResult.outcome === "Amber" && !driverDeclaration) ? C.textMuted : "#fff", fontWeight: 600, padding: "10px 0", cursor: (aiResult.outcome === "Amber" && !driverDeclaration) ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
                Save & Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ── VES DATE CELL ─────────────────────────────────────────────────────────────
const VesDateCell = ({ dateStr, label }) => {
  if (!dateStr) return (
    <div>
      <div style={{ color: C.textMuted, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 3 }}>{label}</div>
      <span style={{ color: C.textMuted, fontSize: 13 }}>—</span>
    </div>
  );
  const s = vesStatus(dateStr);
  const VesIcon = s.iconName === "XCircle" ? XCircle : s.iconName === "AlertTriangle" ? AlertTriangle : CheckCircle;
  return (
    <div>
      <div style={{ color: C.textMuted, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 3 }}>{label}</div>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 5, background: `${s.color}15`, border: `1px solid ${s.color}33`, borderRadius: 6, padding: "3px 8px", fontSize: 12, fontWeight: 600, color: s.color }}>
        <VesIcon size={11} color={s.color} strokeWidth={2} />
        {s.label}
      </span>
    </div>
  );
};

// ── GREY FLEET PAGE ───────────────────────────────────────────────────────────
const GreyFleetPage = ({ drivers, loading, greyFleetData, onUpdateGreyFleet, onRefresh, showToast, filter, clearFilter, company, sessionToken }) => {
  const [uploadModal,    setUploadModal]    = useState(null);
  const [detailModal,    setDetailModal]    = useState(null);
  const [fmEditMode,     setFmEditMode]     = useState(false);
  const [fmSelectedRes,  setFmSelectedRes]  = useState("");
  const [fmNoteText,     setFmNoteText]     = useState("");
  const [fmSaving,       setFmSaving]       = useState(false);
  const [vesDataMap,     setVesDataMap]     = useState({});

  useEffect(() => {
    const regs = Object.values(greyFleetData)
      .flatMap(vehicles => vehicles.map(v => v?.reg?.replace(/\s+/g, "").toUpperCase()))
      .filter(Boolean);
    if (regs.length === 0) { setVesDataMap({}); return; }
    const token = sessionToken?.current || SUPABASE_KEY;
    fetch(`${SUPABASE_URL}/rest/v1/vehicle_ves_data?registration=in.(${regs.join(",")})&select=*`, {
      headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(rows => {
        const map = {};
        rows.forEach(row => { map[row.registration] = row; });
        setVesDataMap(map);
      })
      .catch(err => console.error("VES data fetch error:", err));
  }, [greyFleetData, sessionToken]);

  const closeModal = () => { setDetailModal(null); setFmEditMode(false); setFmSelectedRes(""); setFmNoteText(""); };
  const openModal  = (d)  => { setDetailModal(d);  setFmEditMode(false); setFmSelectedRes(""); setFmNoteText(""); };

  const saveFmReview = async () => {
    if (!fmSelectedRes || !detailModal) return;
    setFmSaving(true);
    try {
      const token = sessionToken?.current || SUPABASE_KEY;
      const newOutcome = fmSelectedRes.startsWith("Accepted") ? "Green" : "Amber";
      const patchBody = {
        fm_resolution:  fmSelectedRes,
        fm_notes:       fmNoteText,
        fm_reviewed_at: new Date().toISOString(),
        fm_reviewed_by: company?.name || null,
        ai_outcome:     newOutcome,
      };
      const reg = detailModal.gf?.reg?.replace(/\s+/g, "").toUpperCase();
      console.log("[saveFmReview] PATCH grey_fleet_checks driver_id=", detailModal.id, "body:", patchBody);
      const res = await fetch(`${SUPABASE_URL}/rest/v1/grey_fleet_checks?driver_id=eq.${detailModal.id}&reg=eq.${reg}`, {
        method: "PATCH",
        headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${token}`, "Content-Type": "application/json", Prefer: "return=minimal" },
        body: JSON.stringify(patchBody),
      });
      const resText = await res.text();
      console.log("[saveFmReview] response status:", res.status, "body:", resText);
      if (!res.ok) throw new Error();
      showToast("Review saved", C.green);
      setDetailModal(prev => ({ ...prev, gf: { ...prev.gf, fmResolution: fmSelectedRes, fmNotes: fmNoteText, fmReviewedAt: new Date().toISOString(), fmReviewedBy: company?.name || null, aiResult: { ...prev.gf?.aiResult, outcome: newOutcome } } }));
      setFmEditMode(false);
      onRefresh();
    } catch { showToast("Failed to save review", C.red); }
    setFmSaving(false);
  };

  const gfDrivers = drivers.filter(d => d.is_grey_fleet);
  const enriched = gfDrivers.flatMap(d => {
    const vehicles = greyFleetData[d.id] || [];
    if (vehicles.length === 0) return [{ ...d, gf: null, ves: null }];
    return vehicles.map(gf => {
      const reg = gf.reg?.replace(/\s+/g, "").toUpperCase();
      const ves = reg ? vesDataMap[reg] : null;
      return { ...d, gf, ves };
    });
  });
  const displayed = filter ? enriched.filter(d => {
    if (filter.key === "gf_outcome")     return (d.gf?.aiResult?.outcome || "None") === filter.value;
    if (filter.key === "gf_no_check")    return !d.gf;
    if (filter.key === "ins_alert")      return d.gf?.aiResult?.outcome === "Red" || (d.gf?.policyExpiry && new Date(d.gf.policyExpiry) < new Date());
    if (filter.key === "ves_alert") {
      if (!d.ves) return false;
      const motAlert = (d.ves.mot_expiry && vesStatus(d.ves.mot_expiry).color !== C.green) ||
                       (!d.ves.mot_expiry && d.ves.mot_status && d.ves.mot_status !== "Valid");
      const taxAlert = (d.ves.tax_expiry && vesStatus(d.ves.tax_expiry).color !== C.green) ||
                       (!d.ves.tax_expiry && d.ves.tax_status && d.ves.tax_status !== "Taxed");
      return motAlert || taxAlert;
    }
    if (filter.key === "gf_pending_decl") return d.gf?.aiResult?.outcome === "Amber";
    return true;
  }) : enriched;
  const total = enriched.length;

  return (
    <div>
      <style>{`
        .gf-row:hover { background: #1a2235 !important; }
        .gf-row { transition: background 0.1s; }
      `}</style>
      <div style={{ marginBottom: 24 }}>
        <div style={{ color: C.text, fontWeight: 700, fontSize: 22, marginBottom: 4 }}>Grey Fleet</div>
        <div style={{ color: C.textMuted, fontSize: 14 }}>{displayed.length} of {total} grey fleet drivers</div>
      </div>
      {filter && <FilterBanner label={filter.label} color={filter.color} count={displayed.length} onClear={clearFilter} />}
      {!loading && total === 0 && (
        <div style={{ background: C.blueBg, border: "1px solid rgba(59,130,246,0.25)", borderRadius: 10, padding: "18px 20px", marginBottom: 20, display: "flex", gap: 14, alignItems: "flex-start" }}>
          <Info size={20} color={C.accent} style={{ flexShrink: 0 }} />
          <div>
            <div style={{ color: C.text, fontWeight: 600, fontSize: 14, marginBottom: 4 }}>No grey fleet drivers yet</div>
            <div style={{ color: C.textMuted, fontSize: 13, lineHeight: 1.6 }}>
              Go to the <strong style={{ color: C.textSub }}>Drivers</strong> page and tick "Grey Fleet" on any driver who uses their personal vehicle for work. They'll appear here automatically.
            </div>
          </div>
        </div>
      )}
      {total > 0 && (
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 780 }}>
              <thead style={{ position: "sticky", top: 0, zIndex: 2 }}>
                <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                  {["Driver", "Registration", "Insurance Check", "Last Check", "Policy Expiry", "MOT Expiry", "Tax Expiry", "Actions"].map(h => (
                    <th key={h} style={{ padding: "10px 16px", textAlign: "left", color: C.textMuted, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, whiteSpace: "nowrap", background: C.surface }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? Array(5).fill(0).map((_, i) => <SkeletonRow key={i} cols={8} />) :
                  displayed.map(d => (
                    <tr key={d.id} className="gf-row"
                        style={{ borderBottom: `1px solid ${C.border}22`, background: "transparent", cursor: "pointer" }}
                        onClick={() => openModal(d)}>
                        <td style={{ padding: "14px 16px" }}>
                          <div style={{ color: C.text, fontWeight: 600, fontSize: 14 }}>{d.name}</div>
                          <div style={{ color: C.textMuted, fontSize: 12, marginTop: 1 }}>{d.email}</div>
                        </td>
                        <td style={{ padding: "14px 16px" }}>
                          {d.gf?.reg
                            ? <span style={{ color: C.text, fontWeight: 700, fontFamily: "'Courier New', monospace", letterSpacing: 2, background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 6, padding: "3px 8px", fontSize: 13 }}>{d.gf.reg}</span>
                            : <span style={{ color: C.textMuted, fontSize: 13 }}>—</span>}
                        </td>
                        <td style={{ padding: "14px 16px" }}><GreyFleetBadge outcome={d.gf?.aiResult?.outcome} fmResolution={d.gf?.fmResolution} policyExpiry={d.gf?.policyExpiry} /></td>
                        <td style={{ padding: "14px 16px", color: C.textSub, fontSize: 13 }}>{fmtDate(d.gf?.checkedAt)}</td>
                        <td style={{ padding: "14px 16px" }}><VesDateCell dateStr={d.gf?.policyExpiry} label="Policy" /></td>
                        <td style={{ padding: "14px 16px" }}>
                          {d.ves?.mot_expiry
                            ? <VesDateCell dateStr={d.ves.mot_expiry} label="MOT" />
                            : d.ves?.mot_status
                              ? <div><div style={{ color: C.textMuted, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 3 }}>MOT</div><span style={{ color: C.textMuted, fontSize: 13 }}>{d.ves.mot_status}</span></div>
                              : <VesDateCell dateStr={null} label="MOT" />}
                        </td>
                        <td style={{ padding: "14px 16px" }}><VesDateCell dateStr={d.ves?.tax_expiry} label="Tax" /></td>
                        <td style={{ padding: "14px 16px" }}>
                          <button onClick={e => { e.stopPropagation(); setUploadModal(d); }}
                            style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 7, color: C.textSub, fontWeight: 600, fontSize: 12, padding: "6px 12px", cursor: "pointer", whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 5, fontFamily: "inherit" }}
                            onMouseOver={e => e.currentTarget.style.borderColor = C.accent}
                            onMouseOut={e => e.currentTarget.style.borderColor = C.border}>
                            {d.gf ? <><RotateCcw size={12} /> Re-check</> : <><FileCheck size={12} /> Run Check</>}
                          </button>
                        </td>
                      </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {total > 0 && (
        <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ color: C.textMuted, fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
            <RefreshCw size={12} color={C.textMuted} /> Insurance re-check reminders are scheduled 1 month before policy expiry and triggered automatically on renewal.
          </div>
          <div style={{ color: C.textMuted, fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
            <Info size={12} color={C.textMuted} /> MOT and road tax dates are currently mocked for demo. Once DVLA VES is integrated, these will refresh nightly across all grey fleet vehicles at no cost.
          </div>
        </div>
      )}
      {detailModal && (() => {
        const d = detailModal;
        const outcome = d.gf?.aiResult?.outcome;
        const needsFmReview = outcome === "Amber" || outcome === "Red";
        return (
          <Modal title="Vehicle Details" onClose={closeModal}>
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                <div>
                  <div style={{ color: C.text, fontWeight: 700, fontSize: 16 }}>{d.name}</div>
                  {d.gf?.reg && (
                    <span style={{ color: C.text, fontWeight: 700, fontFamily: "'Courier New', monospace", letterSpacing: 2, background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 6, padding: "3px 8px", fontSize: 13, display: "inline-block", marginTop: 6 }}>{d.gf.reg}</span>
                  )}
                </div>
                <GreyFleetBadge outcome={outcome} fmResolution={d.gf?.fmResolution} policyExpiry={d.gf?.policyExpiry} />
              </div>
              {[
                ["Policy type",   d.gf?.aiResult?.clause || "—",  d.gf?.aiResult?.outcome === "Red" ? C.red : C.textSub],
                ["Policy expiry", fmtDate(d.gf?.policyExpiry),    d.gf?.policyExpiry ? vesStatus(d.gf.policyExpiry).color : C.textSub],
                ["Last checked",  fmtDate(d.gf?.checkedAt),       C.textSub],
                ...(d.ves?.mot_expiry ? [["MOT Expiry", fmtDate(d.ves.mot_expiry), vesStatus(d.ves.mot_expiry).color]] : []),
                ...(d.ves?.tax_expiry ? [["Tax Expiry", fmtDate(d.ves.tax_expiry), vesStatus(d.ves.tax_expiry).color]] : []),
              ].map(([label, value, valueColor = C.textSub]) => (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "10px 0", borderBottom: `1px solid ${C.border}22` }}>
                  <span style={{ color: C.textMuted, fontSize: 13 }}>{label}</span>
                  <span style={{ color: valueColor, fontSize: 13, textAlign: "right", maxWidth: "60%" }}>{value}</span>
                </div>
              ))}
              {d.gf?.aiResult?.summary && (
                <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: 12, marginTop: 14 }}>
                  <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Notes</div>
                  <div style={{ color: C.textSub, fontSize: 13, lineHeight: 1.6 }}>{d.gf.aiResult.summary}</div>
                </div>
              )}
            </div>

            {(needsFmReview || d.gf?.fmResolution) && (
              <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 18, marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                  <div style={{ color: C.textSub, fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>Fleet Manager Review</div>
                  {d.gf?.fmResolution && !fmEditMode && (
                    <Btn small variant="secondary" onClick={() => { setFmSelectedRes(d.gf.fmResolution); setFmNoteText(d.gf.fmNotes || ""); setFmEditMode(true); }}>Edit Review</Btn>
                  )}
                </div>

                {d.gf?.fmResolution && !fmEditMode ? (
                  <div>
                    <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", marginBottom: 10 }}>
                      <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Resolution</div>
                      <div style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>{d.gf.fmResolution}</div>
                    </div>
                    {d.gf.fmNotes && (
                      <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", marginBottom: 10 }}>
                        <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Notes</div>
                        <div style={{ color: C.textSub, fontSize: 13, lineHeight: 1.5 }}>{d.gf.fmNotes}</div>
                      </div>
                    )}
                    {d.gf.fmReviewedAt && (
                      <div style={{ color: C.textMuted, fontSize: 12 }}>Reviewed {fmtDate(d.gf.fmReviewedAt)}{d.gf.fmReviewedBy ? ` by ${d.gf.fmReviewedBy}` : ""}</div>
                    )}
                  </div>
                ) : (
                  <div>
                    <select
                      value={fmSelectedRes}
                      onChange={e => setFmSelectedRes(e.target.value)}
                      style={{ width: "100%", background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px", color: fmSelectedRes ? C.text : C.textMuted, fontSize: 13, outline: "none", boxSizing: "border-box", fontFamily: "inherit", marginBottom: 14, cursor: "pointer" }}>
                      <option value="">Select a resolution...</option>
                      {FM_RESOLUTIONS.map(res => <option key={res} value={res}>{res}</option>)}
                    </select>
                    <textarea
                      value={fmNoteText}
                      onChange={e => setFmNoteText(e.target.value)}
                      placeholder="Add notes about the action taken..."
                      rows={3}
                      style={{ width: "100%", background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", color: C.text, fontSize: 13, resize: "vertical", outline: "none", boxSizing: "border-box", fontFamily: "inherit", marginBottom: 12 }}
                    />
                    <Btn onClick={saveFmReview} disabled={!fmSelectedRes || fmSaving} style={{ width: "100%", justifyContent: "center" }}>
                      {fmSaving ? "Saving…" : "Save Review"}
                    </Btn>
                  </div>
                )}
              </div>
            )}

            <Btn variant="secondary" onClick={closeModal} style={{ width: "100%", justifyContent: "center" }}>Close</Btn>
          </Modal>
        );
      })()}
      {uploadModal && (
        <InsuranceUploadModal driver={uploadModal} existingData={greyFleetData[uploadModal.id] || null} onClose={() => setUploadModal(null)} onSave={onUpdateGreyFleet} showToast={showToast} company={company} sessionToken={sessionToken} />
      )}
    </div>
  );
};

// ── COMPLIANCE SCORE PANEL ────────────────────────────────────────────────────
const CompliancePanel = ({ title, score, loading, segments, onSegmentClick }) => {
  const r = 46, circ = 2 * Math.PI * r;
  let offset = 0;
  const arcs = segments.map(seg => {
    const pct   = seg.pct || 0;
    const dash  = (pct / 100) * circ;
    const gap   = circ - dash;
    const rot   = offset;
    offset += (pct / 100) * 360;
    return { ...seg, dash, gap, rot };
  });
  return (
    <Card style={{ flex: 1, minWidth: 260 }}>
      <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 14 }}>{title}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
        <div style={{ position: "relative", width: 110, height: 110, flexShrink: 0 }}>
          <svg width="110" height="110" style={{ transform: "rotate(-90deg)" }}>
            <circle cx="55" cy="55" r={r} fill="none" stroke={C.border} strokeWidth="8" />
            {!loading && arcs.map((arc, i) => (
              <circle key={i} cx="55" cy="55" r={r} fill="none" stroke={arc.color} strokeWidth="8"
                strokeDasharray={`${arc.dash} ${arc.gap}`} strokeDashoffset={-(arc.rot / 360) * circ}
                strokeLinecap="butt" style={{ transition: "stroke-dasharray 0.8s ease", cursor: arc.onClick ? "pointer" : "default" }}
                onClick={arc.onClick} />
            ))}
          </svg>
          <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            {loading ? <SkeletonCell width="50%" /> : <><div style={{ color: C.text, fontSize: 22, fontWeight: 700, lineHeight: 1 }}>{score}</div><div style={{ color: C.textMuted, fontSize: 10, fontWeight: 600, marginTop: 2 }}>Score</div></>}
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 120 }}>
          {loading ? Array(3).fill(0).map((_, i) => <div key={i} style={{ marginBottom: 10 }}><SkeletonCell width="90%" /></div>) :
            segments.map((seg, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, cursor: seg.onClick ? "pointer" : "default", borderRadius: 6, padding: "3px 6px", transition: "background 0.15s" }}
                onClick={seg.onClick}
                onMouseEnter={e => seg.onClick && (e.currentTarget.style.background = C.surfaceAlt)}
                onMouseLeave={e => (e.currentTarget.style.background = "transparent")}>
                <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: seg.color, display: "inline-block", flexShrink: 0 }} />
                  <span style={{ color: C.textSub, fontSize: 12 }}>{seg.label}</span>
                </div>
                <span style={{ color: seg.color, fontWeight: 700, fontSize: 13 }}>{seg.count}</span>
              </div>
            ))}
        </div>
      </div>
    </Card>
  );
};

// ── DASHBOARD — LICENCE CHECKS TAB ───────────────────────────────────────────
const LicenceDashboard = ({ navigateTo, drivers, loading, greyFleetData }) => {
  const total        = drivers.length;
  const mandPending  = drivers.filter(d => d.mandate_status === "Pending").length;
  const mandExpired  = drivers.filter(d => d.mandate_status === "Expired").length;
  const mandValid    = drivers.filter(d => d.mandate_status === "Completed").length;
  const high         = drivers.filter(d => d.risk === "High").length;
  const banned       = drivers.filter(d => d.licence_status === "Banned").length;
  const low          = drivers.filter(d => d.risk === "Low").length;
  const medium       = drivers.filter(d => d.risk === "Medium").length;
  const mandScore    = total ? Math.round((mandValid / total) * 100) : 0;
  const licScore     = total ? Math.round((low / total) * 100) : 0;
  const mandSegments = [
    { label: "Completed", count: mandValid,   color: C.green, pct: total ? (mandValid / total) * 100 : 0,   onClick: () => navigateTo("mandates", { key: "mandate_status", value: "Completed", label: "Completed Mandates", color: C.green }) },
    { label: "Pending",   count: mandPending, color: C.amber, pct: total ? (mandPending / total) * 100 : 0, onClick: () => navigateTo("mandates", { key: "mandate_status", value: "Pending",   label: "Pending Mandates",   color: C.amber }) },
    { label: "Expired",   count: mandExpired, color: C.red,   pct: total ? (mandExpired / total) * 100 : 0, onClick: () => navigateTo("mandates", { key: "mandate_status", value: "Expired",   label: "Expired Mandates",   color: C.red   }) },
  ];
  const licSegments = [
    { label: "Low Risk",      count: low,          color: C.green, pct: total ? (low / total) * 100 : 0,              onClick: () => navigateTo("drivers", { key: "risk", value: "Low",    label: "Low Risk Drivers",    color: C.green }) },
    { label: "Medium Risk",   count: medium,        color: C.amber, pct: total ? (medium / total) * 100 : 0,          onClick: () => navigateTo("drivers", { key: "risk", value: "Medium", label: "Medium Risk Drivers", color: C.amber }) },
    { label: "High / Banned", count: high + banned, color: C.red,   pct: total ? ((high + banned) / total) * 100 : 0, onClick: () => navigateTo("drivers", { key: "risk", value: "High",   label: "High Risk Drivers",   color: C.red   }) },
  ];
  return (
    <div>
      <div className="kpi-grid">
        {loading ? Array(4).fill(0).map((_, i) => <SkeletonKPI key={i} />) : <>
          <KPICard label="Total Drivers"     value={total}       icon={Users}         color={C.blue}  sub="Registered"      onClick={() => navigateTo("drivers", null)} />
          <KPICard label="Mandates Pending"  value={mandPending} icon={Clock}         color={C.amber} sub="Awaiting consent" onClick={() => navigateTo("mandates", { key: "mandate_status", value: "Pending", label: "Pending Mandates", color: C.amber })} />
          <KPICard label="High Risk Drivers" value={high}        icon={AlertTriangle} color={C.red}   sub="Needs attention"  onClick={() => navigateTo("drivers", { key: "risk", value: "High", label: "High Risk Drivers", color: C.red })} />
          <KPICard label="Banned Drivers"    value={banned}      icon={XCircle}       color={C.red}   sub="Immediate action" onClick={() => navigateTo("drivers", { key: "licence_status", value: "Banned", label: "Banned Drivers", color: C.red })} />
        </>}
      </div>
      <div style={{ display: "flex", gap: 14, marginBottom: 20, flexWrap: "wrap" }}>
        <CompliancePanel title="Mandate Compliance" score={mandScore} loading={loading} segments={mandSegments} />
        <CompliancePanel title="Licence Compliance" score={licScore}  loading={loading} segments={licSegments}  />
      </div>
    </div>
  );
};

// ── DASHBOARD — GREY FLEET TAB ────────────────────────────────────────────────
const GreyFleetDashboard = ({ navigateTo, drivers, loading, greyFleetData, sessionToken }) => {
  const [vesDataMap, setVesDataMap] = useState({});
  const [vesLoading, setVesLoading] = useState(true);
  useEffect(() => {
    const regs = Object.values(greyFleetData)
      .flatMap(vehicles => vehicles.map(v => v?.reg?.replace(/\s+/g, "").toUpperCase()))
      .filter(Boolean);
    if (regs.length === 0) { setVesDataMap({}); setVesLoading(false); return; }
    setVesLoading(true);
    const token = sessionToken?.current || SUPABASE_KEY;
    fetch(`${SUPABASE_URL}/rest/v1/vehicle_ves_data?registration=in.(${regs.join(",")})&select=*`, {
      headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(rows => {
        const map = {};
        rows.forEach(row => { map[row.registration] = row; });
        setVesDataMap(map);
        setVesLoading(false);
      })
      .catch(() => { setVesLoading(false); });
  }, [greyFleetData, sessionToken]);

  const gfDrivers      = drivers.filter(d => d.is_grey_fleet);
  const total          = gfDrivers.length;
  const totalVehicles  = gfDrivers.reduce((acc, d) => acc + (greyFleetData[d.id]?.length || 0), 0);
  const notStarted     = gfDrivers.filter(d => !greyFleetData[d.id]?.length).length;
  const submitted      = gfDrivers.reduce((acc, d) => {
    const vehicles = greyFleetData[d.id] || [];
    return acc + vehicles.filter(v => v.aiResult?.outcome && v.aiResult.outcome !== "Uploaded").length;
  }, 0);
  const pendingDecl    = gfDrivers.reduce((acc, d) => {
    const vehicles = greyFleetData[d.id] || [];
    return acc + vehicles.filter(v => v.aiResult?.outcome === "Amber").length;
  }, 0);
  const insAlert       = gfDrivers.reduce((acc, d) => {
    const vehicles = greyFleetData[d.id] || [];
    return acc + vehicles.filter(v => v.aiResult?.outcome === "Red").length;
  }, 0);
  const motTaxAlert    = gfDrivers.reduce((acc, d) => {
    const vehicles = greyFleetData[d.id] || [];
    return acc + vehicles.filter(v => {
      const reg = v.reg?.replace(/\s+/g, "").toUpperCase();
      const ves = reg ? vesDataMap[reg] : null;
      if (!ves) return false;
      const motAlert = (ves.mot_expiry && vesStatus(ves.mot_expiry).color !== C.green) ||
                       (!ves.mot_expiry && ves.mot_status && ves.mot_status !== "Valid");
      const taxAlert = (ves.tax_expiry && vesStatus(ves.tax_expiry).color !== C.green) ||
                       (!ves.tax_expiry && ves.tax_status && ves.tax_status !== "Taxed");
      return motAlert || taxAlert;
    }).length;
  }, 0);
  const insConfirmed   = gfDrivers.reduce((acc, d) => {
    const vehicles = greyFleetData[d.id] || [];
    return acc + vehicles.filter(v => v.aiResult?.outcome === "Green").length;
  }, 0);
  const fullyComplete  = gfDrivers.reduce((acc, d) => {
    const vehicles = greyFleetData[d.id] || [];
    return acc + vehicles.filter(v => {
      if (v.aiResult?.outcome !== "Green") return false;
      const reg = v.reg?.replace(/\s+/g, "").toUpperCase();
      const ves = reg ? vesDataMap[reg] : null;
      const motOk = !ves || vesStatus(ves.mot_expiry).color === C.green;
      const taxOk = !ves || vesStatus(ves.tax_expiry).color === C.green;
      return motOk && taxOk;
    }).length;
  }, 0);
  const gfMandScore    = totalVehicles ? Math.round((fullyComplete / totalVehicles) * 100) : 0;
  const fullCompliance = fullyComplete;
  const gfCompScore    = submitted > 0 ? Math.round((fullCompliance / submitted) * 100) : 0;
  const mandSegments   = [
    { label: "Complete (reg + insurance)", count: fullyComplete,             color: C.green, pct: total ? (fullyComplete / total) * 100 : 0,                  onClick: () => navigateTo("greyfleet", { key: "gf_outcome",     value: "Green", label: "Insurance Confirmed",                    color: C.green }) },
    { label: "Submitted — needs action",   count: submitted - fullyComplete, color: C.amber, pct: total ? ((submitted - fullyComplete) / total) * 100 : 0,     onClick: () => navigateTo("greyfleet", { key: "gf_pending_decl", value: true,   label: "Pending Declaration",                   color: C.amber }) },
    { label: "Not yet submitted",          count: notStarted,                color: C.red,   pct: total ? (notStarted / total) * 100 : 0,                      onClick: () => navigateTo("greyfleet", { key: "gf_no_check",    value: true,   label: "No Check Submitted",                    color: C.red   }) },
  ];
  const compSegments   = [
    { label: "Insurance + MOT/Tax valid",  count: fullCompliance,                           color: C.green, pct: submitted ? (fullCompliance / submitted) * 100 : 0,                                  onClick: () => navigateTo("greyfleet", { key: "gf_outcome", value: "Green", label: "Insurance Confirmed",                 color: C.green }) },
    { label: "Insurance confirmed only",   count: Math.max(0, insConfirmed - fullCompliance), color: C.amber, pct: submitted ? (Math.max(0, insConfirmed - fullCompliance) / submitted) * 100 : 0,   onClick: () => navigateTo("greyfleet", { key: "gf_outcome", value: "Green", label: "Insurance Confirmed",                 color: C.green }) },
    { label: "Insurance alert / no cover", count: insAlert,                                 color: C.red,   pct: submitted ? (insAlert / submitted) * 100 : 0,                                        onClick: () => navigateTo("greyfleet", { key: "ins_alert",  value: true,  label: "Insurance Alert — No Cover or Expired", color: C.red   }) },
  ];
  return (
    <div>
      <div className="kpi-grid">
        {loading ? Array(4).fill(0).map((_, i) => <SkeletonKPI key={i} />) : <>
          <KPICard label="Grey Fleet Vehicles" value={totalVehicles} icon={Car}           color={C.blue}                        sub="Personal vehicles"        onClick={() => navigateTo("greyfleet", null)} />
          <KPICard label="Pending Declaration" value={pendingDecl} icon={AlertTriangle} color={C.amber}                       sub="Ambiguous insurance"       onClick={() => navigateTo("greyfleet", { key: "gf_pending_decl", value: true,  label: "Pending Declaration",                   color: C.amber })} />
          <KPICard label="Insurance Alert"     value={insAlert}    icon={XCircle}       color={C.red}                         sub="No cover or expired"       onClick={() => navigateTo("greyfleet", { key: "ins_alert",       value: true,  label: "Insurance Alert — No Cover or Expired", color: C.red   })} />
          <KPICard label="MOT / Tax Alert"     value={motTaxAlert} icon={Truck}         color={motTaxAlert > 0 ? C.red : C.green} sub="Expired or expiring soon" loading={vesLoading} onClick={() => navigateTo("greyfleet", { key: "ves_alert",       value: true,  label: "MOT / Tax Alerts",                      color: motTaxAlert > 0 ? C.red : C.green })} />
        </>}
      </div>
      <div style={{ display: "flex", gap: 14, marginBottom: 20, flexWrap: "wrap" }}>
        <CompliancePanel title="Grey Fleet Mandate Completion"   score={gfMandScore} loading={loading} segments={mandSegments} />
        <CompliancePanel title="Insurance & MOT/Tax Compliance" score={gfCompScore} loading={loading} segments={compSegments} />
      </div>
    </div>
  );
};

// ── DASHBOARD (tabbed) ────────────────────────────────────────────────────────
const DashboardPage = ({ navigateTo, drivers, loading, greyFleetData, sessionToken, company }) => {
  const [tab, setTab] = useState("licence");
  const tabs = [
    { id: "licence",   label: "Licence Checks", icon: ShieldCheck },
    { id: "greyfleet", label: "Grey Fleet",      icon: Car },
  ];
  return (
    <div>
      <style>{shimmer}</style>
      <div style={{ marginBottom: 20 }}>
        <div style={{ color: C.text, fontWeight: 700, fontSize: 22, marginBottom: 4 }}>Dashboard</div>
        <div style={{ color: C.textMuted, fontSize: 14 }}>{company?.name} · Live data</div>
      </div>
      <div style={{ display: "flex", gap: 2, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 4, marginBottom: 22, width: "fit-content" }}>
        {tabs.map(t => {
          const IconComp = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} style={{ display: "flex", alignItems: "center", gap: 7, padding: "8px 18px", borderRadius: 8, border: "none", fontWeight: 600, fontSize: 13, cursor: "pointer", transition: "all 0.18s", background: tab === t.id ? C.accent : "transparent", color: tab === t.id ? "#fff" : C.textMuted, boxShadow: tab === t.id ? `0 2px 10px ${C.accent}40` : "none", fontFamily: "inherit" }}>
              <IconComp size={15} strokeWidth={tab === t.id ? 2.2 : 1.75} />
              {t.label}
            </button>
          );
        })}
      </div>
      {tab === "licence"
        ? <LicenceDashboard navigateTo={navigateTo} drivers={drivers} loading={loading} greyFleetData={greyFleetData} />
        : <GreyFleetDashboard navigateTo={navigateTo} drivers={drivers} loading={loading} greyFleetData={greyFleetData} sessionToken={sessionToken} />}
    </div>
  );
};

// ── DRIVERS ───────────────────────────────────────────────────────────────────
const DriversPage = ({ filter, clearFilter, navigateTo, drivers, loading, onRefresh, showToast, onToggleGreyFleet, company, sessionToken, greyFleetData }) => {
  const [showAdd, setShowAdd]       = useState(false);
  const [saving, setSaving]         = useState(false);
  const [csvPreview, setCsvPreview] = useState(null);
  const [csvImporting, setCsvImporting] = useState(false);
  const csvInputRef = useRef(null);
  const [mandateDriver, setMandate] = useState(null);
  const [confirmDelete, setConfirm]         = useState(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState("");
  const [deleting, setDeleting]             = useState(false);
  const [newDriver, setNewDriver]   = useState({ name: "", email: "", is_grey_fleet: false });
  const [openVehicleModal, setOpenVehicleModal] = useState(null);
  const [driverSearch, setDriverSearch] = useState("");
  const [driverPage,   setDriverPage]   = useState(1);
  const DRIVERS_PER_PAGE = 100;

  const displayed = filter ? drivers.filter(d => d[filter.key] === filter.value) : drivers;
  const filteredDrivers = driverSearch
    ? displayed.filter(d => [d.name, d.email, d.mandate_status, d.licence_status].some(f => (f || "").toLowerCase().includes(driverSearch.toLowerCase())))
    : displayed;
  const driverPageCount  = Math.ceil(filteredDrivers.length / DRIVERS_PER_PAGE);
  const pagedDrivers     = filteredDrivers.slice((driverPage - 1) * DRIVERS_PER_PAGE, driverPage * DRIVERS_PER_PAGE);

  const addDriver = async () => {
    if (!newDriver.name || !newDriver.email) return;
    if (drivers.some(d => (d.email || "").toLowerCase() === newDriver.email.trim().toLowerCase())) {
      showToast("A driver with this email already exists", C.red);
      return;
    }
    if (company.driver_seats > 0 && drivers.length >= company.driver_seats) {
      showToast(`Seat limit reached — you have ${drivers.length} of ${company.driver_seats} seats used. To add more drivers, please upgrade your plan in Settings.`, C.red);
      return;
    }
    setSaving(true);

    try {
      const inserted = await db.insert("drivers", { name: newDriver.name, email: newDriver.email, mandate_status: "Pending", licence_status: "Unchecked", points: 0, risk: "Low", endorsements: "—", is_grey_fleet: newDriver.is_grey_fleet, company_id: company.id }, sessionToken.current);
      const newDriverRecord = Array.isArray(inserted) ? inserted[0] : inserted;
      if (newDriverRecord?.id) {
        const mandateEndpoint = company.product_tier === "lite" ? "send-lite-mandate-email" : "send-mandate-email";
        fetch(`${SUPABASE_URL}/functions/v1/${mandateEndpoint}`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${SUPABASE_KEY}`, "apikey": SUPABASE_KEY },
          body: JSON.stringify({ driver_id: newDriverRecord.id, driver_name: newDriver.name, driver_email: newDriver.email, company_name: company.name, product_tier: company.product_tier, is_grey_fleet: newDriver.is_grey_fleet }),
        }).catch(err => console.warn("Mandate email failed silently:", err));
      }
      setShowAdd(false); setNewDriver({ name: "", email: "", is_grey_fleet: false });
      onRefresh(); showToast("Driver added — mandate email sent", C.green);
    } catch { showToast("Failed to add driver", C.red); }
    setSaving(false);
  };

  const deleteDriver = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    const driverId   = confirmDelete.id;
    const driverName = confirmDelete.name;
    const authHeader = { apikey: SUPABASE_KEY, Authorization: `Bearer ${sessionToken.current || SUPABASE_KEY}` };
    try {
      await fetch(`${SUPABASE_URL}/rest/v1/licence_summaries?driver_id=eq.${driverId}`, { method: "DELETE", headers: authHeader });
      await fetch(`${SUPABASE_URL}/rest/v1/grey_fleet_checks?driver_id=eq.${driverId}`, { method: "DELETE", headers: authHeader });
      await db.delete("drivers", driverId, sessionToken.current);
      try {
        await fetch(`${SUPABASE_URL}/functions/v1/delete-portal-account`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey: SUPABASE_KEY,
            Authorization: `Bearer ${sessionToken?.current || SUPABASE_KEY}`,
          },
          body: JSON.stringify({ driver_id: driverId }),
        });
      } catch (err) {
        console.error("delete-portal-account error:", err);
      }
      await db.insert("removal_requests", {
        driver_id:       driverId,
        company_id:      company?.id   || null,
        acknowledged_by: company?.name || null,
        acknowledged_at: new Date().toISOString(),
        deleted_at:      new Date().toISOString(),
      }, sessionToken.current);
      setConfirm(null);
      setDeleteConfirmName("");
      onRefresh();
      showToast(`${driverName} permanently deleted`, C.green);
    } catch { showToast("Failed to delete driver", C.red); }
    setDeleting(false);
  };

  const downloadTemplate = () => {
    const content = "first_name,last_name,email,grey_fleet\nJohn,Smith,john.smith@example.co.uk,Y";
    const blob = new Blob([content], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "drivesafe_driver_template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const parseCSV = (text) => {
    const lines = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n").filter(l => l.trim());
    if (lines.length < 2) return { valid: [], invalid: [{ line: 1, reasons: ["File is empty or has no data rows"] }] };
    const headers = lines[0].split(",").map(h => h.trim().toLowerCase());
    const fi = headers.indexOf("first_name"), li = headers.indexOf("last_name");
    const ei = headers.indexOf("email"),      gi = headers.indexOf("grey_fleet");
    const valid = [], invalid = [];
    for (let i = 1; i < lines.length; i++) {
      const cols = lines[i].split(",").map(c => c.trim());
      const firstName  = fi >= 0 ? cols[fi]  || "" : "";
      const lastName   = li >= 0 ? cols[li]  || "" : "";
      const email      = ei >= 0 ? cols[ei]  || "" : "";
      const greyFleet  = gi >= 0 ? (cols[gi] || "").toUpperCase() : "N";
      const reasons = [];
      if (!firstName)  reasons.push("first_name required");
      if (!lastName)   reasons.push("last_name required");
      if (!email)      reasons.push("email required");
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) reasons.push("invalid email format");
      if (gi >= 0 && greyFleet !== "Y" && greyFleet !== "N") reasons.push("grey_fleet must be Y or N");
      if (reasons.length) invalid.push({ line: i + 1, firstName, lastName, email, greyFleet, reasons });
      else valid.push({ firstName, lastName, email, is_grey_fleet: greyFleet === "Y" });
    }
    return { valid, invalid };
  };

  const handleCSVFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const parsed = parseCSV(e.target.result);
      const existingEmails = new Set(drivers.map(d => (d.email || "").toLowerCase()));
      const dupes   = parsed.valid.filter(r =>  existingEmails.has(r.email.toLowerCase()));
      const clean   = parsed.valid.filter(r => !existingEmails.has(r.email.toLowerCase()));
      const dupesAsInvalid = dupes.map(r => ({
        line: "—", firstName: r.firstName, lastName: r.lastName, email: r.email,
        reasons: ["already exists in this company"],
      }));
      setCsvPreview({ valid: clean, invalid: [...parsed.invalid, ...dupesAsInvalid] });
    };
    reader.readAsText(file);
  };

  const importCSV = async () => {
    if (!csvPreview?.valid?.length) return;
    if (company.driver_seats > 0 && drivers.length >= company.driver_seats) {
      showToast(`Seat limit reached — you have ${drivers.length} of ${company.driver_seats} seats used. To add more drivers, please upgrade your plan in Settings.`, C.red);
      return;
    }
    setCsvImporting(true);
    const existingEmails = new Set(drivers.map(d => (d.email || "").toLowerCase()));
    const mandateEndpoint = company.product_tier === "lite" ? "send-lite-mandate-email" : "send-mandate-email";
    let added = 0, skipped = 0, limitSkipped = 0;
    for (const row of csvPreview.valid) {
      if (existingEmails.has(row.email.toLowerCase())) { skipped++; continue; }
      if (company.driver_seats > 0 && drivers.length + added >= company.driver_seats) { limitSkipped++; continue; }
      try {
        const name = `${row.firstName} ${row.lastName}`;
        const inserted = await db.insert("drivers", { name, email: row.email, mandate_status: "Pending", licence_status: "Unchecked", points: 0, risk: "Low", endorsements: "—", is_grey_fleet: row.is_grey_fleet, company_id: company.id }, sessionToken.current);
        const rec = Array.isArray(inserted) ? inserted[0] : inserted;
        if (rec?.id) {
          fetch(`${SUPABASE_URL}/functions/v1/${mandateEndpoint}`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${SUPABASE_KEY}`, apikey: SUPABASE_KEY },
            body: JSON.stringify({ driver_id: rec.id, driver_name: name, driver_email: row.email, company_name: company.name, product_tier: company.product_tier, is_grey_fleet: row.is_grey_fleet }),
          }).catch(err => console.warn("Mandate email failed silently:", err));
        }
        added++;
      } catch { /* skip failed rows */ }
    }
    setCsvImporting(false);
    setCsvPreview(null);
    let msg = `${added} driver${added !== 1 ? "s" : ""} added`;
    if (skipped) msg += `, ${skipped} skipped (already exist)`;
    if (limitSkipped) msg += `, ${limitSkipped} skipped (seat limit reached)`;
    showToast(msg, limitSkipped ? C.amber : C.green);
    onRefresh();
  };

  const sendGreyFleetMandate = async (driver) => {
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/send-lite-mandate-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${SUPABASE_KEY}`, "apikey": SUPABASE_KEY },
        body: JSON.stringify({ driver_id: driver.id, driver_name: driver.name, driver_email: driver.email, company_name: company?.name, is_grey_fleet: true }),
      });
      if (!res.ok) throw new Error();
      showToast(`Mandate sent to ${driver.name}`, C.green);
    } catch { showToast("Failed to send mandate", C.red); }
  };

  return (
    <div>
      <style>{shimmer}</style>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ color: C.text, fontWeight: 700, fontSize: 22, marginBottom: 4 }}>Drivers</div>
          <div style={{ color: C.textMuted, fontSize: 14 }}>{displayed.length} of {drivers.length} drivers</div>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <Btn variant="secondary" onClick={downloadTemplate}><Download size={14} /> Download Template</Btn>
          <Btn variant="secondary" onClick={() => csvInputRef.current?.click()}><Upload size={14} /> Upload CSV</Btn>
          <Btn onClick={() => setShowAdd(true)}><Plus size={14} /> Add Driver</Btn>
        </div>
        <input ref={csvInputRef} type="file" accept=".csv" style={{ display: "none" }} onChange={e => { handleCSVFile(e.target.files[0]); e.target.value = ""; }} />
      </div>
      {filter && <FilterBanner label={filter.label} color={filter.color} count={displayed.length} onClear={clearFilter} />}
      <div style={{ position: "relative", marginBottom: 12 }}>
        <input
          value={driverSearch}
          onChange={e => { setDriverSearch(e.target.value); setDriverPage(1); }}
          placeholder="Search drivers..."
          style={{ width: "100%", background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: "9px 36px 9px 12px", color: C.text, fontSize: 14, outline: "none", boxSizing: "border-box" }}
        />
        {driverSearch && (
          <button onClick={() => { setDriverSearch(""); setDriverPage(1); }} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: C.textMuted, cursor: "pointer", display: "flex", padding: 0 }}><X size={15} color={C.textMuted} /></button>
        )}
      </div>
      <Card>
        <TableWrap>
          <thead style={{ position: "sticky", top: 0, zIndex: 2 }}><tr>{["Name","Email","Mandate Status","Last Check","Re-check Due","Points","Risk","Mandate","Actions"].map(h => <Th key={h} c={h} />)}</tr></thead>
          <tbody>
            {loading ? Array(5).fill(0).map((_, i) => <SkeletonRow key={i} cols={9} />) :
              pagedDrivers.map(d => {
                const recheckDate  = d.recheck_due ? new Date(d.recheck_due) : null;
                const recheckDiff  = recheckDate ? Math.floor((recheckDate - new Date()) / (1000 * 60 * 60 * 24)) : null;
                const recheckColor = recheckDiff === null ? C.textMuted : recheckDiff < 0 ? C.red : recheckDiff <= 30 ? C.amber : C.textSub;
                return (
                <tr key={d.id}>
                  <Td><span style={{ color: C.text, fontWeight: 500 }}>{d.name}</span></Td>
                  <Td>{d.email}</Td>
                  <Td><RiskBadge level={d.mandate_status} /></Td>
                  <Td>{fmtDate(d.last_check)}</Td>
                  <Td><span style={{ color: recheckColor, fontWeight: recheckDiff !== null && recheckDiff <= 30 ? 600 : 400 }}>{d.recheck_due ? fmtDate(d.recheck_due) : "—"}</span></Td>
                  <Td>{d.points}</Td>
                  <Td><RiskBadge level={d.risk} /></Td>
                  <Td>
                    <Btn small variant="secondary" onClick={() => setMandate(d)}><Eye size={12} /> Mandate</Btn>
                  </Td>
                  <Td>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {(() => {
                        const vehicles = greyFleetData[d.id];
                        const gf = vehicles?.find(v => v.isPrimary) || vehicles?.[0] || null;
                        if (!d.is_grey_fleet) return (
                          <Btn small variant="ghost" onClick={() => { onToggleGreyFleet(d.id, true); sendGreyFleetMandate(d); }} style={{ fontSize: 11, opacity: 0.6, minWidth: 110 }}><Car size={12} /> Add to GF</Btn>
                        );
                        if (!gf?.reg) return (
                          <Btn small disabled style={{ fontSize: 11, background: `${C.amber}18`, border: `1px solid ${C.amber}44`, color: C.amber, opacity: 1, minWidth: 110 }}><Clock size={12} /> Pending</Btn>
                        );
                        const outcome = gf.aiResult?.outcome;
                        const col = outcome === "Green" ? C.green : outcome === "Red" ? C.red : C.amber;
                        const extraCount = (vehicles?.length || 1) - 1;
                        return (
                          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            <button onClick={() => setOpenVehicleModal({ ...d, gf })}
                              style={{ background: `${col}15`, border: `1px solid ${col}44`, borderRadius: 6, padding: "3px 8px", color: col, fontWeight: 700, fontFamily: "'Courier New', monospace", letterSpacing: 2, fontSize: 12, cursor: "pointer", minWidth: 110 }}>
                              {gf.reg}
                            </button>
                            {extraCount > 0 && (
                              <span
                                onClick={e => { e.stopPropagation(); navigateTo("vehicles", { key: "driver_id", value: d.id, label: `${d.name}'s vehicles`, color: C.accent }); }}
                                style={{ fontSize: 10, color: C.accent, background: C.surfaceAlt, borderRadius: 4, padding: "1px 5px", cursor: "pointer", textDecoration: "underline" }}
                              >+{extraCount}</span>
                            )}
                          </div>
                        );
                      })()}
                      <Btn small variant="danger" onClick={() => setConfirm(d)}><Trash2 size={12} /></Btn>
                    </div>
                  </Td>
                </tr>
              ); })}
          </tbody>
        </TableWrap>
      </Card>
      {driverPageCount > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 14, flexWrap: "wrap", gap: 10 }}>
          <span style={{ color: C.textMuted, fontSize: 13 }}>
            Showing {(driverPage - 1) * DRIVERS_PER_PAGE + 1}–{Math.min(driverPage * DRIVERS_PER_PAGE, filteredDrivers.length)} of {filteredDrivers.length} drivers
          </span>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <Btn small variant="secondary" disabled={driverPage === 1} onClick={() => setDriverPage(p => p - 1)}>← Previous</Btn>
            <span style={{ color: C.textMuted, fontSize: 13 }}>Page {driverPage} of {driverPageCount}</span>
            <Btn small variant="secondary" disabled={driverPage === driverPageCount} onClick={() => setDriverPage(p => p + 1)}>Next →</Btn>
          </div>
        </div>
      )}

      {mandateDriver && <MandateViewer driver={mandateDriver} onClose={() => setMandate(null)} company={company} sessionToken={sessionToken} showToast={showToast} />}

      {confirmDelete && (
        <Modal title="Permanently Delete Driver" onClose={() => { setConfirm(null); setDeleteConfirmName(""); }}>
          <div style={{ background: C.redBg, border: `1px solid ${C.red}33`, borderRadius: 8, padding: 12, marginBottom: 16, display: "flex", alignItems: "flex-start", gap: 8 }}>
            <AlertTriangle size={15} color={C.red} style={{ flexShrink: 0, marginTop: 1 }} />
            <div style={{ color: C.red, fontSize: 13, lineHeight: 1.5 }}>
              <strong>Permanent data erasure (GDPR)</strong><br />
              This action cannot be undone. The following data will be deleted immediately:
            </div>
          </div>
          <ul style={{ margin: "0 0 16px 0", paddingLeft: 20, color: C.textSub, fontSize: 13, lineHeight: 1.8 }}>
            <li>Driver record for <strong style={{ color: C.text }}>{confirmDelete.name}</strong></li>
            <li>All licence check history</li>
            <li>Any grey fleet vehicle records</li>
          </ul>
          <div style={{ marginBottom: 18 }}>
            <label style={{ display: "block", color: C.textSub, fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
              Type the driver's full name to confirm
            </label>
            <input
              value={deleteConfirmName}
              onChange={e => setDeleteConfirmName(e.target.value)}
              placeholder={confirmDelete.name}
              style={{ width: "100%", background: C.surfaceAlt, border: `1px solid ${deleteConfirmName === confirmDelete.name ? C.red : C.border}`, borderRadius: 8, padding: "10px 14px", color: C.text, fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "inherit" }}
            />
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <Btn variant="secondary" onClick={() => { setConfirm(null); setDeleteConfirmName(""); }} style={{ flex: 1 }}>Cancel</Btn>
            <Btn variant="danger" onClick={deleteDriver} disabled={deleting || deleteConfirmName !== confirmDelete.name} style={{ flex: 1 }}>{deleting ? "Deleting…" : "Permanently Delete"}</Btn>
          </div>
        </Modal>
      )}

      {showAdd && (
        <Modal title="Add Driver" onClose={() => setShowAdd(false)}>
          <Input label="Full Name" value={newDriver.name} onChange={e => setNewDriver(f => ({ ...f, name: e.target.value }))} placeholder="James Thornton" />
          <Input label="Email Address" value={newDriver.email} onChange={e => setNewDriver(f => ({ ...f, email: e.target.value }))} placeholder="james@company.co.uk" />
          <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: 12, marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
            <Mail size={15} color={C.textMuted} /><div style={{ color: C.textSub, fontSize: 13 }}>A mandate request email will be sent to this driver automatically.</div>
          </div>
          <div style={{ background: C.surfaceAlt, border: `1px solid ${newDriver.is_grey_fleet ? C.accent : C.border}`, borderRadius: 10, padding: "12px 14px", marginBottom: 16, transition: "border-color 0.15s" }}>
            <label style={{ display: "flex", gap: 12, cursor: "pointer", alignItems: "flex-start" }}>
              <input type="checkbox" checked={newDriver.is_grey_fleet} onChange={e => setNewDriver(f => ({ ...f, is_grey_fleet: e.target.checked }))} style={{ marginTop: 2, accentColor: C.accent, width: 16, height: 16, flexShrink: 0 }} />
              <div>
                <div style={{ color: C.text, fontSize: 13, fontWeight: 600, marginBottom: 2, display: "flex", alignItems: "center", gap: 6 }}><Car size={13} color={C.textSub} /> Grey Fleet driver</div>
                <div style={{ color: C.textMuted, fontSize: 12, lineHeight: 1.5 }}>This driver uses their personal vehicle for work. They'll appear on the Grey Fleet page for insurance, MOT and tax checks.</div>
              </div>
            </label>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <Btn variant="secondary" onClick={() => setShowAdd(false)} style={{ flex: 1 }}>Cancel</Btn>
            <Btn onClick={addDriver} disabled={saving || !newDriver.name || !newDriver.email} style={{ flex: 1 }}>{saving ? "Adding…" : "Add Driver"}</Btn>
          </div>
        </Modal>
      )}

      {csvPreview && (
        <Modal title="CSV Import Preview" onClose={() => setCsvPreview(null)}>
          {csvPreview.valid.length > 0 && (
            <>
              <div style={{ color: C.text, fontWeight: 600, fontSize: 14, marginBottom: 8 }}>
                {csvPreview.valid.length} row{csvPreview.valid.length !== 1 ? "s" : ""} ready to import
              </div>
              <div style={{ overflowX: "auto", marginBottom: 16 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: C.surfaceAlt }}>
                      {["First Name", "Last Name", "Email", "Grey Fleet"].map(h => (
                        <th key={h} style={{ padding: "8px 12px", textAlign: "left", color: C.textMuted, fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, borderBottom: `1px solid ${C.border}` }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {csvPreview.valid.map((row, i) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                        <td style={{ padding: "8px 12px", color: C.text }}>{row.firstName}</td>
                        <td style={{ padding: "8px 12px", color: C.text }}>{row.lastName}</td>
                        <td style={{ padding: "8px 12px", color: C.text }}>{row.email}</td>
                        <td style={{ padding: "8px 12px", color: row.is_grey_fleet ? C.accent : C.textMuted }}>{row.is_grey_fleet ? "Yes" : "No"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {csvPreview.invalid.length > 0 && (
            <>
              <div style={{ color: C.amber, fontWeight: 600, fontSize: 13, marginBottom: 6 }}>
                {csvPreview.invalid.length} row{csvPreview.invalid.length !== 1 ? "s" : ""} skipped
              </div>
              <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px", marginBottom: 16, maxHeight: 140, overflowY: "auto" }}>
                {csvPreview.invalid.map((row, i) => (
                  <div key={i} style={{ color: C.textMuted, fontSize: 12, marginBottom: 4 }}>
                    Row {row.line}{row.email ? ` (${row.email})` : ""}: {row.reasons.join(", ")}
                  </div>
                ))}
              </div>
            </>
          )}
          {csvPreview.valid.length === 0 && (
            <div style={{ color: C.textMuted, fontSize: 14, textAlign: "center", padding: "16px 0" }}>No valid rows found in this file.</div>
          )}
          <div style={{ display: "flex", gap: 10 }}>
            <Btn variant="secondary" onClick={() => setCsvPreview(null)} style={{ flex: 1 }}>Cancel</Btn>
            <Btn onClick={importCSV} disabled={csvImporting || csvPreview.valid.length === 0} style={{ flex: 1 }}>
              {csvImporting ? "Importing…" : `Import ${csvPreview.valid.length} Driver${csvPreview.valid.length !== 1 ? "s" : ""}`}
            </Btn>
          </div>
        </Modal>
      )}

      {openVehicleModal && (() => {
        const d = openVehicleModal;
        return (
          <Modal title="Vehicle Details" onClose={() => setOpenVehicleModal(null)}>
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                <div>
                  <div style={{ color: C.text, fontWeight: 700, fontSize: 16 }}>{d.name}</div>
                  {d.gf?.reg && <span style={{ color: C.text, fontWeight: 700, fontFamily: "'Courier New', monospace", letterSpacing: 2, background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 6, padding: "3px 8px", fontSize: 13, display: "inline-block", marginTop: 6 }}>{d.gf.reg}</span>}
                </div>
                <GreyFleetBadge outcome={d.gf?.aiResult?.outcome} fmResolution={d.gf?.fmResolution} policyExpiry={d.gf?.policyExpiry} />
              </div>
              {[
                ["Policy type",   d.gf?.aiResult?.clause || "—",  d.gf?.aiResult?.outcome === "Red" ? C.red : C.textSub],
                ["Policy expiry", fmtDate(d.gf?.policyExpiry),    d.gf?.policyExpiry ? vesStatus(d.gf.policyExpiry).color : C.textSub],
                ["Last checked",  fmtDate(d.gf?.checkedAt),       C.textSub],
                ...(d.ves?.mot_expiry ? [["MOT Expiry", fmtDate(d.ves.mot_expiry), vesStatus(d.ves.mot_expiry).color]] : []),
                ...(d.ves?.tax_expiry ? [["Tax Expiry", fmtDate(d.ves.tax_expiry), vesStatus(d.ves.tax_expiry).color]] : []),
              ].map(([label, value, valueColor = C.textSub]) => (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "10px 0", borderBottom: `1px solid ${C.border}22` }}>
                  <span style={{ color: C.textMuted, fontSize: 13 }}>{label}</span>
                  <span style={{ color: valueColor, fontSize: 13, textAlign: "right", maxWidth: "60%" }}>{value}</span>
                </div>
              ))}
              {d.gf?.aiResult?.summary && (
                <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: 12, marginTop: 14 }}>
                  <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Notes</div>
                  <div style={{ color: C.textSub, fontSize: 13, lineHeight: 1.6 }}>{d.gf.aiResult.summary}</div>
                </div>
              )}
            </div>
            <Btn variant="secondary" onClick={() => setOpenVehicleModal(null)} style={{ width: "100%", justifyContent: "center" }}>Close</Btn>
          </Modal>
        );
      })()}
    </div>
  );
};

// ── MANDATES ──────────────────────────────────────────────────────────────────
const MandatesPage = ({ filter, clearFilter, drivers, loading, showToast, company }) => {
  const pending   = drivers.filter(d => d.mandate_status === "Pending").length;
  const expired   = drivers.filter(d => d.mandate_status === "Expired").length;
  const displayed = filter ? drivers.filter(d => d[filter.key] === filter.value) : drivers;
  const [sending, setSending] = useState(null);

  const sendMandate = async (driver) => {
    setSending(driver.id);
    try {
      const endpoint = company?.product_tier === "premium" ? "send-mandate-email" : "send-lite-mandate-email";
      const res = await fetch(`${SUPABASE_URL}/functions/v1/${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${SUPABASE_KEY}`, "apikey": SUPABASE_KEY },
        body: JSON.stringify({ driver_id: driver.id, driver_name: driver.name, driver_email: driver.email, company_name: company?.name, product_tier: company?.product_tier, is_grey_fleet: driver.is_grey_fleet }),
      });
      if (res.ok) {
        showToast(`Mandate sent to ${driver.name}`, C.green);
      } else {
        showToast(`Failed to send mandate to ${driver.name}`, C.red);
      }
    } catch {
      showToast(`Failed to send mandate to ${driver.name}`, C.red);
    }
    setSending(null);
  };

  return (
    <div>
      <style>{shimmer}</style>
      <div style={{ marginBottom: 24 }}>
        <div style={{ color: C.text, fontWeight: 700, fontSize: 22, marginBottom: 4 }}>Mandates</div>
        <div style={{ color: C.textMuted, fontSize: 14 }}>Driver consent for DVLA licence checking</div>
      </div>
      {filter && <FilterBanner label={filter.label} color={filter.color} count={displayed.length} onClear={clearFilter} />}
      {!filter && (pending + expired > 0) && (
        <div style={{ background: C.amberBg, border: `1px solid ${C.amber}33`, borderRadius: 10, padding: 14, marginBottom: 20, display: "flex", alignItems: "center", gap: 12 }}>
          <AlertTriangle size={18} color={C.amber} />
          <div>
            <div style={{ color: C.amber, fontWeight: 600, fontSize: 14 }}>{pending + expired} drivers require action</div>
            <div style={{ color: C.textMuted, fontSize: 13 }}>{pending} pending · {expired} expired</div>
          </div>
        </div>
      )}
      <Card>
        <TableWrap>
          <thead><tr>{["Driver Name","Email","Mandate Status","Expiry","Action"].map(h => <Th key={h} c={h} />)}</tr></thead>
          <tbody>
            {loading ? Array(5).fill(0).map((_, i) => <SkeletonRow key={i} cols={5} />) :
              displayed.map(d => (
                <tr key={d.id}>
                  <Td><span style={{ color: C.text, fontWeight: 500 }}>{d.name}</span></Td>
                  <Td>{d.email}</Td>
                  <Td><RiskBadge level={d.mandate_status} /></Td>
                  <Td>{fmtDate(d.mandate_expiry)}</Td>
                  <Td>{d.mandate_status === "Completed"
                    ? <span style={{ color: C.textMuted, fontSize: 12 }}>No action needed</span>
                    : <Btn small variant={d.mandate_status === "Expired" ? "danger" : "secondary"} disabled={sending === d.id} onClick={() => sendMandate(d)}>
                        <Mail size={12} />{sending === d.id ? "Sending…" : d.mandate_status === "Expired" ? "Renew" : "Send Request"}
                      </Btn>}
                  </Td>
                </tr>
              ))}
          </tbody>
        </TableWrap>
      </Card>
    </div>
  );
};

const FM_RESOLUTIONS = [
  "Accepted — driver has obtained correct cover",
  "Escalated — referred to HR or management",
  "Exempted — driver approved to continue temporarily",
  "Rejected — driver removed from grey fleet",
];

// ── COMPLIANCE ────────────────────────────────────────────────────────────────
const VehiclesPage = ({ drivers, loading, greyFleetData, company, showToast, onRefresh, sessionToken, filter, clearFilter }) => {
  const [detailModal, setDetailModal] = useState(null);
  const [fmEditMode, setFmEditMode]       = useState(false);
  const [fmSelectedRes, setFmSelectedRes] = useState("");
  const [fmNoteText, setFmNoteText]       = useState("");
  const [fmSaving, setFmSaving]           = useState(false);
  const [vesDataMap, setVesDataMap]       = useState({});

  useEffect(() => {
    const regs = Object.values(greyFleetData)
      .flatMap(vehicles => (Array.isArray(vehicles) ? vehicles : [vehicles]))
      .map(gf => gf?.reg?.replace(/\s+/g, "").toUpperCase())
      .filter(Boolean);
    if (regs.length === 0) { setVesDataMap({}); return; }
    const token = sessionToken?.current || SUPABASE_KEY;
    fetch(`${SUPABASE_URL}/rest/v1/vehicle_ves_data?registration=in.(${regs.join(",")})&select=*`, {
      headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(rows => {
        const map = {};
        rows.forEach(row => { map[row.registration] = row; });
        setVesDataMap(map);
      })
      .catch(err => console.error("VES data fetch error:", err));
  }, [greyFleetData, sessionToken]);

  const closeModal = () => { setDetailModal(null); setFmEditMode(false); setFmSelectedRes(""); setFmNoteText(""); };
  const openModal  = (d)  => { setDetailModal(d);  setFmEditMode(false); setFmSelectedRes(""); setFmNoteText(""); };

  const saveFmReview = async () => {
    if (!fmSelectedRes || !detailModal) return;
    setFmSaving(true);
    try {
      const token = sessionToken?.current || SUPABASE_KEY;
      const newOutcome = fmSelectedRes.startsWith("Accepted") ? "Green" : "Amber";
      const reg = detailModal.gf?.reg?.replace(/\s+/g, "").toUpperCase();
      const res = await fetch(`${SUPABASE_URL}/rest/v1/grey_fleet_checks?driver_id=eq.${detailModal.id}&reg=eq.${reg}`, {
        method: "PATCH",
        headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${token}`, "Content-Type": "application/json", Prefer: "return=minimal" },
        body: JSON.stringify({
          fm_resolution:  fmSelectedRes,
          fm_notes:       fmNoteText,
          fm_reviewed_at: new Date().toISOString(),
          fm_reviewed_by: company?.name || null,
          ai_outcome:     newOutcome,
        }),
      });
      const resText = await res.text();
      console.log("[saveFmReview] status:", res.status, "body:", resText, "driver_id:", detailModal.id, "reg:", reg);
      if (!res.ok) throw new Error();
      showToast("Review saved", C.green);
      // Optimistically update the open modal so read-only view and badge update immediately
      setDetailModal(prev => ({ ...prev, gf: { ...prev.gf, fmResolution: fmSelectedRes, fmNotes: fmNoteText, fmReviewedAt: new Date().toISOString(), fmReviewedBy: company?.name || null, aiResult: { ...prev.gf?.aiResult, outcome: newOutcome } } }));
      setFmEditMode(false);
      onRefresh();
    } catch { showToast("Failed to save review", C.red); }
    setFmSaving(false);
  };

  const greyDrivers = drivers.filter(d => d.is_grey_fleet);
  const rows = greyDrivers.flatMap(d => {
    const vehicles = greyFleetData[d.id] || [];
    if (vehicles.length === 0) return [{ ...d, gf: null, ves: null }];
    return vehicles.map(gf => {
      const reg = gf.reg?.replace(/\s+/g, "").toUpperCase();
      const ves = reg ? vesDataMap[reg] : null;
      return { ...d, gf, ves };
    });
  });
  const [vehicleSearch, setVehicleSearch] = useState("");
  const [vehiclePage,   setVehiclePage]   = useState(1);
  const VEHICLES_PER_PAGE = 100;
  const driverFiltered = filter?.key === "driver_id" ? rows.filter(d => d.id === filter.value) : rows;
  const filteredVehicles = vehicleSearch
    ? driverFiltered.filter(d => [d.name, d.email, d.gf?.reg, d.gf?.aiResult?.outcome].some(f => (f || "").toLowerCase().includes(vehicleSearch.toLowerCase())))
    : driverFiltered;
  const vehiclePageCount = Math.ceil(filteredVehicles.length / VEHICLES_PER_PAGE);
  const pagedVehicles    = filteredVehicles.slice((vehiclePage - 1) * VEHICLES_PER_PAGE, vehiclePage * VEHICLES_PER_PAGE);

  return (
    <div>
      <style>{shimmer}</style>
      <div style={{ marginBottom: 24 }}>
        <div style={{ color: C.text, fontWeight: 700, fontSize: 22, marginBottom: 4 }}>Vehicles</div>
        <div style={{ color: C.textMuted, fontSize: 14 }}>{rows.length} grey fleet vehicle{rows.length !== 1 ? "s" : ""}</div>
      </div>
      {filter?.key === "driver_id" && <FilterBanner label={filter.label} color={filter.color} count={filteredVehicles.length} onClear={clearFilter} />}
      <div style={{ position: "relative", marginBottom: 12 }}>
        <input
          value={vehicleSearch}
          onChange={e => { setVehicleSearch(e.target.value); setVehiclePage(1); }}
          placeholder="Search vehicles..."
          style={{ width: "100%", background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: "9px 36px 9px 12px", color: C.text, fontSize: 14, outline: "none", boxSizing: "border-box" }}
        />
        {vehicleSearch && (
          <button onClick={() => { setVehicleSearch(""); setVehiclePage(1); }} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: C.textMuted, cursor: "pointer", display: "flex", padding: 0 }}><X size={15} color={C.textMuted} /></button>
        )}
      </div>
      <Card>
        <TableWrap>
          <thead style={{ position: "sticky", top: 0, zIndex: 2 }}>
            <tr>
              {["Registration","Driver","Insurance Check","Last Check","Policy Expiry","MOT Expiry","Tax Expiry","Fuel Type","Engine Size"].map(h => <Th key={h} c={h} />)}
            </tr>
          </thead>
          <tbody>
            {loading ? Array(5).fill(0).map((_, i) => <SkeletonRow key={i} cols={9} />) :
              pagedVehicles.length === 0 ? (
                <tr><td colSpan={9} style={{ padding: "32px 14px", textAlign: "center", color: C.textMuted, fontSize: 14 }}>{vehicleSearch ? "No vehicles match your search." : "No grey fleet vehicles. Mark drivers as Grey Fleet on the Drivers page."}</td></tr>
              ) :
              pagedVehicles.map(d => {
                const motS = vesStatus(d.ves?.mot_expiry);
                const taxS = vesStatus(d.ves?.tax_expiry);
                return (
                  <tr key={d.id} style={{ cursor: "pointer" }}
                    onClick={() => openModal(d)}
                    onMouseEnter={e => e.currentTarget.style.background = C.surfaceAlt}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <Td>
                      {d.gf?.reg
                        ? <span style={{ color: C.text, fontWeight: 700, fontFamily: "'Courier New', monospace", letterSpacing: 2, background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 6, padding: "3px 8px", fontSize: 13 }}>{d.gf.reg}</span>
                        : "—"}
                    </Td>
                    <Td>
                      <div style={{ color: C.text, fontWeight: 600, fontSize: 14 }}>{d.name}</div>
                      <div style={{ color: C.textMuted, fontSize: 12, marginTop: 1 }}>{d.email}</div>
                    </Td>
                    <Td><GreyFleetBadge outcome={d.gf?.aiResult?.outcome} fmResolution={d.gf?.fmResolution} policyExpiry={d.gf?.policyExpiry} /></Td>
                    <Td>{fmtDate(d.gf?.checkedAt)}</Td>
                    <Td>
                      {d.gf?.policyExpiry
                        ? <span style={{ color: vesStatus(d.gf.policyExpiry).color, fontWeight: 600 }}>{fmtDate(d.gf.policyExpiry)}</span>
                        : <span style={{ color: C.textMuted }}>—</span>}
                    </Td>
                    <Td>
                      {d.ves?.mot_expiry
                        ? <span style={{ color: motS.color, fontWeight: 600, fontSize: 13 }}>{fmtDate(d.ves.mot_expiry)}</span>
                        : d.ves?.mot_status
                          ? <span style={{ color: C.textMuted, fontSize: 13 }}>{d.ves.mot_status}</span>
                          : "—"}
                    </Td>
                    <Td>
                      {d.ves?.tax_expiry
                        ? <span style={{ color: taxS.color, fontWeight: 600, fontSize: 13 }}>{fmtDate(d.ves.tax_expiry)}</span>
                        : "—"}
                    </Td>
                    <Td>{d.ves?.fuel_type || "—"}</Td>
                    <Td>{d.ves?.engine_size || "—"}</Td>
                  </tr>
                );
              })}
          </tbody>
        </TableWrap>
      </Card>
      {vehiclePageCount > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 14, flexWrap: "wrap", gap: 10 }}>
          <span style={{ color: C.textMuted, fontSize: 13 }}>
            Showing {(vehiclePage - 1) * VEHICLES_PER_PAGE + 1}–{Math.min(vehiclePage * VEHICLES_PER_PAGE, filteredVehicles.length)} of {filteredVehicles.length} vehicles
          </span>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <Btn small variant="secondary" disabled={vehiclePage === 1} onClick={() => setVehiclePage(p => p - 1)}>← Previous</Btn>
            <span style={{ color: C.textMuted, fontSize: 13 }}>Page {vehiclePage} of {vehiclePageCount}</span>
            <Btn small variant="secondary" disabled={vehiclePage === vehiclePageCount} onClick={() => setVehiclePage(p => p + 1)}>Next →</Btn>
          </div>
        </div>
      )}
      {detailModal && (() => {
        const d = detailModal;
        const outcome = d.gf?.aiResult?.outcome;
        const needsFmReview = outcome === "Amber" || outcome === "Red";
        return (
          <Modal title="Vehicle Details" onClose={closeModal}>
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                <div>
                  <div style={{ color: C.text, fontWeight: 700, fontSize: 16 }}>{d.name}</div>
                  {d.gf?.reg && (
                    <span style={{ color: C.text, fontWeight: 700, fontFamily: "'Courier New', monospace", letterSpacing: 2, background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 6, padding: "3px 8px", fontSize: 13, display: "inline-block", marginTop: 6 }}>{d.gf.reg}</span>
                  )}
                </div>
                <GreyFleetBadge outcome={outcome} fmResolution={d.gf?.fmResolution} policyExpiry={d.gf?.policyExpiry} />
              </div>
              {[
                ["Policy type",   d.gf?.aiResult?.clause || "—",  d.gf?.aiResult?.outcome === "Red" ? C.red : C.textSub],
                ["Policy expiry", fmtDate(d.gf?.policyExpiry),    d.gf?.policyExpiry ? vesStatus(d.gf.policyExpiry).color : C.textSub],
                ["Last checked",  fmtDate(d.gf?.checkedAt),       C.textSub],
                ...(d.ves?.mot_expiry ? [["MOT Expiry", fmtDate(d.ves.mot_expiry), vesStatus(d.ves.mot_expiry).color]] : []),
                ...(d.ves?.tax_expiry ? [["Tax Expiry", fmtDate(d.ves.tax_expiry), vesStatus(d.ves.tax_expiry).color]] : []),
              ].map(([label, value, valueColor = C.textSub]) => (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "10px 0", borderBottom: `1px solid ${C.border}22` }}>
                  <span style={{ color: C.textMuted, fontSize: 13 }}>{label}</span>
                  <span style={{ color: valueColor, fontSize: 13, textAlign: "right", maxWidth: "60%" }}>{value}</span>
                </div>
              ))}
              {d.gf?.aiResult?.summary && (
                <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: 12, marginTop: 14 }}>
                  <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Notes</div>
                  <div style={{ color: C.textSub, fontSize: 13, lineHeight: 1.6 }}>{d.gf.aiResult.summary}</div>
                </div>
              )}
            </div>

            {(needsFmReview || d.gf?.fmResolution) && (
              <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 18, marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                  <div style={{ color: C.textSub, fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>Fleet Manager Review</div>
                  {d.gf?.fmResolution && !fmEditMode && (
                    <Btn small variant="secondary" onClick={() => { setFmSelectedRes(d.gf.fmResolution); setFmNoteText(d.gf.fmNotes || ""); setFmEditMode(true); }}>Edit Review</Btn>
                  )}
                </div>

                {d.gf?.fmResolution && !fmEditMode ? (
                  <div>
                    <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", marginBottom: 10 }}>
                      <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Resolution</div>
                      <div style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>{d.gf.fmResolution}</div>
                    </div>
                    {d.gf.fmNotes && (
                      <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", marginBottom: 10 }}>
                        <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Notes</div>
                        <div style={{ color: C.textSub, fontSize: 13, lineHeight: 1.5 }}>{d.gf.fmNotes}</div>
                      </div>
                    )}
                    {d.gf.fmReviewedAt && (
                      <div style={{ color: C.textMuted, fontSize: 12 }}>Reviewed {fmtDate(d.gf.fmReviewedAt)}{d.gf.fmReviewedBy ? ` by ${d.gf.fmReviewedBy}` : ""}</div>
                    )}
                  </div>
                ) : (
                  <div>
                    <select
                      value={fmSelectedRes}
                      onChange={e => setFmSelectedRes(e.target.value)}
                      style={{ width: "100%", background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px", color: fmSelectedRes ? C.text : C.textMuted, fontSize: 13, outline: "none", boxSizing: "border-box", fontFamily: "inherit", marginBottom: 14, cursor: "pointer" }}>
                      <option value="">Select a resolution...</option>
                      {FM_RESOLUTIONS.map(res => <option key={res} value={res}>{res}</option>)}
                    </select>
                    <textarea
                      value={fmNoteText}
                      onChange={e => setFmNoteText(e.target.value)}
                      placeholder="Add notes about the action taken..."
                      rows={3}
                      style={{ width: "100%", background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", color: C.text, fontSize: 13, resize: "vertical", outline: "none", boxSizing: "border-box", fontFamily: "inherit", marginBottom: 12 }}
                    />
                    <Btn onClick={saveFmReview} disabled={!fmSelectedRes || fmSaving} style={{ width: "100%", justifyContent: "center" }}>
                      {fmSaving ? "Saving…" : "Save Review"}
                    </Btn>
                  </div>
                )}
              </div>
            )}

            <Btn variant="secondary" onClick={closeModal} style={{ width: "100%", justifyContent: "center" }}>Close</Btn>
          </Modal>
        );
      })()}
    </div>
  );
};

// ── REPORTS ───────────────────────────────────────────────────────────────────
const ReportsPage = ({ navigateTo, drivers, loading }) => {
  const checked = drivers.filter(d => d.last_check).length;
  const withPts = drivers.filter(d => d.points > 0).length;
  const expired = drivers.filter(d => d.mandate_status === "Expired").length;
  const banned  = drivers.filter(d => d.licence_status === "Banned").length;
  const riskBars = [
    { label: "Low Risk",    count: drivers.filter(d => d.risk === "Low").length,    color: C.green, f: { key: "risk", value: "Low",    label: "Low Risk Drivers",    color: C.green } },
    { label: "Medium Risk", count: drivers.filter(d => d.risk === "Medium").length, color: C.amber, f: { key: "risk", value: "Medium", label: "Medium Risk Drivers", color: C.amber } },
    { label: "High Risk",   count: drivers.filter(d => d.risk === "High").length,   color: C.red,   f: { key: "risk", value: "High",   label: "High Risk Drivers",   color: C.red   } },
  ];
  return (
    <div>
      <style>{shimmer}</style>
      <div style={{ marginBottom: 24 }}>
        <div style={{ color: C.text, fontWeight: 700, fontSize: 22, marginBottom: 4 }}>Reports</div>
        <div style={{ color: C.textMuted, fontSize: 14 }}>Summary of compliance activity</div>
      </div>
      <div className="kpi-grid">
        {loading ? Array(4).fill(0).map((_, i) => <SkeletonKPI key={i} />) : <>
          <KPICard label="Checked This Month"  value={checked} icon={Search}        color={C.blue}  sub={`of ${drivers.length}`} onClick={() => navigateTo("drivers", null)} />
          <KPICard label="Drivers With Points" value={withPts} icon={FileText}      color={C.amber} sub="Monitor closely"        onClick={() => navigateTo("drivers", { key: "risk", value: "Medium", label: "Drivers With Points", color: C.amber })} />
          <KPICard label="Expired Mandates"    value={expired} icon={Clock}         color={C.amber} sub="Need renewal"           onClick={() => navigateTo("mandates",   { key: "mandate_status", value: "Expired", label: "Expired Mandates", color: C.amber })} />
          <KPICard label="Banned Drivers"      value={banned}  icon={XCircle}       color={C.red}   sub="Immediate action"       onClick={() => navigateTo("drivers", { key: "licence_status", value: "Banned", label: "Banned Drivers", color: C.red })} />
        </>}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14 }}>
        <Card><TrendChart /></Card>
        <Card>
          <div style={{ color: C.textMuted, fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, marginBottom: 16 }}>Risk Distribution</div>
          {riskBars.map(({ label, count, color, f }) => (
            <div key={label} style={{ marginBottom: 16, cursor: "pointer" }} onClick={() => navigateTo("drivers", f)}
              onMouseEnter={e => e.currentTarget.style.opacity = "0.7"} onMouseLeave={e => e.currentTarget.style.opacity = "1"}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <span style={{ color: C.textSub, fontSize: 13 }}>{label}</span>
                <span style={{ color, fontWeight: 600, fontSize: 13 }}>{count} drivers</span>
              </div>
              <div style={{ background: C.surfaceAlt, borderRadius: 4, height: 8, overflow: "hidden" }}>
                <div style={{ background: color, height: "100%", width: drivers.length ? `${(count / drivers.length) * 100}%` : "0%", borderRadius: 4, transition: "width 0.6s ease" }} />
              </div>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
};

// ── ACTIVITY ──────────────────────────────────────────────────────────────────
const ActivityPage = ({ company, sessionToken }) => {
  const [logs, setLogs]             = useState([]);
  const [loading, setLoading]       = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");
  const [page, setPage]             = useState(0);
  const PAGE_SIZE = 50;

  useEffect(() => {
    if (!company?.id) return;
    setLoading(true);
    const token = sessionToken?.current || SUPABASE_KEY;
    fetch(
      `${SUPABASE_URL}/rest/v1/reminder_log?company_id=eq.${company.id}&order=sent_at.desc&limit=500&select=*,drivers(name)`,
      { headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${token}` } }
    )
      .then(r => r.json())
      .then(rows => { setLogs(Array.isArray(rows) ? rows : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [company, sessionToken]);

  const TYPE_LABELS = {
    driver_onboarding: "Driver Onboarding",
    mandate_renewal:   "Mandate Renewal",
    licence_renewal:   "Licence Renewal",
    insurance_expiry:  "Insurance Expiry",
    insurance_expired: "Insurance Expired",
    mot_expiry:        "MOT Expiry",
    mot_expired:       "MOT Expired",
    tax_expiry:        "Tax Expiry",
    tax_expired:       "Tax Expired",
    fm_digest:         "FM Digest",
  };

  const filtered   = typeFilter === "all" ? logs : logs.filter(l => l.reminder_type === typeFilter);
  const paged      = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);

  const statusColor = s => s === "sent" ? C.green : C.red;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <div style={{ color: C.text, fontWeight: 700, fontSize: 22, marginBottom: 4 }}>Activity</div>
        <div style={{ color: C.textMuted, fontSize: 14 }}>Log of all reminder and digest emails sent by FleetCustodian.</div>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        {["all", ...Object.keys(TYPE_LABELS)].map(t => (
          <button key={t} onClick={() => { setTypeFilter(t); setPage(0); }}
            style={{ background: typeFilter === t ? C.accent : C.surface, border: `1px solid ${typeFilter === t ? C.accent : C.border}`, borderRadius: 20, padding: "5px 14px", color: typeFilter === t ? "#fff" : C.textMuted, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
            {t === "all" ? "All" : TYPE_LABELS[t]}
          </button>
        ))}
      </div>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: C.textMuted }}>Loading…</div>
        ) : paged.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: C.textMuted }}>
            <Bell size={32} color={C.textMuted} style={{ marginBottom: 12, opacity: 0.4 }} />
            <div style={{ fontWeight: 600, marginBottom: 6 }}>No activity yet</div>
            <div style={{ fontSize: 13 }}>Reminder emails will appear here once the system starts sending them.</div>
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                {["Date & Time", "Type", "Recipient", "Driver", "Status"].map(h => (
                  <th key={h} style={{ padding: "12px 16px", textAlign: "left", color: C.textMuted, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paged.map((log, i) => (
                <tr key={log.id} style={{ borderBottom: `1px solid ${C.border}`, background: i % 2 === 0 ? "transparent" : C.surfaceAlt }}>
                  <td style={{ padding: "12px 16px", color: C.textMuted, fontSize: 13, whiteSpace: "nowrap" }}>
                    {new Date(log.sent_at).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    <span style={{ background: C.blueBg, color: C.accent, border: `1px solid ${C.accent}22`, borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 600 }}>
                      {TYPE_LABELS[log.reminder_type] || log.reminder_type}
                    </span>
                  </td>
                  <td style={{ padding: "12px 16px", color: C.text, fontSize: 13 }}>{log.recipient_email}</td>
                  <td style={{ padding: "12px 16px", color: C.textMuted, fontSize: 13 }}>{log.drivers?.name || "—"}</td>
                  <td style={{ padding: "12px 16px" }}>
                    <span style={{ color: statusColor(log.status), fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
                      {log.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 16 }}>
          <Btn small variant="secondary" disabled={page === 0} onClick={() => setPage(p => p - 1)}>← Prev</Btn>
          <span style={{ color: C.textMuted, fontSize: 13, alignSelf: "center" }}>Page {page + 1} of {totalPages}</span>
          <Btn small variant="secondary" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next →</Btn>
        </div>
      )}
    </div>
  );
};

// ── SETTINGS ──────────────────────────────────────────────────────────────────
const SettingsPage = ({ company, sessionToken, showToast, drivers, onSave }) => {
  const [companyName,    setCompanyName]    = useState(company?.name  ?? '');
  const [companyEmail,   setCompanyEmail]   = useState(company?.email ?? '');
  const [renewalPeriod,  setRenewalPeriod]  = useState('12_months');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);
  const [reminderSettings, setReminderSettings] = useState({
    licence_renewal_enabled: true,  licence_renewal_days: 14,
    insurance_expiry_enabled: true, insurance_expiry_days: 30,
    insurance_expired_enabled: true,
    mot_expiry_enabled: true,       mot_expiry_days: 30,
    mot_expired_enabled: true,
    tax_expiry_enabled: true,       tax_expiry_days: 30,
    tax_expired_enabled: true,
    digest_enabled: true,
    digest_on_clear_change: true,
    digest_on_clear_always: false,
  });
  const [reminderSaving, setReminderSaving] = useState(false);
  const [reminderSaved,  setReminderSaved]  = useState(false);

  useEffect(() => {
    if (company?.name)  setCompanyName(company.name);
    if (company?.email) setCompanyEmail(company.email);
    if (company?.mandate_renewal_period) setRenewalPeriod(company.mandate_renewal_period);
    // Fetch reminder settings
    if (company?.id) {
      const token = sessionToken?.current || SUPABASE_KEY;
      fetch(`${SUPABASE_URL}/rest/v1/reminder_settings?company_id=eq.${company.id}&limit=1`, {
        headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${token}` },
      })
        .then(r => r.json())
        .then(rows => { if (rows?.[0]) setReminderSettings(rows[0]); })
        .catch(() => {});
    }
  }, [company, sessionToken]);

  const handleSave = async () => {
    if (!company?.id) return;
    setSaving(true); setSaved(false);
    try {
      const token = sessionToken?.current || SUPABASE_KEY;
      console.log("SettingsPage handleSave:", { id: company.id, companyName, companyEmail, renewalPeriod });
      const res = await fetch(`${SUPABASE_URL}/rest/v1/companies?id=eq.${company.id}`, {
        method: "PATCH",
        headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${token}`, "Content-Type": "application/json", Prefer: "return=minimal" },
        body: JSON.stringify({ name: companyName, mandate_renewal_period: renewalPeriod }),
      });
      console.log("SettingsPage PATCH response:", res.status, await res.text());
      if (res.ok) {
        onSave?.({ ...company, name: companyName, mandate_renewal_period: renewalPeriod });

        // Recalculate recheck_due for all drivers
        try {
          // Step 1 — all drivers with a last_check
          const driversRes = await fetch(
            `${SUPABASE_URL}/rest/v1/drivers?company_id=eq.${company.id}&last_check=not.is.null&select=id,last_check,is_grey_fleet`,
            { headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${token}` } }
          );
          if (!driversRes.ok) throw new Error(await driversRes.text());
          const allDrivers = await driversRes.json();

          // Step 2 & 3 — calculate and PATCH
          console.log("recheck recalc: drivers count:", allDrivers.length, "first driver:", allDrivers[0]);
          await Promise.all(allDrivers.map((driver) => {
            let newRecheck;
            const base = new Date(driver.last_check);
            if (renewalPeriod === '1_month') {
              const d = new Date(base); d.setMonth(d.getMonth() + 1); newRecheck = d.toISOString().split('T')[0];
            } else if (renewalPeriod === '6_months') {
              const d = new Date(base); d.setMonth(d.getMonth() + 6); newRecheck = d.toISOString().split('T')[0];
            } else {
              const d = new Date(base); d.setFullYear(d.getFullYear() + 1); newRecheck = d.toISOString().split('T')[0];
            }
            return fetch(`${SUPABASE_URL}/rest/v1/drivers?id=eq.${driver.id}`, {
              method: "PATCH",
              headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${token}`, "Content-Type": "application/json", Prefer: "return=minimal" },
              body: JSON.stringify({ recheck_due: newRecheck }),
            }).then(async r => {
              if (!r.ok) console.error("driver recheck PATCH failed:", driver.id, r.status, await r.text());
              return r;
            });
          }));
        } catch (err) {
          console.error("Failed to recalculate recheck_due:", err);
        }

        setSaved(true);
        setTimeout(() => setSaved(false), 2500);
      } else {
        showToast("Failed to save settings", C.red);
      }
    } catch {
      showToast("Failed to save settings", C.red);
    }
    setSaving(false);
  };

  const handleSaveReminders = async () => {
    if (!company?.id) return;
    setReminderSaving(true); setReminderSaved(false);
    try {
      const token = sessionToken?.current || SUPABASE_KEY;
      const res = await fetch(`${SUPABASE_URL}/rest/v1/reminder_settings?company_id=eq.${company.id}`, {
        method: "POST",
        headers: {
          apikey: SUPABASE_KEY, Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Prefer: "resolution=merge-duplicates,return=minimal",
        },
        body: JSON.stringify({ ...reminderSettings, company_id: company.id }),
      });
      if (res.ok) {
        setReminderSaved(true);
        setTimeout(() => setReminderSaved(false), 2500);
      } else {
        showToast("Failed to save reminder settings", C.red);
      }
    } catch {
      showToast("Failed to save reminder settings", C.red);
    }
    setReminderSaving(false);
  };

  const selectStyle = { width: "100%", background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", color: C.text, fontSize: 14, outline: "none", fontFamily: "inherit" };

  const ReminderRow = ({ label, enabledKey, daysKey }) => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderBottom: `1px solid ${C.border}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div
          onClick={() => setReminderSettings(s => ({ ...s, [enabledKey]: !s[enabledKey] }))}
          style={{ width: 36, height: 20, borderRadius: 10, background: reminderSettings[enabledKey] ? C.accent : C.surfaceAlt, border: `1px solid ${reminderSettings[enabledKey] ? C.accent : C.border}`, cursor: "pointer", position: "relative", transition: "background 0.2s", flexShrink: 0 }}
        >
          <div style={{ position: "absolute", top: 2, left: reminderSettings[enabledKey] ? 18 : 2, width: 14, height: 14, borderRadius: "50%", background: "#fff", transition: "left 0.2s" }} />
        </div>
        <span style={{ color: reminderSettings[enabledKey] ? C.text : C.textMuted, fontSize: 13, fontWeight: 500 }}>{label}</span>
      </div>
      {daysKey && (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <input
            type="number" min={1} max={90}
            value={reminderSettings[daysKey]}
            onChange={e => setReminderSettings(s => ({ ...s, [daysKey]: parseInt(e.target.value) || 1 }))}
            disabled={!reminderSettings[enabledKey]}
            style={{ width: 54, background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 6, padding: "4px 8px", color: reminderSettings[enabledKey] ? C.text : C.textMuted, fontSize: 13, textAlign: "center", outline: "none", fontFamily: "inherit" }}
          />
          <span style={{ color: C.textMuted, fontSize: 12 }}>days before</span>
        </div>
      )}
    </div>
  );

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <div style={{ color: C.text, fontWeight: 700, fontSize: 22, marginBottom: 4 }}>Settings</div>
        <div style={{ color: C.textMuted, fontSize: 14 }}>Company and compliance configuration</div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 16 }}>
        <Card>
          <div style={{ color: C.text, fontWeight: 600, fontSize: 15, marginBottom: 16 }}>Company Settings</div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", color: C.textSub, fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Company Name</label>
            <input value={companyName} onChange={e => setCompanyName(e.target.value)} style={{ width: "100%", background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", color: C.text, fontSize: 14, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
          </div>
          {company.driver_seats > 0 && (() => {
            const used = drivers?.length ?? 0;
            const seats = company.driver_seats;
            const pct = Math.min(100, Math.round((used / seats) * 100));
            const barColor = pct >= 100 ? C.red : pct >= 80 ? C.amber : C.green;
            return (
              <div>
                <div style={{ height: 1, background: C.border, marginBottom: 16 }} />
                <label style={{ display: "block", color: C.textSub, fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Driver Seats</label>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
                  <span style={{ color: C.text, fontSize: 13 }}>{used} of {seats} seats used</span>
                  <span style={{ color: barColor, fontSize: 12, fontWeight: 700 }}>{pct}%</span>
                </div>
                <div style={{ height: 6, background: C.surfaceAlt, borderRadius: 99, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${pct}%`, background: barColor, borderRadius: 99, transition: "width 0.3s ease" }} />
                </div>
                <div style={{ marginTop: 12 }}>
                  <Btn small onClick={() => showToast("Please contact hello@fleetcustodian.com to upgrade your plan.", C.blue)}>Upgrade Plan</Btn>
                </div>
              </div>
            );
          })()}
        </Card>
        <Card>
          <div style={{ color: C.text, fontWeight: 600, fontSize: 15, marginBottom: 16 }}>Compliance Settings</div>

          {/* Licence Check Frequency — Pro only, not interactive */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <label style={{ color: C.textSub, fontSize: 13, fontWeight: 600 }}>Licence Check Frequency</label>
              <span style={{ background: C.amberBg, color: C.amber, border: `1px solid ${C.amber}`, borderRadius: 20, padding: "2px 8px", fontSize: 11, fontWeight: 700 }}>Pro only</span>
            </div>
            <select disabled style={{ ...selectStyle, color: C.textMuted, cursor: "not-allowed", opacity: 0.6 }}>
              <option>Monthly / Quarterly / Six-Monthly / Annually</option>
            </select>
          </div>

          {/* Mandate Renewal Period — functional */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", color: C.textSub, fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Mandate Renewal Period</label>
            <select value={renewalPeriod} onChange={e => setRenewalPeriod(e.target.value)} style={selectStyle}>
              <option value="1_month">1 Month</option>
              <option value="6_months">6 Months</option>
              <option value="12_months">12 Months</option>
            </select>
          </div>
        </Card>
      </div>
      <div style={{ marginTop: 20, display: "flex", alignItems: "center", gap: 14 }}>
        <Btn onClick={handleSave} disabled={saving}>{saving ? "Saving…" : "Save Settings"}</Btn>
        {saved && <span style={{ color: C.green, fontSize: 14, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}><CheckCircle size={15} color={C.green} /> Saved</span>}
      </div>

      {/* Reminders */}
      <div style={{ marginTop: 16 }}>
        <Card>
          <div style={{ color: C.text, fontWeight: 600, fontSize: 15, marginBottom: 4 }}>Driver Reminders</div>
          <div style={{ color: C.textMuted, fontSize: 13, marginBottom: 16 }}>Automated emails sent directly to drivers before and when compliance dates lapse.</div>
          <ReminderRow label="Licence renewal reminder"   enabledKey="licence_renewal_enabled"   daysKey="licence_renewal_days" />
          <ReminderRow label="Insurance expiry warning"   enabledKey="insurance_expiry_enabled"  daysKey="insurance_expiry_days" />
          <ReminderRow label="Insurance expired alert"    enabledKey="insurance_expired_enabled" />
          <ReminderRow label="MOT expiry warning"         enabledKey="mot_expiry_enabled"        daysKey="mot_expiry_days" />
          <ReminderRow label="MOT expired alert"          enabledKey="mot_expired_enabled" />
          <ReminderRow label="Vehicle tax expiry warning" enabledKey="tax_expiry_enabled"        daysKey="tax_expiry_days" />
          <ReminderRow label="Vehicle tax expired alert"  enabledKey="tax_expired_enabled" />

          <div style={{ color: C.text, fontWeight: 600, fontSize: 15, marginTop: 24, marginBottom: 4 }}>FM Daily Digest</div>
          <div style={{ color: C.textMuted, fontSize: 13, marginBottom: 16 }}>A morning email summarising your fleet compliance status.</div>
          <ReminderRow label="Send daily digest email"                        enabledKey="digest_enabled" />
          <ReminderRow label="Send green tick email when fleet becomes clean" enabledKey="digest_on_clear_change" />
          <ReminderRow label="Send green tick email every day"                enabledKey="digest_on_clear_always" />

          <div style={{ marginTop: 20, display: "flex", alignItems: "center", gap: 14 }}>
            <Btn onClick={handleSaveReminders} disabled={reminderSaving}>{reminderSaving ? "Saving…" : "Save Reminder Settings"}</Btn>
            {reminderSaved && <span style={{ color: C.green, fontSize: 14, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}><CheckCircle size={15} color={C.green} /> Saved</span>}
          </div>
        </Card>
      </div>
    </div>
  );
};

// ── AUTH SCREENS ──────────────────────────────────────────────────────────────
const ConfirmPage = ({ email, onBack }) => {
  const [resending, setResending] = useState(false);
  const [resent, setResent]       = useState(false);
  const [error, setError]         = useState(null);

  const handleResend = async () => {
    setResending(true); setError(null); setResent(false);
    try {
      const res = await fetch(`${SUPABASE_URL}/auth/v1/resend`, {
        method: "POST",
        headers: { apikey: SUPABASE_KEY, "Content-Type": "application/json" },
        body: JSON.stringify({ type: "signup", email }),
      });
      if (!res.ok) throw new Error("Failed to resend");
      setResent(true);
    } catch { setError("Failed to resend. Please try again."); }
    setResending(false);
  };

  return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ width: "100%", maxWidth: 400, textAlign: "center" }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 10, marginBottom: 28 }}>
          <div style={{ width: 36, height: 36, background: C.accent, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <ShieldCheck size={20} color="#fff" strokeWidth={2} />
          </div>
          <span style={{ color: C.text, fontWeight: 800, fontSize: 20 }}>FleetCustodian</span>
        </div>
        <div style={{ width: 64, height: 64, background: C.surfaceAlt, border: `2px solid ${C.accent}33`, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
          <Mail size={28} color={C.accent} />
        </div>
        <div style={{ color: C.text, fontWeight: 700, fontSize: 24, marginBottom: 8 }}>Almost there!</div>
        <div style={{ color: C.textMuted, fontSize: 14, lineHeight: 1.6, marginBottom: 24 }}>
          We've sent a confirmation link to <strong style={{ color: C.text }}>{email}</strong>. Click the link in your email to activate your account.
        </div>
        <Card>
          {resent && (
            <div style={{ color: C.green, fontSize: 13, marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
              <CheckCircle size={14} color={C.green} /> Confirmation email resent
            </div>
          )}
          {error && <div style={{ color: C.red, fontSize: 13, marginBottom: 12 }}>{error}</div>}
          <Btn onClick={handleResend} disabled={resending} style={{ width: "100%", justifyContent: "center", marginBottom: 10 }}>
            {resending ? "Sending…" : "Resend confirmation email"}
          </Btn>
          <Btn variant="secondary" onClick={onBack} style={{ width: "100%", justifyContent: "center" }}>← Back to Login</Btn>
        </Card>
      </div>
    </div>
  );
};

const LoginPage = ({ onLogin, onSignup }) => {
  const [view, setView]           = useState("login"); // "login" | "forgot"
  const [email, setEmail]         = useState("");
  const [password, setPassword]   = useState("");
  const [error, setError]         = useState(null);
  const [loggingIn, setLoggingIn] = useState(false);

  const [resetEmail, setResetEmail]   = useState("");
  const [resetSending, setResetSending] = useState(false);
  const [resetSent, setResetSent]     = useState(false);
  const [resetError, setResetError]   = useState(null);

  const handleLogin = async () => {
    setLoggingIn(true); setError(null);
    try {
      const res = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
        method: "POST",
        headers: { apikey: SUPABASE_KEY, "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const authData = await res.json();
      if (!authData.user) { setError("Invalid email or password."); setLoggingIn(false); return; }
      const userId = authData.user.id;
      const companyRes = await fetch(
        `${SUPABASE_URL}/rest/v1/companies?user_id=eq.${userId}&select=*&limit=1`,
        { headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${authData.access_token}` } }
      );
      const companies = await companyRes.json();
      onLogin(Array.isArray(companies) && companies.length > 0 ? companies[0] : null, authData.access_token);
    } catch { setError("Login failed. Please try again."); }
    setLoggingIn(false);
  };

  const handleResetRequest = async () => {
    setResetSending(true); setResetError(null);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/send-password-reset`, {
        method: "POST",
        headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${SUPABASE_KEY}`, "Content-Type": "application/json" },
        body: JSON.stringify({ email: resetEmail, redirect_to: APP_URL }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to send reset email.");
      setResetSent(true);
    } catch (err) {
      setResetError(err.message || "Something went wrong. Please try again.");
    }
    setResetSending(false);
  };

  const logo = (
    <div style={{ textAlign: "center", marginBottom: 32 }}>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <div style={{ width: 36, height: 36, background: C.accent, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <ShieldCheck size={20} color="#fff" strokeWidth={2} />
        </div>
        <span style={{ color: C.text, fontWeight: 800, fontSize: 20 }}>FleetCustodian</span>
      </div>
    </div>
  );

  if (view === "forgot") return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ width: "100%", maxWidth: 400 }}>
        {logo}
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ width: 56, height: 56, background: C.surfaceAlt, border: `2px solid ${C.accent}33`, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
            <Mail size={24} color={C.accent} />
          </div>
          <div style={{ color: C.text, fontWeight: 700, fontSize: 22, marginBottom: 6 }}>Reset your password</div>
          <div style={{ color: C.textMuted, fontSize: 14 }}>Enter your email and we'll send you a reset link</div>
        </div>
        <Card>
          {resetSent ? (
            <>
              <div style={{ textAlign: "center", marginBottom: 20 }}>
                <CheckCircle size={36} color={C.green} style={{ margin: "0 auto 12px" }} />
                <div style={{ color: C.green, fontWeight: 700, fontSize: 15, marginBottom: 6 }}>Check your email</div>
                <div style={{ color: C.textMuted, fontSize: 14 }}>We've sent a reset link to <strong style={{ color: C.text }}>{resetEmail}</strong></div>
              </div>
            </>
          ) : (
            <>
              <Input label="Email Address" type="email" value={resetEmail} onChange={e => setResetEmail(e.target.value)} placeholder="admin@company.co.uk" autoComplete="email" />
              {resetError && (
                <div style={{ color: C.red, fontSize: 13, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
                  <AlertTriangle size={14} color={C.red} /> {resetError}
                </div>
              )}
              <Btn onClick={handleResetRequest} disabled={resetSending || !resetEmail} style={{ width: "100%", padding: "12px 0", fontSize: 15, marginTop: 4, justifyContent: "center" }}>
                {resetSending ? "Sending…" : "Send reset link"}
              </Btn>
            </>
          )}
          <div style={{ textAlign: "center", marginTop: 16, color: C.textMuted, fontSize: 13 }}>
            <span onClick={() => { setView("login"); setResetSent(false); setResetError(null); }} style={{ color: C.accent, cursor: "pointer", fontWeight: 600 }}>← Back to login</span>
          </div>
        </Card>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ width: "100%", maxWidth: 400 }}>
        {logo}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ color: C.text, fontWeight: 700, fontSize: 24, marginBottom: 6 }}>Welcome back</div>
          <div style={{ color: C.textMuted, fontSize: 14 }}>Sign in to your compliance dashboard</div>
        </div>
        <Card>
          <Input label="Email Address" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="admin@company.co.uk" autoComplete="email" />
          <Input label="Password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" autoComplete="current-password" />
          {error && (
            <div style={{ color: C.red, fontSize: 13, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
              <AlertTriangle size={14} color={C.red} /> {error}
            </div>
          )}
          <Btn onClick={handleLogin} disabled={loggingIn} style={{ width: "100%", padding: "12px 0", fontSize: 15, marginTop: 4, justifyContent: "center" }}>
            {loggingIn ? "Signing in…" : "Sign In"}
          </Btn>
          <div style={{ textAlign: "center", marginTop: 12, fontSize: 13 }}>
            <span onClick={() => { setView("forgot"); setResetEmail(email); }} style={{ color: C.accent, cursor: "pointer", fontWeight: 600 }}>Forgot your password?</span>
          </div>
          <div style={{ textAlign: "center", marginTop: 12, color: C.textMuted, fontSize: 13 }}>
            Don't have an account? <span onClick={onSignup} style={{ color: C.accent, cursor: "pointer", fontWeight: 600 }}>Create Account</span>
          </div>
        </Card>
      </div>
    </div>
  );
};

const MandateDriverPage = ({ onBack, preFill = {} }) => {
  const params       = new URLSearchParams(window.location.search);
  const driverId     = params.get("driver_id");
  const driverEmail  = params.get("email") || preFill.email || "";
  const type         = params.get("type") || preFill.type || "";
  const isGreyFleet  = params.get("is_grey_fleet") === "true";

  // ── Standard / Grey Fleet state ────────────────────────────────────────────
  const [mode, setMode]         = useState("standard");
  const [form, setForm]         = useState({ name: preFill.name || "", email: preFill.email || "", licence: "", postcode: "", consent: false, reg: "", fileName: null, gfConsent: false });
  const [dragOver, setDragOver] = useState(false);
  const [done, setDone]         = useState(false);

  // ── Portal account state (Lite done flow) ───────────────────────────────────
  const [portalStep, setPortalStep]                   = useState("password"); // "password" | "complete"
  const [portalPassword, setPortalPassword]           = useState("");
  const [portalPasswordConfirm, setPortalPasswordConfirm] = useState("");
  const [portalPasswordError, setPortalPasswordError] = useState("");
  const [creatingAccount, setCreatingAccount]         = useState(false);

  const handleFile = (file) => { if (file && file.type === "application/pdf") setForm(f => ({ ...f, fileName: file.name })); };
  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); };

  const canSubmit = mode === "standard"
    ? form.consent
    : form.consent && form.reg.trim() && form.fileName && form.gfConsent;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    if (driverId) {
      const expiry = new Date();
      expiry.setFullYear(expiry.getFullYear() + 1);
      await db.update("drivers", driverId, {
        mandate_status: "Completed",
        mandate_expiry: expiry.toISOString().split("T")[0],
      });
    }
    setDone(true);
  };

  // ── Lite state ──────────────────────────────────────────────────────────────
  const [liteFile, setLiteFile]           = useState(null);
  const [liteConsent, setLiteConsent]     = useState(false);
  const [liteDragOver, setLiteDragOver]   = useState(false);
  const [liteSubmitting, setLiteSubmitting] = useState(false);
  const [liteError, setLiteError]         = useState(null);
  const liteFileRef                       = useRef(null);
  const [liteVehicleReg, setLiteVehicleReg]         = useState("");
  const [liteInsuranceFile, setLiteInsuranceFile]   = useState(null);
  const [liteInsDragOver, setLiteInsDragOver]       = useState(false);
  const liteInsuranceFileRef                        = useRef(null);
  const [submitProgress, setSubmitProgress]         = useState(0);
  const [submitMessage, setSubmitMessage]           = useState("");

  const handleLiteFile = (file) => { if (file && file.type === "application/pdf") setLiteFile(file); };
  const handleLiteDrop = (e) => { e.preventDefault(); setLiteDragOver(false); handleLiteFile(e.dataTransfer.files[0]); };
  const handleLiteInsuranceFile = (file) => { if (file && file.type === "application/pdf") setLiteInsuranceFile(file); };
  const handleLiteInsDrop = (e) => { e.preventDefault(); setLiteInsDragOver(false); handleLiteInsuranceFile(e.dataTransfer.files[0]); };
  const liteCanSubmit  = liteFile && liteConsent && (!isGreyFleet || (liteVehicleReg.trim() && liteInsuranceFile));

  const handleLiteSubmit = async () => {
    if (!liteCanSubmit || liteSubmitting) return;
    setLiteSubmitting(true);
    setLiteError(null);
    setSubmitProgress(10);
    setSubmitMessage("Uploading your documents...");
    try {
      // Read PDF as base64
      const base64Data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(liteFile);
      });

      // Read insurance PDF as base64 if grey fleet
      let insuranceBase64 = null;
      if (isGreyFleet && liteInsuranceFile) {
        insuranceBase64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(",")[1]);
          reader.onerror = reject;
          reader.readAsDataURL(liteInsuranceFile);
        });
      }

      setSubmitProgress(30);
      setSubmitMessage("Extracting your licence details...");

      // Extract data via Supabase Edge Function
      const edgeRes = await fetch(`${SUPABASE_URL}/functions/v1/extract-licence-pdf`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${SUPABASE_KEY}`,
          "apikey": SUPABASE_KEY,
        },
        body: JSON.stringify({ pdf_base64: base64Data, driver_id: driverId, is_grey_fleet: isGreyFleet, vehicle_registration: liteVehicleReg, insurance_pdf_base64: insuranceBase64 }),
      });

      const edgeJson = await edgeRes.json().catch(() => ({}));

      if (edgeRes.status === 422 && edgeJson.error === "document_too_old") {
        setLiteError(edgeJson.message || "This licence summary is too old. Please generate a fresh one from the DVLA website.");
        setSubmitProgress(0);
        setSubmitMessage("");
        setLiteSubmitting(false);
        return;
      }

      if (!edgeRes.ok) {
        throw new Error(edgeJson.message || edgeJson.error || "Failed to process PDF");
      }

      if (!edgeJson.success) throw new Error("Failed to extract licence data");

      setSubmitProgress(75);
      setSubmitMessage("Checking your insurance certificate...");

      setSubmitProgress(100);
      setSubmitMessage("Saving your records...");
      await new Promise(resolve => setTimeout(resolve, 500));

      // Check if driver already has a portal auth account — skip password step if so
      try {
        const portalCheckRes = await fetch(`${SUPABASE_URL}/functions/v1/create-portal-account`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey: SUPABASE_KEY,
            Authorization: `Bearer ${SUPABASE_KEY}`,
          },
          body: JSON.stringify({ email: driverEmail, driver_id: driverId, password: null }),
        });
        const portalCheckData = await portalCheckRes.json();
        if (portalCheckData.already_existed) {
          setPortalStep("already-registered");
        }
      } catch (_) {}

      setDone(true);
    } catch (err) {
      setLiteError(err.message || "Something went wrong. Please try again.");
      setSubmitProgress(0);
      setSubmitMessage("");
    }
    setLiteSubmitting(false);
  };

  // ── Portal account creation handler (Lite flow) ─────────────────────────────
  const handleCreatePortalAccount = async () => {
    setPortalPasswordError("");
    if (portalPassword.length < 8) {
      setPortalPasswordError("Password must be at least 8 characters");
      return;
    }
    if (portalPassword !== portalPasswordConfirm) {
      setPortalPasswordError("Passwords do not match");
      return;
    }
    setCreatingAccount(true);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/create-portal-account`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_KEY,
          Authorization: `Bearer ${SUPABASE_KEY}`,
        },
        body: JSON.stringify({ email: driverEmail, password: portalPassword, driver_id: driverId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to create account");
      setPortalStep("complete");
    } catch (err) {
      setPortalPasswordError(err.message || "Something went wrong. Please try again.");
    }
    setCreatingAccount(false);
  };

  // ── Shared done screen ──────────────────────────────────────────────────────
  if (done) return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <Card style={{ maxWidth: 440, width: "100%", textAlign: "center" }}>
        {type === "lite" ? (
          portalStep === "already-registered" ? (
            <>
              <CheckCircle size={48} color={C.green} style={{ margin: "0 auto 16px" }} />
              <div style={{ color: C.green, fontWeight: 700, fontSize: 20, marginBottom: 8 }}>Documents submitted</div>
              <div style={{ color: C.textMuted, fontSize: 14, marginBottom: 20 }}>Your documents have been submitted. Log in to your FleetCustodian portal at {PORTAL_URL} to view your compliance record.</div>
              <Btn onClick={() => window.open(PORTAL_URL, "_blank")} style={{ width: "100%", justifyContent: "center", marginBottom: 12 }}>Go to my portal</Btn>
              <div style={{ color: C.textMuted, fontSize: 12, cursor: "pointer", textDecoration: "underline" }} onClick={() => window.close()}>Close this tab</div>
            </>
          ) : portalStep === "password" ? (
            <>
              <div style={{ width: 48, height: 48, background: C.surfaceAlt, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                <Lock size={24} color={C.accent} />
              </div>
              <div style={{ color: C.text, fontWeight: 700, fontSize: 20, marginBottom: 8 }}>Create your compliance portal account</div>
              <div style={{ color: C.textMuted, fontSize: 14, marginBottom: 24 }}>Your documents have been submitted. Set a password to access your FleetCustodian compliance portal at any time.</div>
              <div style={{ textAlign: "left" }}>
                <Input label="Password" type="password" value={portalPassword} onChange={e => setPortalPassword(e.target.value)} placeholder="Min. 8 characters" />
                <Input label="Confirm password" type="password" value={portalPasswordConfirm} onChange={e => setPortalPasswordConfirm(e.target.value)} placeholder="Re-enter password" />
              </div>
              {portalPasswordError && (
                <div style={{ color: C.red, fontSize: 13, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
                  <AlertTriangle size={14} color={C.red} /> {portalPasswordError}
                </div>
              )}
              <Btn onClick={handleCreatePortalAccount} disabled={creatingAccount} style={{ width: "100%", justifyContent: "center", marginTop: 4 }}>
                {creatingAccount ? "Creating account…" : "Create Account"}
              </Btn>
            </>
          ) : (
            <>
              <CheckCircle size={48} color={C.green} style={{ margin: "0 auto 16px" }} />
              <div style={{ color: C.green, fontWeight: 700, fontSize: 20, marginBottom: 8 }}>Thank you for submitting your details</div>
              <div style={{ color: C.textMuted, fontSize: 14, marginBottom: 20 }}>Your employer has been notified and your compliance record has been updated. You can now log in to your compliance portal at any time using your email and password.</div>
              <Btn onClick={() => window.open(PORTAL_URL, "_blank")} style={{ width: "100%", justifyContent: "center", marginBottom: 12 }}>Go to my portal</Btn>
              <div style={{ color: C.textMuted, fontSize: 12, cursor: "pointer", textDecoration: "underline" }} onClick={() => window.close()}>Close this tab</div>
            </>
          )
        ) : (
          <>
            <CheckCircle size={48} color={C.green} style={{ margin: "0 auto 16px" }} />
            <div style={{ color: C.green, fontWeight: 700, fontSize: 20, marginBottom: 8 }}>Mandate completed</div>
            <div style={{ color: C.textMuted, fontSize: 14, marginBottom: 8 }}>Your employer can now perform automated DVLA licence checks on your behalf.</div>
            {mode === "greyfleet" && <div style={{ color: C.textMuted, fontSize: 14, marginBottom: 20 }}>Your vehicle insurance certificate has been submitted for review.</div>}
            <Btn onClick={onBack} variant="secondary">← Back to App</Btn>
          </>
        )}
      </Card>
    </div>
  );

  // ── Lite flow ───────────────────────────────────────────────────────────────
  if (type === "lite") return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ width: "100%", maxWidth: 520 }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <div style={{ width: 32, height: 32, background: C.accent, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <ShieldCheck size={16} color="#fff" strokeWidth={2.2} />
            </div>
            <span style={{ color: C.text, fontWeight: 800, fontSize: 18 }}>FleetCustodian</span>
          </div>
          <div style={{ color: C.text, fontWeight: 700, fontSize: 22, marginBottom: 4 }}>Upload Your Licence Summary</div>
          <div style={{ color: C.textMuted, fontSize: 13 }}>Follow the 3 steps below to complete your compliance check</div>
        </div>

        {/* Steps panel */}
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
          <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 16 }}>How to get your licence summary</div>
          {[
            ["Visit the DVLA website", <>Go to <a href="https://www.gov.uk/view-driving-licence" target="_blank" rel="noopener noreferrer" style={{ color: C.accent }}>www.gov.uk/view-driving-licence</a> and log in with your Government Gateway credentials.</>],
            ["Download your licence summary", "Generate and download the PDF summary of your driving licence record."],
            ["Upload it below", "Return here and upload the PDF, then tick the consent box and submit."],
          ].map(([title, desc], i) => (
            <div key={i} style={{ display: "flex", gap: 14, marginBottom: i < 2 ? 16 : 0 }}>
              <div style={{ flexShrink: 0, width: 28, height: 28, background: C.accent, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 13, fontWeight: 700 }}>{i + 1}</div>
              <div>
                <div style={{ color: C.text, fontWeight: 600, fontSize: 14, marginBottom: 2 }}>{title}</div>
                <div style={{ color: C.textMuted, fontSize: 13, lineHeight: 1.5 }}>{desc}</div>
              </div>
            </div>
          ))}
        </div>

        <Card>
          {/* PDF upload */}
          <div style={{ marginBottom: 18 }}>
            <label style={{ display: "block", color: C.textSub, fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Licence Summary PDF</label>
            <div
              onDragOver={e => { e.preventDefault(); setLiteDragOver(true); }}
              onDragLeave={() => setLiteDragOver(false)}
              onDrop={handleLiteDrop}
              onClick={() => liteFileRef.current?.click()}
              style={{ background: liteDragOver ? "rgba(59,130,246,0.08)" : C.surfaceAlt, border: `2px dashed ${liteDragOver ? C.accent : liteFile ? C.green : C.border}`, borderRadius: 10, padding: "20px 16px", textAlign: "center", cursor: "pointer", transition: "all 0.15s" }}
            >
              <input ref={liteFileRef} type="file" accept=".pdf" style={{ display: "none" }} onChange={e => handleLiteFile(e.target.files[0])} />
              {liteFile ? (
                <><FileCheck size={24} color={C.green} style={{ margin: "0 auto 4px" }} /><div style={{ color: C.green, fontWeight: 600, fontSize: 13 }}>{liteFile.name}</div><div style={{ color: C.textMuted, fontSize: 12, marginTop: 2 }}>Click to replace</div></>
              ) : (
                <><Upload size={24} color={C.textMuted} style={{ margin: "0 auto 4px" }} /><div style={{ color: C.textSub, fontSize: 13, fontWeight: 500 }}>Drop your licence summary here or click to browse</div><div style={{ color: C.textMuted, fontSize: 12, marginTop: 2 }}>PDF only</div></>
              )}
            </div>
          </div>

          {/* Grey fleet vehicle details */}
          {isGreyFleet && (
            <div style={{ marginBottom: 18 }}>
              <div style={{ color: C.textSub, fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 14 }}>Vehicle details</div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: "block", color: C.textSub, fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Vehicle registration</label>
                <input value={liteVehicleReg} onChange={e => setLiteVehicleReg(e.target.value.toUpperCase())} placeholder="e.g. AB12 CDE" maxLength={8}
                  style={{ width: "100%", background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px", color: C.text, fontSize: 16, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase", outline: "none", boxSizing: "border-box", fontFamily: "'Courier New', monospace" }} />
              </div>
              <div>
                <label style={{ display: "block", color: C.textSub, fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Insurance certificate</label>
                <div
                  onDragOver={e => { e.preventDefault(); setLiteInsDragOver(true); }}
                  onDragLeave={() => setLiteInsDragOver(false)}
                  onDrop={handleLiteInsDrop}
                  onClick={() => liteInsuranceFileRef.current?.click()}
                  style={{ background: liteInsDragOver ? "rgba(59,130,246,0.08)" : C.surfaceAlt, border: `2px dashed ${liteInsDragOver ? C.accent : liteInsuranceFile ? C.green : C.border}`, borderRadius: 10, padding: "20px 16px", textAlign: "center", cursor: "pointer", transition: "all 0.15s" }}
                >
                  <input ref={liteInsuranceFileRef} type="file" accept=".pdf" style={{ display: "none" }} onChange={e => handleLiteInsuranceFile(e.target.files[0])} />
                  {liteInsuranceFile ? (
                    <><FileCheck size={24} color={C.green} style={{ margin: "0 auto 4px" }} /><div style={{ color: C.green, fontWeight: 600, fontSize: 13 }}>{liteInsuranceFile.name}</div><div style={{ color: C.textMuted, fontSize: 12, marginTop: 2 }}>Click to replace</div></>
                  ) : (
                    <><Upload size={24} color={C.textMuted} style={{ margin: "0 auto 4px" }} /><div style={{ color: C.textSub, fontSize: 13, fontWeight: 500 }}>Drop your insurance certificate here or click to browse</div><div style={{ color: C.textMuted, fontSize: 12, marginTop: 2 }}>PDF only</div></>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Consent */}
          <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 10, padding: 14, marginBottom: 18 }}>
            <label style={{ display: "flex", gap: 12, cursor: "pointer", alignItems: "flex-start" }}>
              <input type="checkbox" checked={liteConsent} onChange={e => setLiteConsent(e.target.checked)} style={{ marginTop: 2, accentColor: C.accent, width: 16, height: 16, flexShrink: 0 }} />
              <span style={{ color: C.textSub, fontSize: 13, lineHeight: 1.5 }}>I consent to my employer reviewing my driving licence summary and I confirm the document is genuine and up to date. I understand I can withdraw this consent at any time in writing.</span>
            </label>
          </div>

          {liteError && (
            <div style={{ background: C.redBg, border: `1px solid ${C.red}33`, borderRadius: 8, padding: 12, marginBottom: 16, color: C.red, fontSize: 13, display: "flex", alignItems: "flex-start", gap: 8 }}>
              <AlertTriangle size={15} color={C.red} style={{ flexShrink: 0, marginTop: 1 }} /> {liteError}
            </div>
          )}

          <Btn onClick={handleLiteSubmit} disabled={!liteCanSubmit || liteSubmitting} style={{ width: "100%", padding: "11px 0", fontSize: 15, marginBottom: 10, justifyContent: "center" }}>
            {liteSubmitting ? "Processing…" : "Submit Licence Summary"}
          </Btn>
          <Btn onClick={onBack} variant="ghost" style={{ width: "100%", padding: "9px 0", justifyContent: "center" }}>← Back to App</Btn>
        </Card>
      </div>
      {submitProgress > 0 && (
        <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, height: 64, background: "#0B0F1A", zIndex: 1000, display: "flex", flexDirection: "column", justifyContent: "center", padding: "0 0 6px" }}>
          <div style={{ color: "#ffffff", fontSize: 13, textAlign: "center", marginBottom: 8, letterSpacing: 0.2 }}>{submitMessage}</div>
          <div style={{ width: "100%", height: 4, background: "rgba(255,255,255,0.08)" }}>
            <div style={{ height: "100%", width: `${submitProgress}%`, background: "#3B82F6", transition: "width 0.6s ease" }} />
          </div>
        </div>
      )}
    </div>
  );

  // ── Standard / Grey Fleet flow ──────────────────────────────────────────────
  return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ width: "100%", maxWidth: 480 }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <div style={{ width: 32, height: 32, background: C.accent, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <ShieldCheck size={16} color="#fff" strokeWidth={2.2} />
            </div>
            <span style={{ color: C.text, fontWeight: 800, fontSize: 18 }}>FleetCustodian</span>
          </div>
          <div style={{ color: C.text, fontWeight: 700, fontSize: 22, marginBottom: 4 }}>Driver Consent Mandate</div>
          <div style={{ color: C.textMuted, fontSize: 13 }}>Your employer requires consent to check your DVLA licence</div>
        </div>

        <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 10, padding: "4px", display: "flex", gap: 4, marginBottom: 20 }}>
          {[["standard", ShieldCheck, "Standard Mandate"], ["greyfleet", Car, "Grey Fleet Mandate"]].map(([m, IconComp, label]) => (
            <button key={m} onClick={() => setMode(m)} style={{ flex: 1, padding: "8px 0", borderRadius: 8, border: "none", fontWeight: 600, fontSize: 13, cursor: "pointer", transition: "all 0.15s", background: mode === m ? C.accent : "transparent", color: mode === m ? "#fff" : C.textMuted, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, fontFamily: "inherit" }}>
              <IconComp size={14} /> {label}
            </button>
          ))}
        </div>
        <div style={{ color: C.textMuted, fontSize: 11, textAlign: "center", marginTop: -14, marginBottom: 18 }}>↑ Preview toggle — not visible to drivers</div>

        <Card>
          {[["Full Name","text","James Thornton","name"],["Email Address","email","james@company.co.uk","email"],["Driving Licence Number","text","THORM801195JA9AB","licence"],["Postcode","text","SW1A 1AA","postcode"]].map(([l,t,p,k]) => (
            <Input key={k} label={l} type={t} value={form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} placeholder={p} />
          ))}
          <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 10, padding: 14, marginBottom: 18 }}>
            <label style={{ display: "flex", gap: 12, cursor: "pointer", alignItems: "flex-start" }}>
              <input type="checkbox" checked={form.consent} onChange={e => setForm(f => ({ ...f, consent: e.target.checked }))} style={{ marginTop: 2, accentColor: C.accent, width: 16, height: 16, flexShrink: 0 }} />
              <span style={{ color: C.textSub, fontSize: 13, lineHeight: 1.5 }}>I consent to my employer checking my driving licence with the DVLA on a monthly basis. I understand I can withdraw this consent at any time in writing.</span>
            </label>
          </div>
          {mode === "greyfleet" && (
            <>
              <div style={{ borderTop: `1px solid ${C.border}`, margin: "4px 0 18px", position: "relative" }}>
                <span style={{ position: "absolute", top: -10, left: "50%", transform: "translateX(-50%)", background: C.surface, padding: "0 10px", color: C.textMuted, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>Grey Fleet Vehicle</span>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", color: C.textSub, fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Vehicle Registration</label>
                <input value={form.reg} onChange={e => setForm(f => ({ ...f, reg: e.target.value.toUpperCase() }))} placeholder="e.g. AB12 CDE" maxLength={8}
                  style={{ width: "100%", background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px", color: C.text, fontSize: 16, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase", outline: "none", boxSizing: "border-box", fontFamily: "'Courier New', monospace" }} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", color: C.textSub, fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Business Use Insurance Certificate (PDF)</label>
                <div onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={handleDrop}
                  onClick={() => document.getElementById("mandate-file-input").click()}
                  style={{ background: dragOver ? "rgba(59,130,246,0.08)" : C.surfaceAlt, border: `2px dashed ${dragOver ? C.accent : form.fileName ? C.green : C.border}`, borderRadius: 10, padding: "20px 16px", textAlign: "center", cursor: "pointer", transition: "all 0.15s" }}>
                  <input id="mandate-file-input" type="file" accept=".pdf" style={{ display: "none" }} onChange={e => handleFile(e.target.files[0])} />
                  {form.fileName ? (
                    <><FileCheck size={24} color={C.green} style={{ margin: "0 auto 4px" }} /><div style={{ color: C.green, fontWeight: 600, fontSize: 13 }}>{form.fileName}</div><div style={{ color: C.textMuted, fontSize: 12, marginTop: 2 }}>Click to replace</div></>
                  ) : (
                    <><Upload size={24} color={C.textMuted} style={{ margin: "0 auto 4px" }} /><div style={{ color: C.textSub, fontSize: 13, fontWeight: 500 }}>Drop your insurance certificate here or click to browse</div><div style={{ color: C.textMuted, fontSize: 12, marginTop: 2 }}>PDF only · Must show business use cover</div></>
                  )}
                </div>
              </div>
              <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 10, padding: 14, marginBottom: 18 }}>
                <label style={{ display: "flex", gap: 12, cursor: "pointer", alignItems: "flex-start" }}>
                  <input type="checkbox" checked={form.gfConsent} onChange={e => setForm(f => ({ ...f, gfConsent: e.target.checked }))} style={{ marginTop: 2, accentColor: C.accent, width: 16, height: 16, flexShrink: 0 }} />
                  <span style={{ color: C.textSub, fontSize: 13, lineHeight: 1.5 }}>I confirm that my vehicle insurance includes business use cover and that I will notify my employer if this changes. I consent to annual renewal checks on my MOT and road tax status.</span>
                </label>
              </div>
            </>
          )}
          <Btn onClick={handleSubmit} variant={canSubmit ? "primary" : "secondary"} style={{ width: "100%", padding: "11px 0", fontSize: 15, marginBottom: 10, justifyContent: "center" }}>Submit Mandate</Btn>
          <Btn onClick={onBack} variant="ghost" style={{ width: "100%", padding: "9px 0", justifyContent: "center" }}>← Back to App</Btn>
        </Card>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// SIGNUP PAGE — 4-step wizard
// ═══════════════════════════════════════════════════════════════════════════════

const StepIndicator = ({ current, total }) => (
  <div style={{ textAlign: "center", marginBottom: 8, color: C.textMuted, fontSize: 13, fontWeight: 600 }}>
    Step {current} of {total}
  </div>
);

const SignupStepDots = ({ current, total }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 28 }}>
    {Array.from({ length: total }, (_, i) => i + 1).map(n => {
      const done = current > n, active = current === n;
      return (
        <div key={n} style={{
          width: done || active ? 28 : 8, height: 8, borderRadius: 4,
          background: done ? C.green : active ? C.accent : C.surfaceAlt,
          transition: "all 0.3s",
        }} />
      );
    })}
  </div>
);

const SignupStep1 = ({ form, setForm, onNext, onBack }) => {
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const valid = form.company && form.adminName && form.email.includes("@") && form.password.length >= 8;
  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <div style={{ color: C.text, fontWeight: 700, fontSize: 22, marginBottom: 6 }}>Create your account</div>
        <div style={{ color: C.textMuted, fontSize: 14 }}>Tell us about your company — takes 2 minutes</div>
      </div>
      <Input label="Company Name"    value={form.company}   onChange={set("company")}   placeholder="Acme Construction Ltd" autoComplete="off" />
      <Input label="Admin Full Name" value={form.adminName} onChange={set("adminName")} placeholder="John Smith"             autoComplete="off" />
      <Input label="Email Address"   type="email"    value={form.email}    onChange={set("email")}    placeholder="john@acme.co.uk"     autoComplete="off" />
      <Input label="Password"        type="password" value={form.password} onChange={set("password")} placeholder="Min. 8 characters"   autoComplete="new-password" />
      {form.password.length > 0 && form.password.length < 8 && (
        <div style={{ color: C.amber, fontSize: 12, marginTop: -10, marginBottom: 12, display: "flex", alignItems: "center", gap: 4 }}>
          <AlertTriangle size={12} color={C.amber} /> Password must be at least 8 characters
        </div>
      )}
      <Btn onClick={onNext} disabled={!valid} style={{ width: "100%", padding: "12px 0", fontSize: 15, justifyContent: "center", marginTop: 8 }}>Create Account →</Btn>
      <button onClick={onBack} style={{ width: "100%", background: "none", border: "none", color: C.textMuted, cursor: "pointer", fontSize: 13, marginTop: 12, padding: "6px 0", fontFamily: "inherit" }}>← Back to sign in</button>
    </div>
  );
};

const TIER_CONFIG = {
  lite: {
    name: "FleetCustodian Core",
    price: "£1", unit: "/driver/month",
    grey: "Grey fleet compliance included",
    billing: "Billed annually upfront",
    color: C.accent,
    bullets: ["Annual DVLA licence checks", "PDF upload via driver portal", "Grey fleet insurance checks", "Email mandate flow", "Compliance dashboard"],
  },
  premium: {
    name: "FleetCustodian Premium",
    price: "£20", unit: "/driver/year",
    grey: "£24/driver/year grey fleet add-on",
    billing: "Billed monthly in arrears",
    color: C.green,
    bullets: ["Everything in Core, plus:", "Automated monthly DVLA API checks", "No driver action required", "Real-time alerts"],
  },
};

const SignupStep2 = ({ form, setForm, onNext, onBack }) => (
  <div>
    <div style={{ marginBottom: 24 }}>
      <div style={{ color: C.text, fontWeight: 700, fontSize: 22, marginBottom: 6 }}>Choose your tier</div>
      <div style={{ color: C.textMuted, fontSize: 14 }}>Select the plan that works for your fleet</div>
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 24 }}>
      {Object.entries(TIER_CONFIG).map(([key, tier]) => {
        const active = form.tier === key;
        return (
          <div key={key} onClick={() => setForm(f => ({ ...f, tier: key }))}
            style={{ background: active ? `${tier.color}12` : C.surfaceAlt, border: `2px solid ${active ? tier.color : C.border}`, borderRadius: 12, padding: 18, cursor: "pointer", transition: "all 0.18s", position: "relative" }}>
            {active && (
              <div style={{ position: "absolute", top: 10, right: 10, background: tier.color, borderRadius: "50%", width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <CheckCircle size={14} color="#fff" />
              </div>
            )}
            <div style={{ color: tier.color, fontWeight: 700, fontSize: 14, marginBottom: 6 }}>{tier.name}</div>
            <div style={{ marginBottom: 2 }}>
              <span style={{ color: C.text, fontWeight: 800, fontSize: 26 }}>{tier.price}</span>
              <span style={{ color: C.textMuted, fontSize: 12 }}>{tier.unit}</span>
            </div>
            <div style={{ color: C.textMuted, fontSize: 11, marginBottom: 6 }}>{tier.grey}</div>
            <div style={{ color: tier.color, fontSize: 10, fontWeight: 700, marginBottom: 12, background: `${tier.color}18`, borderRadius: 6, padding: "3px 8px", display: "inline-block", textTransform: "uppercase", letterSpacing: 0.5 }}>{tier.billing}</div>
            <div>
              {tier.bullets.map(b => (
                <div key={b} style={{ display: "flex", alignItems: "flex-start", gap: 7, marginBottom: 6 }}>
                  <CheckCircle size={12} color={tier.color} strokeWidth={2.5} style={{ flexShrink: 0, marginTop: 2 }} />
                  <span style={{ color: C.textSub, fontSize: 12, lineHeight: 1.4 }}>{b}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
    <Btn onClick={onNext} disabled={!form.tier} style={{ width: "100%", padding: "12px 0", fontSize: 15, justifyContent: "center" }}>Continue →</Btn>
    <button onClick={onBack} style={{ width: "100%", background: "none", border: "none", color: C.textMuted, cursor: "pointer", fontSize: 13, marginTop: 12, padding: "6px 0", fontFamily: "inherit" }}>← Back</button>
  </div>
);

const SignupStep3 = ({ form, setForm, onNext, onBack }) => {
  const count = parseInt(form.driverCount, 10) || 0;
  const isLite = form.tier === "lite";
  const costDisplay = count > 0
    ? isLite
      ? `£${(count * 12).toLocaleString("en-GB")} / year`
      : `£${(count * 21 / 12).toFixed(2)} / month`
    : null;
  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <div style={{ color: C.text, fontWeight: 700, fontSize: 22, marginBottom: 6 }}>How many drivers?</div>
        <div style={{ color: C.textMuted, fontSize: 14 }}>{isLite ? "We'll calculate your annual total" : "We'll calculate your monthly cost"}</div>
      </div>
      <div style={{ marginBottom: 20 }}>
        <label style={{ display: "block", color: C.textSub, fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Number of drivers</label>
        <input type="number" min="1" value={form.driverCount} onChange={e => setForm(f => ({ ...f, driverCount: e.target.value }))} placeholder="e.g. 25"
          style={{ width: "100%", background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: "12px 14px", color: C.text, fontSize: 22, fontWeight: 700, outline: "none", boxSizing: "border-box", fontFamily: "inherit" }} />
      </div>
      {costDisplay && (
        <div style={{ background: C.accentGlow, border: `1px solid ${C.accent}33`, borderRadius: 12, padding: "16px 20px", marginBottom: 20, textAlign: "center" }}>
          <div style={{ color: C.textMuted, fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>
            {isLite ? "Annual total" : "Monthly cost"}
          </div>
          <div style={{ color: C.text, fontWeight: 800, fontSize: 32 }}>{costDisplay}</div>
          <div style={{ color: C.textMuted, fontSize: 12, marginTop: 4 }}>
            {isLite ? `${count} driver${count === 1 ? "" : "s"} × £12/year` : `${count} driver${count === 1 ? "" : "s"} × £21/year ÷ 12`}
          </div>
        </div>
      )}
      <Btn onClick={onNext} disabled={count < 1} style={{ width: "100%", padding: "12px 0", fontSize: 15, justifyContent: "center" }}>Continue →</Btn>
      <button onClick={onBack} style={{ width: "100%", background: "none", border: "none", color: C.textMuted, cursor: "pointer", fontSize: 13, marginTop: 12, padding: "6px 0", fontFamily: "inherit" }}>← Back</button>
    </div>
  );
};

const SignupStep4Lite = ({ form, onConfirm }) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState(null);

  const handleComplete = async () => {
    setSubmitting(true); setError(null);
    try {
      // Step 1: create auth account
      const authRes = await fetch(`${SUPABASE_URL}/auth/v1/signup`, {
        method: "POST",
        headers: { apikey: SUPABASE_KEY, "Content-Type": "application/json" },
        body: JSON.stringify({ email: form.email, password: form.password }),
      });
      const authData = await authRes.json();

      // Detect duplicate email
      const errMsg = authData.error?.message || authData.msg || (typeof authData.error === "string" ? authData.error : "") || "";
      if (!authRes.ok || errMsg.toLowerCase().includes("registered")) {
        setError("duplicate");
        setSubmitting(false);
        return;
      }

      const userId = authData.user?.id || authData.id;
      if (!userId) throw new Error("Account creation failed — please try again.");

      // Step 2: send confirmation email with all signup data stored server-side
      await fetch(`${SUPABASE_URL}/functions/v1/send-confirmation-email`, {
        method: "POST",
        headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${SUPABASE_KEY}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id:      userId,
          email:        form.email,
          company_name: form.company,
          contact_name: form.adminName,
          driver_count: parseInt(form.driverCount, 10) || 0,
          product_tier: "lite",
        }),
      });

      // Step 3: show confirmation screen
      onConfirm(form.email);
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <div style={{ color: C.text, fontWeight: 700, fontSize: 22, marginBottom: 6 }}>Almost there</div>
        <div style={{ color: C.textMuted, fontSize: 14 }}>Review your plan and complete setup</div>
      </div>
      <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16, marginBottom: 20 }}>
        <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 12 }}>Account Summary</div>
        {[["Company", form.company], ["Admin", form.adminName], ["Email", form.email], ["Plan", "FleetCustodian Core"], ["Drivers", `${form.driverCount} driver${parseInt(form.driverCount, 10) !== 1 ? "s" : ""}`], ["Annual total", `£${(parseInt(form.driverCount, 10) * 12).toLocaleString("en-GB")}`]].map(([label, value]) => (
          <div key={label} style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
            <span style={{ color: C.textMuted, fontSize: 13 }}>{label}</span>
            <span style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>{value}</span>
          </div>
        ))}
      </div>
      <div style={{ background: C.amberBg, border: `1px solid ${C.amber}33`, borderRadius: 10, padding: "14px 16px", marginBottom: 20, display: "flex", gap: 10 }}>
        <Clock size={18} color={C.amber} style={{ flexShrink: 0, marginTop: 1 }} />
        <div>
          <div style={{ color: C.amber, fontWeight: 600, fontSize: 13, marginBottom: 3 }}>Payment coming soon</div>
          <div style={{ color: C.textSub, fontSize: 13 }}>Your account will be created now. We'll be in touch with payment details before any charges.</div>
        </div>
      </div>
      {error === "duplicate" ? (
        <div style={{ background: C.amberBg, border: `1px solid ${C.amber}33`, borderRadius: 10, padding: "14px 16px", marginBottom: 16 }}>
          <div style={{ color: C.amber, fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Account already registered</div>
          <div style={{ color: C.textSub, fontSize: 13, marginBottom: 12 }}>An account with this email already exists. Check your inbox for a confirmation email or click below to resend.</div>
          <Btn
            variant="secondary"
            disabled={submitting}
            onClick={async () => {
              setSubmitting(true); setError(null);
              try {
                await fetch(`${SUPABASE_URL}/functions/v1/send-confirmation-email`, {
                  method: "POST",
                  headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${SUPABASE_KEY}`, "Content-Type": "application/json" },
                  body: JSON.stringify({
                    email:        form.email,
                    company_name: form.company,
                    contact_name: form.adminName,
                    driver_count: parseInt(form.driverCount, 10) || 0,
                    product_tier: "lite",
                  }),
                });
                setError("resent");
              } catch { setError("Failed to resend. Please try again."); }
              setSubmitting(false);
            }}
            style={{ fontSize: 13, padding: "7px 14px" }}
          >
            Resend confirmation email
          </Btn>
        </div>
      ) : error === "resent" ? (
        <div style={{ color: C.green, fontSize: 13, marginBottom: 16, display: "flex", alignItems: "center", gap: 6 }}>
          <CheckCircle size={14} color={C.green} /> Confirmation email resent — check your inbox.
        </div>
      ) : error ? (
        <div style={{ color: C.red, fontSize: 13, marginBottom: 16, display: "flex", alignItems: "center", gap: 6 }}>
          <AlertTriangle size={14} color={C.red} /> {error}
        </div>
      ) : null}
      <Btn onClick={handleComplete} disabled={submitting} style={{ width: "100%", padding: "12px 0", fontSize: 15, justifyContent: "center" }}>
        {submitting ? "Creating account…" : "Complete Setup →"}
      </Btn>
    </div>
  );
};

const SignupStep4Premium = ({ form }) => {
  useEffect(() => {
    fetch(`${SUPABASE_URL}/functions/v1/send-mandate-email`, {
      method: "POST",
      headers: { apikey: SUPABASE_KEY, "Content-Type": "application/json" },
      body: JSON.stringify({ driver_id: "premium-enquiry", driver_name: form.adminName, driver_email: "shaun.rowley@me.com", company_name: form.company }),
    }).catch(() => {});
  }, []); // eslint-disable-line

  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ width: 64, height: 64, background: C.greenBg, border: `2px solid ${C.green}44`, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
        <CheckCircle size={32} color={C.green} />
      </div>
      <div style={{ color: C.text, fontWeight: 700, fontSize: 22, marginBottom: 10 }}>Thanks for your interest!</div>
      <div style={{ color: C.textSub, fontSize: 14, lineHeight: 1.7, marginBottom: 24 }}>
        FleetCustodian Premium gives you automated DVLA API checks and real-time alerts — no driver action required.<br /><br />
        Someone from our team will be in touch within <strong style={{ color: C.text }}>24 hours</strong> to get you set up.
      </div>
      <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 10, padding: "14px 18px", marginBottom: 8, textAlign: "left" }}>
        <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Your enquiry</div>
        <div style={{ color: C.text, fontWeight: 600, fontSize: 14 }}>{form.company}</div>
        <div style={{ color: C.textMuted, fontSize: 13 }}>{form.adminName} · {form.email}</div>
        <div style={{ color: C.textMuted, fontSize: 13 }}>{form.driverCount} driver{parseInt(form.driverCount, 10) !== 1 ? "s" : ""}</div>
      </div>
    </div>
  );
};

const SignupPage = ({ onBack, onConfirm }) => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ company: "", adminName: "", email: "", password: "", tier: "", driverCount: "" });
  const goBack = () => step === 1 ? onBack() : setStep(s => s - 1);

  return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", flexDirection: "column", fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>
      <div style={{ borderBottom: `1px solid ${C.border}`, padding: "16px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 32, height: 32, background: C.accent, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <ShieldCheck size={16} color="#fff" strokeWidth={2.2} />
          </div>
          <span style={{ color: C.text, fontWeight: 800, fontSize: 17 }}>FleetCustodian</span>
        </div>
        <div style={{ color: C.textMuted, fontSize: 13 }}>
          Already have an account? <span onClick={onBack} style={{ color: C.accent, cursor: "pointer", fontWeight: 600 }}>Sign in</span>
        </div>
      </div>
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "32px 16px" }}>
        <div style={{ width: "100%", maxWidth: step === 2 ? 620 : 480 }}>
          <StepIndicator current={step} total={4} />
          <SignupStepDots current={step} total={4} />
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, padding: "28px 28px 24px" }}>
            {step === 1 && <SignupStep1    form={form} setForm={setForm} onNext={() => setStep(2)} onBack={goBack} />}
            {step === 2 && <SignupStep2    form={form} setForm={setForm} onNext={() => setStep(3)} onBack={goBack} />}
            {step === 3 && <SignupStep3    form={form} setForm={setForm} onNext={() => setStep(4)} onBack={goBack} />}
            {step === 4 && form.tier === "lite"    && <SignupStep4Lite    form={form} onConfirm={onConfirm} />}
            {step === 4 && form.tier === "premium" && <SignupStep4Premium form={form} />}
          </div>
        </div>
      </div>
      <div style={{ borderTop: `1px solid ${C.border}`, padding: "14px 24px", display: "flex", justifyContent: "center", gap: 24, flexWrap: "wrap" }}>
        {["Privacy Policy", "Terms of Service", "GDPR", "Contact Support"].map(l => (
          <span key={l} style={{ color: C.textMuted, fontSize: 12, cursor: "pointer" }}>{l}</span>
        ))}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// DRIVER PORTAL
// ═══════════════════════════════════════════════════════════════════════════════
const DriverPortalApp = ({ supabaseUrl, supabaseKey }) => {
  const [portalView, setPortalView]         = useState("login"); // "login" | "forgot" | "dashboard"
  const [portalUser, setPortalUser]         = useState(null);
  const [portalEmail, setPortalEmail]       = useState("");
  const [portalPassword, setPortalPassword] = useState("");
  const [portalLoginError, setPortalLoginError] = useState("");
  const [portalLoggingIn, setPortalLoggingIn]   = useState(false);
  const [forgotSent, setForgotSent]         = useState(false);
  const [forgotError, setForgotError]       = useState("");
  const [forgotSending, setForgotSending]   = useState(false);
  const [portalLicence, setPortalLicence]   = useState(null);
  const [portalVehicles, setPortalVehicles] = useState([]);
  const [portalVes, setPortalVes]           = useState({});
  const [portalDataLoading, setPortalDataLoading] = useState(false);
  const [vehicleIndex, setVehicleIndex]     = useState(0);
  const carouselRef = useRef(null);
  const slideRefs   = useRef([]);
  const [showDeleteVehicle, setShowDeleteVehicle]   = useState(false);
  const [deletingVehicle, setDeletingVehicle]       = useState(false);
  const [showAddVehicle, setShowAddVehicle]         = useState(false);
  const [addVehicleReg, setAddVehicleReg]           = useState("");
  const [addVehicleFile, setAddVehicleFile]         = useState(null);
  const [addVehicleFileName, setAddVehicleFileName] = useState("");
  const [addVehicleError, setAddVehicleError]       = useState("");
  const [addVehicleSubmitting, setAddVehicleSubmitting] = useState(false);
  const [addVehicleDone, setAddVehicleDone]         = useState(false);
  const [showGdprModal, setShowGdprModal]   = useState(false);
  const [gdprConfirmText, setGdprConfirmText] = useState("");
  const [gdprDeleting, setGdprDeleting]     = useState(false);
  const [gdprDeleted, setGdprDeleted]       = useState(false);

  const fetchPortalData = useCallback(async () => {
    if (!portalUser) return;
    setPortalDataLoading(true);
    const headers = { apikey: supabaseKey, Authorization: `Bearer ${portalUser.accessToken}` };
    try {
      const [licRes, gfRes] = await Promise.all([
        fetch(`${supabaseUrl}/rest/v1/licence_summaries?driver_id=eq.${portalUser.id}&order=extracted_at.desc&limit=1`, { headers }),
        fetch(`${supabaseUrl}/rest/v1/grey_fleet_checks?driver_id=eq.${portalUser.id}&order=is_primary.desc,created_at.desc`, { headers }),
      ]);
      const [licData, gfData] = await Promise.all([licRes.json(), gfRes.json()]);
      const lic = Array.isArray(licData) && licData.length ? licData[0] : null;
      const vehicles = Array.isArray(gfData) ? gfData : [];
      setPortalLicence(lic);
      setPortalVehicles(vehicles);
      const regs = vehicles.map(v => v.reg).filter(Boolean).map(r => r.replace(/\s+/g, "").toUpperCase());
      if (regs.length > 0) {
        const vesRes = await fetch(
          `${supabaseUrl}/rest/v1/vehicle_ves_data?registration=in.(${regs.join(",")})&select=*`,
          { headers }
        );
        const vesRows = await vesRes.json();
        const vesMap = {};
        vesRows.forEach(row => { vesMap[row.registration] = row; });
        setPortalVes(vesMap);
      }
    } catch (err) {
      console.error("Portal data fetch error:", err);
    }
    setPortalDataLoading(false);
  }, [portalUser, supabaseUrl, supabaseKey]);

  useEffect(() => {
    if (portalView !== "dashboard" || !portalUser) return;
    fetchPortalData();
  }, [fetchPortalData, portalUser, portalView]);

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    const handleScroll = () => {
      const idx = Math.round(el.scrollLeft / el.offsetWidth);
      setVehicleIndex(idx);
    };
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, [portalVehicles.length]);

  const handlePortalLogin = async () => {
    setPortalLoginError("");
    setPortalLoggingIn(true);
    try {
      const res = await fetch(`${supabaseUrl}/auth/v1/token?grant_type=password`, {
        method: "POST",
        headers: { "Content-Type": "application/json", apikey: supabaseKey },
        body: JSON.stringify({ email: portalEmail, password: portalPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error_description || "Invalid email or password");

      const driverRes = await fetch(
        `${supabaseUrl}/rest/v1/drivers?portal_user_id=eq.${data.user.id}&select=*&limit=1`,
        { headers: { apikey: supabaseKey, Authorization: `Bearer ${data.access_token}` } }
      );
      const drivers = await driverRes.json();
      if (!drivers?.length) throw new Error("No driver record found for this account");

      setPortalUser({ ...drivers[0], accessToken: data.access_token });
      setPortalView("dashboard");
    } catch (err) {
      setPortalLoginError(err.message || "Sign in failed");
    }
    setPortalLoggingIn(false);
  };

  const handleForgotPassword = async () => {
    setForgotError("");
    setForgotSending(true);
    try {
      const res = await fetch(`${supabaseUrl}/functions/v1/send-password-reset`, {
        method: "POST",
        headers: { apikey: supabaseKey, Authorization: `Bearer ${supabaseKey}`, "Content-Type": "application/json" },
        body: JSON.stringify({ email: portalEmail }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to send reset email.");
      setForgotSent(true);
    } catch (err) {
      setForgotError(err.message || "Something went wrong. Please try again.");
    }
    setForgotSending(false);
  };

  const handleDeleteVehicle = async () => {
    const vehicle = portalVehicles[vehicleIndex];
    if (!vehicle || vehicle.isPrimary) return;
    setDeletingVehicle(true);
    try {
      await fetch(
        `${supabaseUrl}/rest/v1/grey_fleet_checks?driver_id=eq.${portalUser.id}&reg=eq.${vehicle.reg.replace(/\s+/g, "").toUpperCase()}`,
        {
          method: "DELETE",
          headers: {
            apikey: supabaseKey,
            Authorization: `Bearer ${portalUser.accessToken}`,
            Prefer: "return=minimal",
          },
        }
      );
      setShowDeleteVehicle(false);
      setVehicleIndex(0);
      await fetchPortalData();
    } catch (err) {
      console.error("Delete vehicle error:", err);
    }
    setDeletingVehicle(false);
  };

  const handleAddVehicle = async () => {
    setAddVehicleError("");
    const reg = addVehicleReg.replace(/\s+/g, "").toUpperCase();
    if (!reg) { setAddVehicleError("Please enter your vehicle registration"); return; }
    if (!addVehicleFile) { setAddVehicleError("Please upload your insurance certificate"); return; }

    setAddVehicleSubmitting(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(",")[1]);
        reader.onerror = () => reject(new Error("Failed to read file"));
        reader.readAsDataURL(addVehicleFile);
      });

      console.log("Calling check-insurance-pdf with:", { reg, driver_id: portalUser.id });
      const res = await fetch(`${supabaseUrl}/functions/v1/check-insurance-pdf`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: supabaseKey,
          Authorization: `Bearer ${supabaseKey}`,
        },
        body: JSON.stringify({
          insurance_pdf_base64: base64,
          vehicle_registration: reg,
          driver_id:  portalUser.id,
          company_id: portalUser.company_id,
        }),
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Submission failed");

      setAddVehicleDone(true);
      setTimeout(() => {
        setShowAddVehicle(false);
        setAddVehicleReg("");
        setAddVehicleFile(null);
        setAddVehicleFileName("");
        setAddVehicleDone(false);
        fetchPortalData();
      }, 2000);

    } catch (err) {
      console.error("handleAddVehicle error:", err);
      setAddVehicleError(err.message || "Something went wrong. Please try again.");
    }
    setAddVehicleSubmitting(false);
  };

  const handleGdprDelete = async () => {
    setGdprDeleting(true);
    try {
      await fetch(`${supabaseUrl}/functions/v1/delete-portal-account`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: supabaseKey,
          Authorization: `Bearer ${portalUser.accessToken}`,
        },
        body: JSON.stringify({ driver_id: portalUser.id }),
      });
    } catch (err) {
      console.error("delete-portal-account error:", err);
    }
    try {
      await fetch(`${supabaseUrl}/rest/v1/removal_requests`, {
        method: "POST",
        headers: {
          apikey: supabaseKey,
          Authorization: `Bearer ${portalUser.accessToken}`,
          "Content-Type": "application/json",
          Prefer: "return=minimal",
        },
        body: JSON.stringify({
          driver_id:      portalUser.id,
          acknowledged_at: new Date().toISOString(),
          deleted_at:      new Date().toISOString(),
        }),
      });
    } catch (err) {
      console.error("removal_requests insert error:", err);
    }
    setGdprDeleting(false);
    setGdprDeleted(true);
    setShowGdprModal(false);
  };

  const Logo = () => (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
      <div style={{ width: 36, height: 36, background: C.accent, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <ShieldCheck size={20} color="#fff" strokeWidth={2} />
      </div>
      <span style={{ color: C.text, fontWeight: 800, fontSize: 20 }}>FleetCustodian</span>
    </div>
  );

  if (portalView === "login") return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>
      <div style={{ width: "100%", maxWidth: 400 }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <Logo />
          <div style={{ color: C.text, fontWeight: 700, fontSize: 24, marginBottom: 6 }}>Driver Compliance Portal</div>
          <div style={{ color: C.textMuted, fontSize: 14 }}>Sign in to view your compliance record</div>
        </div>
        <Card>
          <Input label="Email Address" type="email" value={portalEmail} onChange={e => setPortalEmail(e.target.value)} placeholder="your@email.com" autoComplete="email" />
          <Input label="Password" type="password" value={portalPassword} onChange={e => setPortalPassword(e.target.value)} placeholder="••••••••" autoComplete="current-password" />
          {portalLoginError && (
            <div style={{ color: C.red, fontSize: 13, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
              <AlertTriangle size={14} color={C.red} /> {portalLoginError}
            </div>
          )}
          <Btn onClick={handlePortalLogin} disabled={portalLoggingIn} style={{ width: "100%", padding: "12px 0", fontSize: 15, marginTop: 4, justifyContent: "center" }}>
            {portalLoggingIn ? "Signing in…" : "Sign In"}
          </Btn>
          <div style={{ textAlign: "center", marginTop: 16, color: C.textMuted, fontSize: 13 }}>
            <span onClick={() => { setPortalLoginError(""); setPortalView("forgot"); }} style={{ color: C.accent, cursor: "pointer", fontWeight: 600 }}>Forgot your password?</span>
          </div>
        </Card>
      </div>
    </div>
  );

  if (portalView === "forgot") return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>
      <div style={{ width: "100%", maxWidth: 400 }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <Logo />
          <div style={{ color: C.text, fontWeight: 700, fontSize: 22, marginBottom: 6 }}>Reset your password</div>
          <div style={{ color: C.textMuted, fontSize: 14 }}>Enter your email and we'll send you a reset link</div>
        </div>
        <Card>
          {forgotSent ? (
            <>
              <div style={{ textAlign: "center", marginBottom: 20 }}>
                <CheckCircle size={40} color={C.green} style={{ margin: "0 auto 12px" }} />
                <div style={{ color: C.green, fontWeight: 700, fontSize: 16, marginBottom: 6 }}>Check your email</div>
                <div style={{ color: C.textMuted, fontSize: 14 }}>We've sent a reset link to <strong style={{ color: C.text }}>{portalEmail}</strong></div>
              </div>
            </>
          ) : (
            <>
              <Input label="Email Address" type="email" value={portalEmail} onChange={e => setPortalEmail(e.target.value)} placeholder="your@email.com" autoComplete="email" />
              {forgotError && (
                <div style={{ color: C.red, fontSize: 13, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
                  <AlertTriangle size={14} color={C.red} /> {forgotError}
                </div>
              )}
              <Btn onClick={handleForgotPassword} disabled={forgotSending} style={{ width: "100%", padding: "12px 0", fontSize: 15, marginTop: 4, justifyContent: "center" }}>
                {forgotSending ? "Sending…" : "Send reset link"}
              </Btn>
            </>
          )}
          <div style={{ textAlign: "center", marginTop: 16, color: C.textMuted, fontSize: 13 }}>
            <span onClick={() => { setForgotSent(false); setForgotError(""); setPortalView("login"); }} style={{ color: C.accent, cursor: "pointer", fontWeight: 600 }}>← Back to sign in</span>
          </div>
        </Card>
      </div>
    </div>
  );

  if (portalView === "dashboard") {
    const licenceStatusCfg = {
      Valid:   { color: C.green, bg: C.greenBg, label: "Valid" },
      Banned:  { color: C.red,   bg: C.redBg,   label: "Banned" },
      Expired: { color: C.amber, bg: C.amberBg, label: "Expired" },
    }[portalUser?.licence_status] || { color: C.textMuted, bg: C.surfaceAlt, label: portalUser?.licence_status || "Unknown" };

    const SkeletonLine = ({ w = "100%", h = 14, mb = 8 }) => (
      <div style={{ width: w, height: h, borderRadius: 6, marginBottom: mb, background: `linear-gradient(90deg, ${C.surfaceAlt} 25%, ${C.border} 50%, ${C.surfaceAlt} 75%)`, backgroundSize: "400px 100%", animation: "shimmer 1.4s infinite linear" }} />
    );

    const DashField = ({ label, value, valueColor }) => (
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.7 }}>{label}</div>
        <div style={{ color: valueColor || C.text, fontSize: 14, fontWeight: 600 }}>{value || "—"}</div>
      </div>
    );

    const carouselOuterStyle = {
      overflowX: "scroll",
      overflowY: "hidden",
      scrollSnapType: "x mandatory",
      WebkitOverflowScrolling: "touch",
      scrollBehavior: "smooth",
      display: "flex",
      columnGap: 12,
      msOverflowStyle: "none",
      scrollbarWidth: "none",
      width: "100%",
    };

    const carouselSlideStyle = {
      minWidth: "100%",
      scrollSnapAlign: "start",
      scrollSnapStop: "always",
      boxSizing: "border-box",
      flexShrink: 0,
    };

    const scrollToSlide = (idx) => {
      setVehicleIndex(idx);
      const slide = slideRefs.current[idx];
      if (slide) slide.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" });
    };

    return (
      <div style={{ minHeight: "100vh", background: C.bg, fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>
        <style>{shimmer}</style>

        {/* Header */}
        <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, padding: "14px 20px" }}>
          <div style={{ maxWidth: 480, margin: "0 auto" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 30, height: 30, background: C.accent, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <ShieldCheck size={16} color="#fff" strokeWidth={2.2} />
                </div>
                <span style={{ color: C.text, fontWeight: 800, fontSize: 17 }}>FleetCustodian</span>
              </div>
              <Btn variant="secondary" small onClick={() => { setPortalUser(null); setPortalPassword(""); setPortalLoginError(""); setPortalView("login"); }}>
                Sign Out
              </Btn>
            </div>
            <div style={{ marginTop: 10 }}>
              <div style={{ color: C.text, fontWeight: 700, fontSize: 18 }}>{portalUser?.name || "Driver"}</div>
              <div style={{ color: C.textMuted, fontSize: 13 }}>{portalUser?.email || ""}</div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div style={{ maxWidth: 480, margin: "0 auto", padding: "20px 16px 40px" }}>

          {/* Licence Card */}
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20, marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <div style={{ color: C.text, fontWeight: 700, fontSize: 16 }}>Driving Licence</div>
              {!portalDataLoading && (
                <span style={{ background: licenceStatusCfg.bg, color: licenceStatusCfg.color, border: `1px solid ${licenceStatusCfg.color}33`, borderRadius: 20, padding: "4px 12px", fontSize: 12, fontWeight: 700 }}>
                  {licenceStatusCfg.label}
                </span>
              )}
            </div>

            {portalDataLoading ? (
              <>
                <SkeletonLine w="60%" h={12} mb={16} />
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  {[1,2,3,4,5].map(i => <SkeletonLine key={i} w="80%" h={28} mb={0} />)}
                </div>
              </>
            ) : portalLicence ? (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 20px", marginBottom: 16 }}>
                  <DashField label="Points"          value={portalUser?.points ?? portalLicence.points ?? "0"} />
                  <DashField label="Endorsements"    value={portalUser?.endorsements || portalLicence.endorsements || "None"} />
                  <DashField label="Licence Valid To" value={fmtDate(portalLicence.licence_valid_to)} />
                  <DashField label="Last Check"       value={fmtDate(portalLicence.extracted_at)} />
                  <DashField label="Re-check Due"     value={fmtDate(portalUser?.recheck_due)} valueColor={portalUser?.recheck_due && new Date(portalUser.recheck_due) < new Date() ? C.red : undefined} />
                </div>
                {portalLicence.check_code && (
                  <div style={{ color: C.textMuted, fontSize: 11, borderTop: `1px solid ${C.border}`, paddingTop: 12, marginTop: 4 }}>
                    Check code: <span style={{ fontFamily: "monospace", letterSpacing: 1 }}>{portalLicence.check_code}</span>
                  </div>
                )}
              </>
            ) : (
              <div style={{ color: C.textMuted, fontSize: 14, textAlign: "center", padding: "16px 0" }}>No licence data on record yet</div>
            )}
          </div>

          {/* Vehicle Carousel */}
          {portalDataLoading ? (
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20, marginBottom: 16 }}>
              <div style={{ color: C.text, fontWeight: 700, fontSize: 16, marginBottom: 16 }}>Your Vehicle</div>
              <SkeletonLine w="40%" h={36} mb={16} />
              <SkeletonLine w="55%" h={12} mb={12} />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                {[1,2,3,4].map(i => <SkeletonLine key={i} w="80%" h={28} mb={0} />)}
              </div>
            </div>
          ) : portalVehicles.length === 0 ? (
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20, marginBottom: 16 }}>
              <div style={{ color: C.text, fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Your Vehicle</div>
              <div style={{ color: C.textMuted, fontSize: 14, marginBottom: 16 }}>No vehicle on record — add your vehicle to track MOT, tax and insurance expiry.</div>
              <Btn variant="secondary" onClick={() => { setAddVehicleError(""); setAddVehicleReg(""); setAddVehicleFile(null); setAddVehicleFileName(""); setAddVehicleDone(false); setShowAddVehicle(true); }} style={{ width: "100%" }}>+ Add a vehicle</Btn>
            </div>
          ) : (
            <>
              {/* CSS scroll-snap carousel */}
              <style>{`.portal-carousel::-webkit-scrollbar { display: none; }`}</style>
              <div ref={carouselRef} className="portal-carousel" style={{ ...carouselOuterStyle, marginBottom: 8, borderRadius: 14 }}>
                  {portalVehicles.map((gf, i) => {
                    const normReg = gf?.reg ? gf.reg.replace(/\s+/g, "").toUpperCase() : null;
                    const ves = normReg ? portalVes[normReg] : null;
                    const motSt = vesStatus(ves?.mot_expiry, ves?.mot_status);
                    const taxSt = vesStatus(ves?.tax_expiry, ves?.tax_status);
                    const insCfg = {
                      Green: { color: C.green, bg: C.greenBg, label: "Confirmed" },
                      Amber: { color: C.amber, bg: C.amberBg, label: "Needs Review" },
                      Red:   { color: C.red,   bg: C.redBg,   label: "Action Required" },
                    }[gf?.ai_outcome] || null;
                    return (
                      <div key={i} ref={el => { slideRefs.current[i] = el; }} style={carouselSlideStyle}>
                        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20 }}>
                          {/* Card header */}
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                            <div style={{ color: C.text, fontWeight: 700, fontSize: 16 }}>Your Vehicle</div>
                            {!gf.isPrimary && (
                              <span onClick={() => setShowDeleteVehicle(true)} style={{ fontSize: 12, color: C.red, cursor: "pointer" }}>Remove</span>
                            )}
                          </div>
                          {/* UK plate */}
                          {gf?.reg && (
                            <div style={{ display: "inline-flex", alignItems: "center", borderRadius: 6, overflow: "hidden", border: "2px solid #1a1a1a", marginBottom: 16, boxShadow: "0 2px 6px rgba(0,0,0,0.4)" }}>
                              <div style={{ background: "#003399", width: 22, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "6px 3px", gap: 2 }}>
                                <div style={{ color: "#FFD700", fontSize: 7, fontWeight: 800, letterSpacing: 0.5, lineHeight: 1 }}>GB</div>
                                <div style={{ color: "#FFD700", fontSize: 14, lineHeight: 1 }}>★</div>
                              </div>
                              <div style={{ background: "#F5C518", padding: "6px 14px" }}>
                                <span style={{ color: "#000", fontWeight: 800, fontSize: 22, fontFamily: "'UKNumberPlate','Arial Black','Arial',sans-serif", letterSpacing: 3 }}>{gf.reg}</span>
                              </div>
                            </div>
                          )}
                          {/* Insurance status */}
                          <div style={{ marginBottom: 16 }}>
                            <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.7, marginBottom: 6 }}>Insurance Status</div>
                            {insCfg ? (
                              <span style={{ background: insCfg.bg, color: insCfg.color, border: `1px solid ${insCfg.color}33`, borderRadius: 20, padding: "4px 12px", fontSize: 12, fontWeight: 700 }}>{insCfg.label}</span>
                            ) : (
                              <span style={{ color: C.textMuted, fontSize: 13 }}>Not checked</span>
                            )}
                          </div>
                          {/* Fields */}
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 20px" }}>
                            <DashField label="Policy Expiry" value={fmtDate(gf?.policy_expiry)} />
                            <DashField label="Re-check Due"  value={fmtDate(gf?.recheck_due)} />
                            <DashField label="MOT Expiry"    value={ves ? motSt.label : "—"} valueColor={ves ? motSt.color : undefined} />
                            <DashField label="MOT Status"    value={ves ? motSt.statusLabel : "—"} />
                            <DashField label="Tax Expiry"    value={ves ? taxSt.label : "—"} valueColor={ves ? taxSt.color : undefined} />
                            <DashField label="Tax Status"    value={ves?.tax_status || "—"} />
                            {ves?.make && <DashField label="Make"   value={ves.make} />}
                            {ves?.colour && <DashField label="Colour" value={ves.colour} />}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

              {/* Dot indicators */}
              <div style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 8, marginBottom: 12 }}>
                {portalVehicles.map((_, i) => (
                  <div
                    key={i}
                    onClick={() => scrollToSlide(i)}
                    style={{ width: i === vehicleIndex ? 20 : 8, height: 8, borderRadius: 4, background: i === vehicleIndex ? C.accent : C.surfaceAlt, transition: "all 0.3s", cursor: "pointer" }}
                  />
                ))}
              </div>

              {/* Add vehicle button */}
              <div style={{ marginBottom: 16 }}>
                <Btn variant="secondary" onClick={() => { setAddVehicleError(""); setAddVehicleReg(""); setAddVehicleFile(null); setAddVehicleFileName(""); setAddVehicleDone(false); setShowAddVehicle(true); }} style={{ width: "100%" }}>+ Add a vehicle</Btn>
              </div>
            </>
          )}

          {/* Footer */}
          <div style={{ textAlign: "center", color: C.textMuted, fontSize: 12, lineHeight: 1.6, marginTop: 8 }}>
            Data provided by FleetCustodian.<br />For queries, contact your employer.<br />
            <span
              onClick={() => { setGdprConfirmText(""); setShowGdprModal(true); }}
              style={{ color: C.accent, cursor: "pointer", fontWeight: 600, marginTop: 6, display: "inline-block" }}
            >
              Request data erasure (GDPR)
            </span>
          </div>
        </div>

        {/* Remove vehicle confirmation modal */}
        {showDeleteVehicle && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }}>
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 24, width: "100%", maxWidth: 400 }}>
              <div style={{ color: C.text, fontWeight: 700, fontSize: 18, marginBottom: 12 }}>Remove vehicle</div>
              <div style={{ color: C.textSub, fontSize: 14, lineHeight: 1.6, marginBottom: 20 }}>
                Are you sure you want to remove <strong style={{ color: C.text }}>{portalVehicles[vehicleIndex]?.reg}</strong> from your compliance record? This cannot be undone.
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  onClick={() => setShowDeleteVehicle(false)}
                  style={{ flex: 1, padding: "10px 0", borderRadius: 8, border: `1px solid ${C.border}`, background: C.surfaceAlt, color: C.text, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteVehicle}
                  disabled={deletingVehicle}
                  style={{ flex: 1, padding: "10px 0", borderRadius: 8, border: "none", background: C.red, color: "#fff", fontSize: 14, fontWeight: 600, cursor: deletingVehicle ? "not-allowed" : "pointer", fontFamily: "inherit", opacity: deletingVehicle ? 0.7 : 1 }}
                >
                  {deletingVehicle ? "Removing…" : "Remove vehicle"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add vehicle modal */}
        {showAddVehicle && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }}>
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 24, width: "100%", maxWidth: 400 }}>
              {addVehicleDone ? (
                <div style={{ textAlign: "center", padding: "8px 0" }}>
                  <CheckCircle size={44} color={C.green} style={{ margin: "0 auto 14px" }} />
                  <div style={{ color: C.green, fontWeight: 700, fontSize: 16, marginBottom: 6 }}>Vehicle added</div>
                  <div style={{ color: C.textMuted, fontSize: 14 }}>Your insurance certificate is being reviewed.</div>
                </div>
              ) : (
                <>
                  <div style={{ color: C.text, fontWeight: 700, fontSize: 18, marginBottom: 20 }}>Add a vehicle</div>

                  {/* Registration input */}
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ color: C.textMuted, fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.7, marginBottom: 6 }}>Vehicle Registration</div>
                    <input
                      value={addVehicleReg}
                      onChange={e => setAddVehicleReg(e.target.value.toUpperCase())}
                      placeholder="e.g. AB12 CDE"
                      style={{ width: "100%", background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px", color: C.text, fontSize: 15, fontWeight: 700, letterSpacing: 2, outline: "none", boxSizing: "border-box", fontFamily: "inherit" }}
                    />
                  </div>

                  {/* PDF upload dropzone */}
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ color: C.textMuted, fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.7, marginBottom: 6 }}>Insurance Certificate (PDF)</div>
                    <label style={{ display: "block", cursor: "pointer" }}>
                      <input
                        type="file"
                        accept="application/pdf"
                        style={{ display: "none" }}
                        onChange={e => {
                          const f = e.target.files?.[0];
                          if (f && f.type === "application/pdf") {
                            setAddVehicleFile(f);
                            setAddVehicleFileName(f.name);
                          }
                        }}
                      />
                      <div style={{ border: `2px dashed ${addVehicleFile ? C.green : C.border}`, borderRadius: 10, padding: "18px 12px", textAlign: "center", background: addVehicleFile ? C.greenBg : C.surfaceAlt, transition: "border-color 0.2s, background 0.2s" }}>
                        {addVehicleFile ? (
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                            <CheckCircle size={16} color={C.green} />
                            <span style={{ color: C.green, fontSize: 13, fontWeight: 600 }}>{addVehicleFileName}</span>
                          </div>
                        ) : (
                          <div>
                            <div style={{ color: C.textMuted, fontSize: 13, marginBottom: 4 }}>Tap to upload</div>
                            <div style={{ color: C.textMuted, fontSize: 11 }}>PDF only</div>
                          </div>
                        )}
                      </div>
                    </label>
                  </div>

                  {/* Error */}
                  {addVehicleError && (
                    <div style={{ color: C.red, fontSize: 13, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
                      <AlertTriangle size={14} color={C.red} /> {addVehicleError}
                    </div>
                  )}

                  {/* Buttons */}
                  <div style={{ display: "flex", gap: 10 }}>
                    <button
                      onClick={() => setShowAddVehicle(false)}
                      style={{ flex: 1, padding: "10px 0", borderRadius: 8, border: `1px solid ${C.border}`, background: C.surfaceAlt, color: C.text, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAddVehicle}
                      disabled={addVehicleSubmitting}
                      style={{ flex: 1, padding: "10px 0", borderRadius: 8, border: "none", background: C.accent, color: "#fff", fontSize: 14, fontWeight: 600, cursor: addVehicleSubmitting ? "not-allowed" : "pointer", fontFamily: "inherit", opacity: addVehicleSubmitting ? 0.7 : 1 }}
                    >
                      {addVehicleSubmitting ? "Submitting…" : "Submit"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* GDPR deletion modal */}
        {showGdprModal && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }}>
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 24, width: "100%", maxWidth: 400 }}>
              <div style={{ color: C.text, fontWeight: 700, fontSize: 18, marginBottom: 12 }}>Request data erasure</div>
              <div style={{ background: C.redBg, border: `1px solid ${C.red}33`, borderRadius: 8, padding: 12, marginBottom: 16, display: "flex", alignItems: "flex-start", gap: 8 }}>
                <AlertTriangle size={15} color={C.red} style={{ flexShrink: 0, marginTop: 1 }} />
                <div style={{ color: C.red, fontSize: 13, lineHeight: 1.5 }}>
                  This will permanently delete all your compliance data and your portal account. This cannot be undone.
                </div>
              </div>
              <div style={{ color: C.textSub, fontSize: 13, marginBottom: 8 }}>Type <strong style={{ color: C.text }}>DELETE</strong> to confirm.</div>
              <input
                value={gdprConfirmText}
                onChange={e => setGdprConfirmText(e.target.value)}
                placeholder="DELETE"
                style={{ width: "100%", background: C.surfaceAlt, border: `1px solid ${gdprConfirmText === "DELETE" ? C.red : C.border}`, borderRadius: 8, padding: "10px 14px", color: C.text, fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "inherit", marginBottom: 16 }}
              />
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  onClick={() => { setShowGdprModal(false); setGdprConfirmText(""); }}
                  style={{ flex: 1, padding: "10px 0", borderRadius: 8, border: `1px solid ${C.border}`, background: C.surfaceAlt, color: C.text, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleGdprDelete}
                  disabled={gdprDeleting || gdprConfirmText !== "DELETE"}
                  style={{ flex: 1, padding: "10px 0", borderRadius: 8, border: "none", background: gdprConfirmText === "DELETE" ? C.red : C.border, color: "#fff", fontSize: 14, fontWeight: 600, cursor: gdprConfirmText === "DELETE" ? "pointer" : "not-allowed", fontFamily: "inherit", opacity: gdprDeleting ? 0.7 : 1 }}
                >
                  {gdprDeleting ? "Deleting…" : "Delete my account"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* GDPR deleted confirmation screen */}
        {gdprDeleted && (
          <div style={{ position: "fixed", inset: 0, background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100, padding: 16, fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>
            <div style={{ textAlign: "center", maxWidth: 360 }}>
              <CheckCircle size={48} color={C.green} style={{ margin: "0 auto 16px" }} />
              <div style={{ color: C.text, fontWeight: 700, fontSize: 20, marginBottom: 8 }}>Your data has been deleted</div>
              <div style={{ color: C.textMuted, fontSize: 14, lineHeight: 1.6 }}>
                All your compliance data and portal account have been permanently removed in accordance with your GDPR right to erasure.
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
};

// ═══════════════════════════════════════════════════════════════════════════════
// APP SHELL
// ═══════════════════════════════════════════════════════════════════════════════
const NAV = [
  { id: "dashboard",  label: "Dashboard",  icon: LayoutDashboard },
  { id: "drivers",    label: "Drivers",    icon: Users },
  { id: "vehicles",   label: "Vehicles",   icon: Car },
  { id: "mandates",   label: "Mandates",   icon: FileText },
  { id: "reports",    label: "Reports",    icon: BarChart2 },
  { id: "activity",   label: "Activity",   icon: Bell },
  { id: "settings",   label: "Settings",   icon: Settings },
];

export default function App() {
  const [auth, setAuth]                   = useState("login");
  const [confirmEmail, setConfirmEmail]   = useState("");
  const [confirmError, setConfirmError]   = useState("");
  const [page, setPage]                   = useState("dashboard");
  const [filter, setFilter]               = useState(null);
  const [sidebarOpen, setSidebar]         = useState(true);
  const [mobile, setMobile]               = useState(false);
  const [drivers, setDrivers]             = useState([]);
  const [loading, setLoading]             = useState(true);
  const [toast, setToast]                 = useState(null);
  const [greyFleetData, setGreyFleetData] = useState({});
  const [mandatePreFill, setMandatePreFill] = useState({ name: "", email: "", type: "" });
  const [company, setCompany]             = useState(null);
  const sessionToken                      = useRef(null);
  const pollInterval                      = useRef(null);
  const resetTokenRef                     = useRef(null);


  useEffect(() => {
    const check = () => { const m = window.innerWidth < 768; setMobile(m); if (m) setSidebar(false); };
    check(); window.addEventListener("resize", check); return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("signup") === "true") {
      window.history.replaceState(null, "", window.location.pathname);
      setAuth("signup");
      return;
    }
    if (params.get("portal") === "true") {
      setAuth("portal");
      return;
    }
    if (params.get("mandate") === "true") {
      const name  = params.get("name")  || "";
      const email = params.get("email") || "";
      const type  = params.get("type")  || "";
      setMandatePreFill({ name, email, type });
      setAuth("mandate");
      return;
    }
    const confirmToken = params.get("confirm");
    if (confirmToken) {
      window.history.replaceState(null, "", window.location.pathname);
      setAuth("verifying");
      (async () => {
        try {
          // Step 1: verify the custom token — confirms the user's email server-side
          const verifyRes = await fetch(`${SUPABASE_URL}/functions/v1/verify-confirmation-token`, {
            method: "POST",
            headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${SUPABASE_KEY}`, "Content-Type": "application/json" },
            body: JSON.stringify({ token: confirmToken }),
          });
          const verifyData = await verifyRes.json();
          if (!verifyRes.ok || !verifyData.success) {
            setConfirmError(verifyData.error || "Invalid or expired confirmation link.");
            setAuth("confirm-error");
            return;
          }

          // Company created server-side by verify-confirmation-token — go to login
          setAuth("login");
        } catch (err) {
          console.error("Confirmation error:", err);
          setConfirmError("Something went wrong. Please try again or contact support.");
          setAuth("confirm-error");
        }
      })();
      return;
    }

    const hash = window.location.hash;
    if (hash && hash.includes("access_token") && hash.includes("type=recovery")) {
      const hashParams = new URLSearchParams(hash.substring(1));
      const accessToken = hashParams.get("access_token");
      if (accessToken) {
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
        resetTokenRef.current = accessToken;
        setAuth("reset-password");
      }
      return;
    }
    if (hash && hash.includes("access_token") && (hash.includes("type=magiclink") || hash.includes("type=signup"))) {
      const hashParams = new URLSearchParams(hash.substring(1));
      const accessToken = hashParams.get("access_token");
      if (accessToken) {
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
        sessionToken.current = accessToken;
        (async () => {
          try {
            const userRes = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
              headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${accessToken}` },
            });
            const userData = await userRes.json();
            const userId = userData.id;
            if (!userId) { setAuth("no-company"); return; }

            const coRes = await fetch(
              `${SUPABASE_URL}/rest/v1/companies?user_id=eq.${userId}&select=*&limit=1`,
              { headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${accessToken}` } }
            );
            const companies = await coRes.json();
            if (Array.isArray(companies) && companies.length > 0) {
              setCompany(companies[0]);
              setAuth("app");
            } else {
              setAuth("no-company");
            }
          } catch { setAuth("no-company"); }
        })();
      }
    }
  }, []);

  const fetchDrivers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await db.select("drivers", sessionToken.current);
      setDrivers(Array.isArray(data) ? data : []);
    } catch { setDrivers([]); }
    setLoading(false);
  }, []);

  const showToast = (msg, color = C.green) => {
    setToast({ msg, color });
    setTimeout(() => setToast(null), 3000);
  };

  const navigateTo = (p, f) => { setPage(p); setFilter(f); };
  const clearFilter = () => setFilter(null);

  const toggleGreyFleet = useCallback(async (driverId, value) => {
    setDrivers(prev => prev.map(d => d.id === driverId ? { ...d, is_grey_fleet: value } : d));
    try {
      await db.update("drivers", driverId, { is_grey_fleet: value }, sessionToken.current);
      showToast(value ? "Driver added to grey fleet" : "Driver removed from grey fleet", value ? C.green : C.amber);
    } catch {
      setDrivers(prev => prev.map(d => d.id === driverId ? { ...d, is_grey_fleet: !value } : d));
      showToast("Failed to update driver", C.red);
    }
  }, []);

  const fetchGreyFleetChecks = useCallback(async () => {
    try {
      const data = await db.select("grey_fleet_checks", sessionToken.current);
      if (Array.isArray(data)) {
        const keyed = {};
        data.forEach(row => {
          if (!keyed[row.driver_id]) keyed[row.driver_id] = [];
          keyed[row.driver_id].push({
            reg:               row.reg,
            fileName:          row.file_name,
            checkedAt:         row.checked_at,
            driverDeclaration: row.driver_declaration,
            policyExpiry:      row.policy_expiry  || null,
            recheckDue:        row.recheck_due    || null,
            isPrimary:         row.is_primary,
            aiResult: {
              outcome:    row.ai_outcome,
              confidence: row.ai_confidence,
              clause:     row.ai_clause,
              summary:    row.ai_summary,
            },
            fmResolution: row.fm_resolution  || null,
            fmNotes:      row.fm_notes       || null,
            fmReviewedAt: row.fm_reviewed_at || null,
            fmReviewedBy: row.fm_reviewed_by || null,
          });
        });
        setGreyFleetData(keyed);
      }
    } catch { /* silently ignore */ }
  }, []);

  const updateGreyFleet = useCallback(async (driverId, data) => {
    setGreyFleetData(prev => ({ ...prev, [driverId]: data }));
    try {
      await db.upsert("grey_fleet_checks", {
        driver_id:          driverId,
        company_id:         company?.id,
        reg:                data.reg,
        file_name:          data.fileName,
        ai_outcome:         data.aiResult?.outcome    || null,
        ai_confidence:      data.aiResult?.confidence || null,
        ai_clause:          data.aiResult?.clause     || null,
        ai_summary:         data.aiResult?.summary    || null,
        driver_declaration: data.driverDeclaration    || false,
        policy_expiry:      data.policyExpiry         || null,
        recheck_due:        data.recheckDue           || null,
        checked_at:         new Date().toISOString(),
      }, "driver_id", sessionToken.current);
    } catch { showToast("Failed to save insurance check", C.red); }
  }, [company]);

  useEffect(() => {
    if (auth === "app") { fetchDrivers(); fetchGreyFleetChecks(); }
  }, [auth, fetchDrivers, fetchGreyFleetChecks]);

  const silentRefresh = useCallback(async () => {
    try {
      const data = await db.select("drivers", sessionToken.current);
      setDrivers(Array.isArray(data) ? data : []);
    } catch {}
    fetchGreyFleetChecks();
  }, [fetchGreyFleetChecks]);

  useEffect(() => {
    if (auth !== "app") {
      clearInterval(pollInterval.current);
      return;
    }
    pollInterval.current = setInterval(silentRefresh, 30000);
    return () => clearInterval(pollInterval.current);
  }, [auth, silentRefresh]);

  if (auth === "verifying") return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 10, marginBottom: 28 }}>
          <div style={{ width: 36, height: 36, background: C.accent, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <ShieldCheck size={20} color="#fff" strokeWidth={2} />
          </div>
          <span style={{ color: C.text, fontWeight: 800, fontSize: 20 }}>FleetCustodian</span>
        </div>
        <div style={{ color: C.text, fontWeight: 700, fontSize: 18, marginBottom: 8 }}>Confirming your account…</div>
        <div style={{ color: C.textMuted, fontSize: 14 }}>Please wait a moment.</div>
      </div>
    </div>
  );
  if (auth === "confirm-error") return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ width: "100%", maxWidth: 400, textAlign: "center" }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 10, marginBottom: 28 }}>
          <div style={{ width: 36, height: 36, background: C.accent, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <ShieldCheck size={20} color="#fff" strokeWidth={2} />
          </div>
          <span style={{ color: C.text, fontWeight: 800, fontSize: 20 }}>FleetCustodian</span>
        </div>
        <div style={{ width: 64, height: 64, background: C.surfaceAlt, border: `2px solid ${C.red}44`, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
          <AlertTriangle size={28} color={C.red} />
        </div>
        <div style={{ color: C.text, fontWeight: 700, fontSize: 22, marginBottom: 8 }}>Confirmation failed</div>
        <div style={{ color: C.textMuted, fontSize: 14, lineHeight: 1.6, marginBottom: 24 }}>{confirmError || "This link is invalid or has already been used."}</div>
        <Btn variant="secondary" onClick={() => setAuth("login")} style={{ width: "100%", justifyContent: "center" }}>← Back to Login</Btn>
      </div>
    </div>
  );
  if (auth === "reset-password") {
    const ResetPasswordScreen = () => {
      const [newPassword, setNewPassword]         = useState("");
      const [confirmPassword, setConfirmPassword] = useState("");
      const [submitting, setSubmitting]           = useState(false);
      const [error, setError]                     = useState("");
      const [done, setDone]                       = useState(false);

      const handleSubmit = async () => {
        setError("");
        if (!newPassword) { setError("Please enter a new password."); return; }
        if (newPassword.length < 6) { setError("Password must be at least 6 characters."); return; }
        if (newPassword !== confirmPassword) { setError("Passwords do not match."); return; }
        setSubmitting(true);
        try {
          const res = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
            method: "PUT",
            headers: { "Content-Type": "application/json", apikey: SUPABASE_KEY, Authorization: `Bearer ${resetTokenRef.current}` },
            body: JSON.stringify({ password: newPassword }),
          });
          if (!res.ok) {
            const errText = await res.text();
            throw new Error(errText || "Failed to update password.");
          }
          setDone(true);
          setTimeout(() => { resetTokenRef.current = null; setAuth("login"); }, 2500);
        } catch (err) { setError(err.message || "Something went wrong. Please try again."); }
        setSubmitting(false);
      };

      return (
        <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ width: "100%", maxWidth: 400 }}>
            <div style={{ textAlign: "center", marginBottom: 32 }}>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <div style={{ width: 36, height: 36, background: C.accent, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <ShieldCheck size={20} color="#fff" strokeWidth={2} />
                </div>
                <span style={{ color: C.text, fontWeight: 800, fontSize: 20 }}>FleetCustodian</span>
              </div>
              <div style={{ color: C.text, fontWeight: 700, fontSize: 24, marginBottom: 6 }}>Set a new password</div>
              <div style={{ color: C.textMuted, fontSize: 14 }}>Choose a new password for your account</div>
            </div>
            <Card>
              {done ? (
                <div style={{ textAlign: "center", padding: "8px 0 12px" }}>
                  <CheckCircle size={36} color={C.green} style={{ margin: "0 auto 12px" }} />
                  <div style={{ color: C.green, fontWeight: 700, fontSize: 15, marginBottom: 6 }}>Password updated</div>
                  <div style={{ color: C.textMuted, fontSize: 14 }}>Redirecting you to sign in…</div>
                </div>
              ) : (
                <>
                  <Input label="New Password" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="••••••••" autoComplete="new-password" />
                  <Input label="Confirm Password" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="••••••••" autoComplete="new-password" />
                  {error && (
                    <div style={{ color: C.red, fontSize: 13, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
                      <AlertTriangle size={14} color={C.red} /> {error}
                    </div>
                  )}
                  <Btn onClick={handleSubmit} disabled={submitting} style={{ width: "100%", padding: "12px 0", fontSize: 15, marginTop: 4, justifyContent: "center" }}>
                    {submitting ? "Updating…" : "Update Password"}
                  </Btn>
                </>
              )}
            </Card>
          </div>
        </div>
      );
    };
    return <ResetPasswordScreen />;
  }
  if (auth === "login")   return <LoginPage onLogin={(co, token) => { if (!co) { setAuth("no-company"); } else { setCompany(co); sessionToken.current = token; setAuth("app"); } }} onSignup={() => setAuth("signup")} />;
  if (auth === "confirm") return <ConfirmPage email={confirmEmail} onBack={() => setAuth("login")} />;
  if (auth === "signup")  return <SignupPage onBack={() => setAuth("login")} onConfirm={(email) => { setConfirmEmail(email); setAuth("confirm"); }} />;
  if (auth === "no-company") return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, padding: 40, maxWidth: 420, width: "100%", textAlign: "center" }}>
        <AlertTriangle size={40} color={C.amber} style={{ margin: "0 auto 16px" }} />
        <div style={{ color: C.text, fontWeight: 700, fontSize: 20, marginBottom: 10 }}>Account setup incomplete</div>
        <div style={{ color: C.textMuted, fontSize: 14, lineHeight: 1.6 }}>Please contact support to resolve this.</div>
        <div style={{ marginTop: 24 }}><Btn variant="secondary" onClick={() => setAuth("login")}>← Back to Login</Btn></div>
      </div>
    </div>
  );
 if (auth === "portal")  return <DriverPortalApp supabaseUrl={SUPABASE_URL} supabaseKey={SUPABASE_KEY} />;
 if (auth === "mandate") return <MandateDriverPage onBack={() => setAuth("app")} preFill={mandatePreFill} />;

  const props = {
    navigateTo, filter, clearFilter, drivers, loading,
    onRefresh: () => { fetchDrivers(); fetchGreyFleetChecks(); }, showToast,
    onToggleGreyFleet: toggleGreyFleet,
    greyFleetData,
    onUpdateGreyFleet: updateGreyFleet,
    company,
    sessionToken,
  };

  const PAGES = {
    dashboard:  <DashboardPage  {...props} />,
    drivers:    <DriversPage    {...props} />,
    vehicles:   <VehiclesPage   {...props} />,
    mandates:   <MandatesPage   {...props} />,
    reports:    <ReportsPage    {...props} />,
    greyfleet:  <GreyFleetPage  {...props} />,
    activity:   <ActivityPage company={company} sessionToken={sessionToken} />,
    settings:   <SettingsPage company={company} sessionToken={sessionToken} showToast={showToast} drivers={drivers} onSave={(updated) => { setCompany(updated); fetchDrivers(); }} />,
  };

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: C.bg, fontFamily: "'DM Sans','Segoe UI',sans-serif", color: C.text }}>
      <style>{shimmer}</style>

      {sidebarOpen && (
        <div style={{ width: mobile ? "100vw" : 220, background: C.surface, borderRight: `1px solid ${C.border}`, display: "flex", flexDirection: "column", position: mobile ? "fixed" : "relative", inset: mobile ? "0 auto 0 0" : "auto", zIndex: mobile ? 50 : "auto", flexShrink: 0 }}>
          <div style={{ padding: "12px 18px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 32, height: 32, background: C.accent, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <ShieldCheck size={16} color="#fff" strokeWidth={2.2} />
              </div>
              <span style={{ color: C.text, fontWeight: 800, fontSize: 16 }}>FleetCustodian</span>
            </div>
            {mobile && <button onClick={() => setSidebar(false)} style={{ background: "none", border: "none", color: C.textMuted, cursor: "pointer", display: "flex" }}><X size={20} color={C.textMuted} /></button>}
          </div>
          <nav style={{ flex: 1, padding: "12px 10px", overflowY: "auto" }}>
            {NAV.map(n => {
              const active = page === n.id;
              const IconComp = n.icon;
              return (
                <button key={n.id} onClick={() => { setPage(n.id); setFilter(null); if (mobile) setSidebar(false); }}
                  style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "7px 12px", borderRadius: 8, border: "none", cursor: "pointer", marginBottom: 2, textAlign: "left", background: active ? C.accentGlow : "transparent", color: active ? C.accent : C.textSub, fontWeight: active ? 600 : 400, fontSize: 14, transition: "all 0.15s", fontFamily: "inherit" }}>
                  <IconComp size={16} color={active ? C.accent : C.textSub} strokeWidth={active ? 2.2 : 1.75} />
                  {n.label}
                </button>
              );
            })}
          </nav>
          <div style={{ padding: 14, borderTop: `1px solid ${C.border}` }}>
            <button onClick={() => setAuth("mandate")} style={{ width: "100%", background: C.accentGlow, border: `1px solid ${C.accent}33`, borderRadius: 8, padding: "8px 12px", color: C.accent, fontSize: 12, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, fontFamily: "inherit" }}>
              <Eye size={13} color={C.accent} /> Preview Driver Mandate
            </button>
          </div>
          <div style={{ padding: "12px 16px", borderTop: `1px solid ${C.border}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <div style={{ width: 32, height: 32, background: C.accentDim, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, flexShrink: 0, color: "#fff" }}>A</div>
              <div style={{ overflow: "hidden" }}>
                <div style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>Admin User</div>
                <div style={{ color: C.textMuted, fontSize: 11 }}>{company?.name}</div>
              </div>
            </div>
            <button onClick={() => { setAuth("login"); setPage("dashboard"); setFilter(null); setDrivers([]); }}
              style={{ width: "100%", background: "none", border: `1px solid ${C.border}`, borderRadius: 8, padding: "7px 12px", color: C.textMuted, fontSize: 12, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, transition: "all 0.15s", fontFamily: "inherit" }}
              onMouseOver={e => { e.currentTarget.style.borderColor = C.red; e.currentTarget.style.color = C.red; }}
              onMouseOut={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.textMuted; }}>
              <LogOut size={13} /> Sign Out
            </button>
          </div>
        </div>
      )}

      {mobile && sidebarOpen && <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 40 }} onClick={() => setSidebar(false)} />}

      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, padding: "12px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button onClick={() => setSidebar(s => !s)} style={{ background: "none", border: "none", color: C.textMuted, cursor: "pointer", display: "flex", padding: 4 }}>
              <Menu size={18} color={C.textMuted} />
            </button>
            {filter ? (
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
                <span style={{ color: C.accent, cursor: "pointer", fontWeight: 500 }} onClick={() => navigateTo("dashboard", null)}>Dashboard</span>
                <ChevronRight size={14} color={C.textMuted} />
                <span style={{ color: C.text, fontWeight: 600 }}>{filter.label}</span>
              </div>
            ) : <span style={{ color: C.textMuted, fontSize: 13 }}>{company?.name}</span>}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button onClick={fetchDrivers} title="Refresh" style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMuted, cursor: "pointer", padding: "5px 10px", display: "flex", alignItems: "center" }}>
              <RefreshCw size={14} color={C.textMuted} />
            </button>
            <div style={{ background: C.greenBg, border: `1px solid ${C.green}33`, borderRadius: 20, padding: "4px 12px", fontSize: 12, color: C.green, fontWeight: 600, display: "flex", alignItems: "center", gap: 5 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: C.green, display: "inline-block" }} /> Live
            </div>
            <div style={{ width: 32, height: 32, background: C.accentDim, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#fff" }}>A</div>
          </div>
        </div>
        <div style={{ flex: 1, padding: mobile ? 14 : 24, overflowY: "auto" }}>
          {PAGES[page]}
        </div>
      </div>

      {toast && <Toast msg={toast.msg} color={toast.color} />}
    </div>
  );
}